// Always talk directly to the live API — no dev-server proxy involved.
export const BASE_URL = 'https://petrotag360api.itserviceafrica.com';

export interface AuthTokens {
  access: string;
  refresh: string;
}

export interface ApiError {
  message: string;
  status: number;
  /** 429 only: seconds to wait before the same action may be tried again. */
  retryAfter?: number;
  /** 5xx only: the server's log reference, so support can find the failure. */
  reference?: string;
  /** 400 only: field-level validation messages, keyed by field name. */
  errors?: Record<string, string[]>;
}

/* ── Rate limits and server errors (guide §2, §3.5) ──
   Auth and code endpoints are now throttled (10/min per IP; one emailed code per 60s).
   A 429 body is { status, message, retryAfter? }. The UI must show the message and
   must not retry in a loop, so once an endpoint answers 429 every further call to it is
   short-circuited locally until the wait is over — polling loops included.
   A 5xx body carries a `reference`; it is appended to the message so it is always shown. */
const throttledUntil = new Map<string, number>();

function throttleKey(url: string): string {
  // One cooldown per endpoint path; the query string doesn't make it a different limit.
  return url.split('?')[0];
}

/** Seconds left on a known 429 for this URL, or 0. */
export function throttleRemaining(url: string): number {
  const until = throttledUntil.get(throttleKey(url)) ?? 0;
  return Math.max(0, Math.ceil((until - Date.now()) / 1000));
}

function throttledResponse(seconds: number): Response {
  return new Response(JSON.stringify({
    status: 429,
    message: `Too many requests. Please wait ${seconds} second${seconds === 1 ? '' : 's'} and try again.`,
    retryAfter: seconds,
  }), { status: 429, headers: { 'Content-Type': 'application/json' } });
}

/* Wraps fetch for every API call (public auth endpoints included): enforces a known
   cooldown, records new ones from 429s, and folds a 5xx reference into the message. */
async function guardedFetch(url: string, init?: RequestInit): Promise<Response> {
  const waiting = throttleRemaining(url);
  if (waiting > 0) return throttledResponse(waiting);

  const res = await fetch(url, init);
  if (res.status !== 429 && res.status < 500) return res;

  const body = await res.clone().json().catch(() => null);
  if (res.status === 429) {
    const header = Number(res.headers.get('Retry-After'));
    const fromBody = Number(body?.retryAfter);
    // "Expected available in 42 seconds." is the throttle text when no retryAfter is sent.
    const fromText = Number(String(body?.message ?? body?.detail ?? '').match(/(\d+)\s*second/)?.[1]);
    const seconds = [fromBody, header, fromText].find(n => Number.isFinite(n) && n > 0) ?? 60;
    throttledUntil.set(throttleKey(url), Date.now() + seconds * 1000);
    return new Response(JSON.stringify({
      ...(body ?? {}),
      status: 429,
      message: body?.message ?? body?.detail ?? `Too many requests. Please wait ${seconds} seconds and try again.`,
      retryAfter: seconds,
    }), { status: 429, headers: { 'Content-Type': 'application/json' } });
  }

  // 5xx: keep the body, but make sure the reference reaches whoever reads `message`.
  if (body && typeof body === 'object' && body.reference) {
    const base = body.message ?? body.detail ?? 'Something went wrong on the server.';
    const message = String(base).includes(String(body.reference)) ? base : `${base} (Reference: ${body.reference})`;
    return new Response(JSON.stringify({ ...body, message, detail: message }),
      { status: res.status, headers: { 'Content-Type': 'application/json' } });
  }
  return res;
}

/** Builds an ApiError from a failed response body, carrying retryAfter, reference and field errors. */
export function toApiError(status: number, body: any, fallback: string): ApiError {
  const fieldErrors = body?.errors && typeof body.errors === 'object' ? body.errors as Record<string, string[]> : undefined;
  const flat = fieldErrors ? Object.values(fieldErrors).flat().filter(Boolean).join(' ') : '';
  return {
    message:    flat || body?.message || body?.detail || fallback,
    status,
    retryAfter: Number.isFinite(Number(body?.retryAfter)) && Number(body?.retryAfter) > 0 ? Number(body.retryAfter) : undefined,
    reference:  body?.reference ?? undefined,
    errors:     fieldErrors,
  };
}

/* ── The signed-in user's integer account id ──
   Endpoints such as investigate want it ("investigatorId": 1). Read it from the access
   token's payload (user_id / id / sub), falling back to the saved login profile. */
export function currentUserId(): number | undefined {
  const asInt = (v: unknown) => (v != null && v !== '' && Number.isInteger(Number(v)) && Number(v) > 0 ? Number(v) : undefined);
  try {
    const token = localStorage.getItem('pt360_access');
    const part  = token?.split('.')[1];
    if (part) {
      const json = JSON.parse(atob(part.replace(/-/g, '+').replace(/_/g, '/').padEnd(Math.ceil(part.length / 4) * 4, '=')));
      const fromToken = asInt(json?.user_id) ?? asInt(json?.userId) ?? asInt(json?.id) ?? asInt(json?.sub);
      if (fromToken) return fromToken;
    }
  } catch { /* not a JWT, or unreadable — fall through */ }
  try {
    const saved = JSON.parse(localStorage.getItem('fuelMonitoringAuth') ?? 'null');
    return asInt(saved?.userId) ?? asInt(saved?.id);
  } catch {
    return undefined;
  }
}

/* ── Token storage ── */
export const tokenStorage = {
  save(tokens: AuthTokens) {
    localStorage.setItem('pt360_access',  tokens.access);
    localStorage.setItem('pt360_refresh', tokens.refresh);
  },
  getAccess():  string | null { return localStorage.getItem('pt360_access');  },
  getRefresh(): string | null { return localStorage.getItem('pt360_refresh'); },
  clear() {
    localStorage.removeItem('pt360_access');
    localStorage.removeItem('pt360_refresh');
  },
};

/* ── Logout — blacklists the refresh token server-side ── */
export async function logoutUser(): Promise<void> {
  const refresh = tokenStorage.getRefresh();
  // Always clear local tokens first so the user is logged out client-side
  // regardless of whether the server call succeeds.
  tokenStorage.clear();
  if (!refresh) return;

  try {
    await guardedFetch(`${BASE_URL}/api/v1/onboard/auth/logout/`, {
      method:  'POST',
      headers: { 'Content-Type': 'application/json' },
      body:    JSON.stringify({ refresh }),
    });
    // 205 = success, 400 = token already invalid — either way we are done.
  } catch {
    // Network failure: token will expire naturally; local session is already cleared.
  }
}

/* ── Actual API response shape from /api/v1/onboard/auth/login/ ── */
interface LoginApiResponse {
  status:       number;
  message:      string;
  token:        string;        // access token at top level
  refreshToken: string;        // refresh token at top level
  data: {
    id:            string;
    username?:     string;
    name?:         string;
    email:         string;
    company_name:  string;
    industry_type: string;
    role:          'owner' | 'Admin' | 'Manager' | 'Operator' | 'Auditor';
    permissions:   string[];
    assignedSites: string[];
    isOwner:       boolean;
  };
}

export interface LoginResult {
  tokens: AuthTokens;
  user: LoginApiResponse['data'];
}

/* ── Silent access-token refresh ──
   POST /token/refresh — lives outside /api (root-level, no trailing slash); confirmed
   against the live API. Concurrent 401s share one in-flight refresh instead of each
   firing their own. */
let refreshPromise: Promise<string> | null = null;

async function refreshAccessToken(): Promise<string> {
  if (refreshPromise) return refreshPromise;

  refreshPromise = (async () => {
    const refresh = tokenStorage.getRefresh();
    if (!refresh) throw { message: 'No refresh token available.', status: 401 } as ApiError;

    const res  = await guardedFetch(`${BASE_URL}/token/refresh`, {
      method:  'POST',
      headers: { 'Content-Type': 'application/json' },
      body:    JSON.stringify({ refresh }),
    });
    const body = await res.json().catch(() => null);

    if (!res.ok) {
      throw { message: body?.detail ?? 'Session expired.', status: res.status } as ApiError;
    }

    const access = body?.access ?? body?.token;
    if (!access) throw { message: 'Session expired.', status: 401 } as ApiError;

    // Some SimpleJWT configs rotate the refresh token on use — keep the old one if not returned.
    tokenStorage.save({ access, refresh: body?.refresh ?? refresh });
    return access as string;
  })();

  try {
    return await refreshPromise;
  } finally {
    refreshPromise = null;
  }
}

/* ── Authenticated fetch helper ──
   On a 401, silently refreshes the access token via /token/refresh and retries the
   request once. If the refresh itself fails (refresh token expired/invalid), clears
   the session and lets the app know so it can drop the user back to login. */
async function apiFetch(path: string, init?: RequestInit): Promise<Response> {
  const doFetch = (token: string | null) => guardedFetch(`${BASE_URL}${path}`, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...init?.headers,
    },
  });

  const res = await doFetch(tokenStorage.getAccess());
  if (res.status !== 401 || !tokenStorage.getRefresh()) return res;

  try {
    const newAccess = await refreshAccessToken();
    const retried = await doFetch(newAccess);
    if (retried.status === 401) {
      // A fresh token was still refused (e.g. the user was deactivated): the session is over.
      tokenStorage.clear();
      try { sessionStorage.setItem('pt360_session_expired', '1'); } catch { /* ignore */ }
      window.dispatchEvent(new CustomEvent('pt360:session-expired'));
    }
    return retried;
  } catch {
    tokenStorage.clear();
    try { sessionStorage.setItem('pt360_session_expired', '1'); } catch { /* ignore */ }
    window.dispatchEvent(new CustomEvent('pt360:session-expired'));
    return res; // original 401 — caller's existing error handling takes over
  }
}

/* ── Shared response blocks (guide §6) ──
   The same site, tank and user blocks appear in alerts, theft incidents, the theft
   summary, site detail and analytics. They are parsed once here and reused. Older
   endpoints send decimals as strings, newer blocks as numbers: both are accepted. */
export interface GeoLocation { lat?: number; lng?: number; address?: string }

export interface SiteBlock {
  id:        string;
  name:      string;
  status?:   string;
  address?:  string;
  location?: GeoLocation;
}

export interface TankThresholds {
  criticalLevelPct?: number;
  minimumLevelPct?:  number;
  reorderLevelPct?:  number;
  overfillLevelPct?: number;
}

export interface TankBlock {
  id:              string;
  name:            string;
  fuelType?:       string;
  status?:         string;     // normal · warning · critical · offline
  capacity?:       number;
  currentLevel?:   number;     // percent
  currentVolume?:  number;     // litres
  lastRefill?:     string | null;
  lastReading?:    string | null;   // display string ("14 Oct 2026, 3:44 PM WAT")
  lastReadingAgo?: string | null;
  thresholds?:     TankThresholds;
  sensors?:        { levelSensor?: any; qualitySensor?: any; flowMeter?: any };
  alertsEnabled?:  boolean;
  isActive?:       boolean;
  site?:           { id: string; name: string };
  location?:       GeoLocation;
}

export interface UserBlock { id: string; name: string; email?: string }

function mapLocation(l: any): GeoLocation | undefined {
  if (!l || typeof l !== 'object') return undefined;
  return { lat: numOrUndef(l.lat ?? l.latitude), lng: numOrUndef(l.lng ?? l.longitude), address: l.address ?? undefined };
}

export function mapSiteBlock(b: any): SiteBlock | undefined {
  if (!b || typeof b !== 'object') return undefined;
  return {
    id:       String(b.id ?? ''),
    name:     String(b.name ?? ''),
    status:   b.status ?? undefined,
    address:  b.address ?? b.location?.address ?? undefined,
    location: mapLocation(b.location),
  };
}

export function mapTankBlock(b: any): TankBlock | undefined {
  if (!b || typeof b !== 'object') return undefined;
  const th = b.thresholds ?? {};
  return {
    id:             String(b.id ?? ''),
    name:           String(b.name ?? ''),
    fuelType:       b.fuelType ?? b.fuel_type ?? undefined,
    status:         b.status ?? undefined,
    capacity:       numOrUndef(b.capacity ?? b.capacityLitres),
    currentLevel:   numOrUndef(b.currentLevel ?? b.currentLevelPct),
    currentVolume:  numOrUndef(b.currentVolume ?? b.currentVolumeLitres),
    lastRefill:     b.lastRefill ?? null,
    lastReading:    b.lastReading ?? null,
    lastReadingAgo: b.lastReadingAgo ?? null,
    thresholds: {
      criticalLevelPct: numOrUndef(th.criticalLevelPct ?? b.criticalLevelPct),
      minimumLevelPct:  numOrUndef(th.minimumLevelPct  ?? b.minimumLevelPct),
      reorderLevelPct:  numOrUndef(th.reorderLevelPct  ?? b.reorderLevelPct),
      overfillLevelPct: numOrUndef(th.overfillLevelPct ?? b.overfillLevelPct),
    },
    sensors:       b.sensors ?? undefined,
    alertsEnabled: b.alertsEnabled ?? undefined,
    isActive:      b.isActive ?? undefined,
    site:          b.site && typeof b.site === 'object' ? { id: String(b.site.id ?? ''), name: String(b.site.name ?? '') } : undefined,
    location:      mapLocation(b.location),
  };
}

/** acknowledgedBy, resolvedBy, investigator, actor… — null until someone has acted. */
export function mapUserBlock(b: any): UserBlock | undefined {
  if (b == null) return undefined;
  if (typeof b === 'string') return { id: b, name: b };   // older responses sent a bare username
  if (typeof b !== 'object') return undefined;
  return { id: String(b.id ?? ''), name: String(b.name ?? b.username ?? b.email ?? ''), email: b.email ?? undefined };
}

/* ── Alerts ── */
export type AlertType =
  | 'theft' | 'leak' | 'low_fuel' | 'contamination' | 'maintenance' | 'refill' | 'overfill'
  | 'movement'      // a watch-window movement
  | 'consumption';  // abnormal fuel use by a vehicle or generator

export interface AlertsParams {
  siteId?:      string;
  type?:        AlertType;
  severity?:    'low' | 'medium' | 'high' | 'critical';
  acknowledged?: boolean;
  resolved?:    boolean;
  page?:        number;
  limit?:       number;
}

/* siteId and tankId arrive as objects now (guide §3.2). They are flattened back to ids
   here so every comparison in the app keeps working, with the full blocks alongside.
   `timestamp` is kept as the ISO time (from createdAt) for sorting; the server's
   display string is in `timestampDisplay` and is what the UI shows. */
export interface AlertRecord {
  id:           string;
  type:         AlertType;
  severity:     'low' | 'medium' | 'high' | 'critical';
  title:        string;
  message:      string;
  /** ISO 8601 — sort and compare with this. */
  timestamp:    string;
  createdAt?:   string;
  /** "14 Oct 2026, 3:46 PM WAT" — already in Lagos time; show as-is. */
  timestampDisplay?: string;
  timeAgo?:     string;
  siteId:       string;
  siteName?:    string;
  site?:        SiteBlock;
  tankId?:      string;
  tankName?:    string;
  tank?:        TankBlock;
  generatorId?: string;
  acknowledged: boolean;
  resolved:     boolean;
  acknowledgedAtDisplay?: string;
  resolvedAtDisplay?:     string;
}

export interface AlertsResult {
  count:    number;
  next:     string | null;
  previous: string | null;
  results:  AlertRecord[];
}

export async function fetchAlerts(params: AlertsParams = {}): Promise<AlertsResult> {
  const qs = new URLSearchParams();
  if (params.siteId      != null) qs.set('siteId',      params.siteId);
  if (params.type        != null) qs.set('type',         params.type);
  if (params.severity    != null) qs.set('severity',     params.severity);
  if (params.acknowledged != null) qs.set('acknowledged', String(params.acknowledged));
  if (params.resolved    != null) qs.set('resolved',     String(params.resolved));
  if (params.page        != null) qs.set('page',         String(params.page));
  if (params.limit       != null) qs.set('limit',        String(params.limit));

  const res = await apiFetch(`/api/alerts/${qs.toString() ? `?${qs}` : ''}`);
  const body = await res.json().catch(() => null);

  if (!res.ok) {
    const message = body?.detail ?? body?.message ?? 'Failed to fetch alerts.';
    throw { message, status: res.status } as ApiError;
  }

  // Handle formats:
  //   { data: [...], total, page }        ← actual API
  //   { data: { results: [...], count } } ← paginated envelope
  //   { results: [...], count }           ← flat paginated
  const raw = body?.data ?? body;
  const rows: any[] = Array.isArray(raw) ? raw : (raw?.results ?? []);
  const results: AlertRecord[] = rows.map(mapAlert);

  return {
    count:    body?.total ?? raw?.count ?? results.length,
    next:     raw?.next     ?? null,
    previous: raw?.previous ?? null,
    results,
  };
}

/* ── Get single alert ── */
export async function fetchAlertById(alertId: string): Promise<AlertRecord> {
  const res = await apiFetch(`/api/alerts/${alertId}/`);
  const body = await res.json().catch(() => null);

  // 404 means "not found or not yours": an Operator pinned to other sites gets it too (guide §3.4).
  if (res.status === 404) {
    throw { message: "This alert isn't available to your account.", status: 404 } as ApiError;
  }
  if (!res.ok) {
    throw toApiError(res.status, body, 'Failed to fetch alert.');
  }

  // Handle both envelope { data: {...} } and flat response
  return mapAlert(body?.data ?? body);
}

/* ── Analytics: efficiency ── */
export interface EfficiencyParams {
  siteId?: string;
  from?:   string;  // ISO datetime
  to?:     string;  // ISO datetime
}

export interface GeneratorEfficiency {
  id:         string;
  name:       string;
  efficiency: number;
  runtime?:   number;
  fuelUsed?:  number;
  /* October 2026: fuel figures come from fuel issues linked to the generator. */
  status?:              string;
  siteId?:              string;
  siteName?:            string;
  fuelIssuedLitres?:    number;
  fuelIssuedDisplay?:   string;
  fuelIssues?:          number;
  lastFueled?:          string | null;
  lastFueledAgo?:       string | null;
  ratedConsumptionLph?: number;
  hasFuelData?:         boolean;
  /** Display string from the server, e.g. "5 Aug 2026, 1:25 PM WAT". */
  lastMaintenance?:     string | null;
}

export interface EfficiencyTrendPoint {
  date:       string;
  efficiency: number;
}

/* Efficiency (kWh per litre) needs each generator's energy output, which is not
   recorded yet (guide §3.7). Until it is, `overall` is null and efficiencyAvailable is
   false: show efficiencyNote and the fuel-issued figures instead of a 0% gauge. */
export interface EfficiencyResult {
  /** Kept for older callers: `overall`, or 0 when it is null. Do not show this as a percentage when efficiencyAvailable is false. */
  overallEfficiency:   number;
  overall:             number | null;
  efficiencyAvailable: boolean;
  efficiencyNote?:     string;
  fuelIssuedLitres?:   number;
  fuelIssuedDisplay?:  string;
  generatorsSummary?:  { total: number; running: number; withFuelData: number; withoutFuelData: string[] };
  generators:          GeneratorEfficiency[];
  trend:               EfficiencyTrendPoint[];
}

export async function fetchEfficiency(params: EfficiencyParams = {}): Promise<EfficiencyResult> {
  const qs = new URLSearchParams();
  if (params.siteId != null) qs.set('siteId', params.siteId);
  if (params.from   != null) qs.set('from',   params.from);
  if (params.to     != null) qs.set('to',     params.to);

  const res  = await apiFetch(`/api/analytics/efficiency/${qs.toString() ? `?${qs}` : ''}`);
  const body = await res.json().catch(() => null);

  if (!res.ok) {
    const message = body?.detail ?? body?.message ?? 'Failed to fetch efficiency data.';
    throw { message, status: res.status } as ApiError;
  }

  const raw = body?.data ?? body;

  // `generators` is now a summary object; the per-generator rows are in byGenerator.
  const rows: any[] = Array.isArray(raw?.byGenerator) ? raw.byGenerator
                    : Array.isArray(raw?.generators) ? raw.generators : [];
  const generators: GeneratorEfficiency[] = rows.map((g: any) => ({
    id:         String(g.id   ?? g.generator_id ?? ''),
    name:       String(g.name ?? g.generator_name ?? 'Generator'),
    efficiency: Number(g.efficiency ?? g.efficiency_score ?? 0),
    runtime:    g.runtime  != null ? Number(g.runtime)   : undefined,
    fuelUsed:   g.fuelUsed != null ? Number(g.fuelUsed)  :
                g.fuel_used != null ? Number(g.fuel_used) : undefined,
    status:              g.status ?? undefined,
    siteId:              g.site?.id != null ? String(g.site.id) : (g.siteId ?? undefined),
    siteName:            g.site?.name ?? g.siteName ?? undefined,
    fuelIssuedLitres:    numOrUndef(g.fuelIssuedLitres),
    fuelIssuedDisplay:   g.fuelIssuedDisplay ?? undefined,
    fuelIssues:          numOrUndef(g.fuelIssues),
    lastFueled:          g.lastFueled ?? null,
    lastFueledAgo:       g.lastFueledAgo ?? null,
    ratedConsumptionLph: numOrUndef(g.ratedConsumptionLph),
    hasFuelData:         g.hasFuelData ?? undefined,
    lastMaintenance:     g.lastMaintenance ?? null,
  }));
  const gs = raw?.generators && !Array.isArray(raw.generators) ? raw.generators : null;

  const trend: EfficiencyTrendPoint[] = (raw?.trend ?? raw?.daily_trend ?? []).map((t: any) => ({
    date:       String(t.date ?? t.timestamp?.slice(0, 10) ?? ''),
    efficiency: Number(t.efficiency ?? t.value ?? 0),
  }));

  const overallRaw = raw?.overall ?? raw?.overall_efficiency ?? raw?.efficiency;
  const overall = overallRaw == null || overallRaw === '' ? null : Number(overallRaw);
  const efficiencyAvailable = raw?.efficiencyAvailable ?? overall != null;
  return {
    overallEfficiency: overall ?? 0,
    overall,
    efficiencyAvailable: Boolean(efficiencyAvailable),
    efficiencyNote:    raw?.efficiencyNote ?? undefined,
    fuelIssuedLitres:  numOrUndef(raw?.fuelIssuedLitres),
    fuelIssuedDisplay: raw?.fuelIssuedDisplay ?? undefined,
    generatorsSummary: gs ? {
      total: numOf(gs.total), running: numOf(gs.running), withFuelData: numOf(gs.withFuelData),
      withoutFuelData: Array.isArray(gs.withoutFuelData) ? gs.withoutFuelData.map(String) : [],
    } : undefined,
    generators,
    trend,
  };
}

/* ── Analytics: consumption ── */
export interface ConsumptionParams {
  siteId?:   string;
  tankId?:   string;
  from?:     string;   // ISO datetime
  to?:       string;   // ISO datetime
  interval?: 'minute' | 'hour' | 'day' | 'week' | 'month';
  /** Window length in hours — e.g. { interval: 'minute', hours: 2 } or { interval: 'hour', hours: 24 }. */
  hours?:    number;
  /** Split breakdown[] by site or by tank. */
  groupBy?:  'none' | 'site' | 'tank';
  /** Only buckets after this ISO time. */
  since?:    string;
}

export interface ConsumptionPoint {
  timestamp:   string;
  date:        string;
  /** What the chart plots — the period's measuredLitres (what sensors show actually left the tank). */
  consumption: number;
  target?:     number;
  efficiency?: number;
  prediction?: number;
  // The API's per-interval row is a mini reconciliation, not a single "consumption"
  // figure — these mirror ConsumptionTotals but scoped to just this point's period.
  recordedLitres?:    number;
  unexplainedLitres?: number;
  leakLitres?:        number;
  refillLitres?:      number;
  /** Sensor readings that contributed to this period's aggregate. */
  readings?:   number;
  /** Ready-made axis label for the bucket (October 2026). Use it instead of formatting the time. */
  label?:      string;
  /** Net change in the tank contents over the bucket. */
  netChangeLitres?: number;
  // Per-generator breakdown returned by the API (generator1, generator2, …)
  [key: string]: number | string | undefined;
}

/** Reconciliation totals for the queried range — measured vs. recorded vs. unexplained losses/gains. */
export interface ConsumptionTotals {
  measuredLitres:     number;
  recordedLitres:     number;
  unexplainedLitres:  number;
  leakLitres:         number;
  refillLitres:       number;
  unexplainedPct:     number;
}

/** Point-in-time tank state as of the response, independent of the queried range. */
export interface ConsumptionLiveSnapshot {
  totalVolumeLitres:   number;
  totalCapacityLitres: number;
  utilizationPct:      number;
  tankCount:           number;
  tanksOffline:        number;
  asOf:                string;
}

export interface ConsumptionScope {
  sites: number;
  tanks: number;
}

export interface ConsumptionBreakdownItem {
  id?:   string;
  name?: string;
  /** The site this row belongs to (address, location) — sent on every breakdown row. */
  site?: SiteBlock;
  measuredLitres?:    number;
  recordedLitres?:    number;
  unexplainedLitres?: number;
  leakLitres?:        number;
  refillLitres?:      number;
  unexplainedPct?:    number;
  [key: string]: number | string | SiteBlock | undefined;
}

/** A site in the report, with its live state. */
export interface ConsumptionSiteDetail {
  id:                   string;
  name:                 string;
  address?:             string;
  currentVolumeLitres?: number;
  capacityLitres?:      number;
  /** Sites: how full the site is. Tanks: the tank's current level (currentLevelPct). */
  utilisationPct?:      number;
  status?:              string;
  /** Sites only. */
  tankCount?:           number;
  lastReadingAt?:       string | null;
  lastReadingDisplay?:  string;
  lastReadingAgo?:      string;
}

/** A tank in the report, with its live state. */
export interface ConsumptionTankDetail extends ConsumptionSiteDetail {
  siteId?:   string;
  siteName?: string;
  fuelType?: string;
}

function mapConsumptionDetail(r: any): ConsumptionSiteDetail {
  const last = r?.lastReading;
  const lastObj = last && typeof last === 'object' ? last : null;
  return {
    id:                  String(r?.id ?? ''),
    name:                String(r?.name ?? ''),
    address:             r?.address ?? r?.location?.address ?? undefined,
    currentVolumeLitres: numOrUndef(r?.currentVolumeLitres ?? r?.currentVolume ?? r?.volumeLitres),
    capacityLitres:      numOrUndef(r?.capacityLitres ?? r?.capacity),
    utilisationPct:      numOrUndef(r?.utilisationPct ?? r?.utilizationPct ?? r?.currentLevelPct ?? r?.utilisation ?? r?.utilization),
    status:              r?.status ?? undefined,
    tankCount:           numOrUndef(r?.tankCount),
    // lastReading is a display string ("12 Oct 2026, 2:35 PM WAT"), an ISO time, or an object.
    lastReadingAt:       r?.lastReadingAt ?? lastObj?.at ?? lastObj?.timestamp ?? (isIsoDate(last) ? last : null),
    lastReadingDisplay:  r?.lastReadingDisplay ?? lastObj?.display ?? lastObj?.timestampDisplay
                         ?? (typeof last === 'string' && !isIsoDate(last) ? last : undefined),
    lastReadingAgo:      r?.lastReadingAgo ?? lastObj?.timeAgo ?? r?.timeAgo ?? undefined,
  };
}

export interface ConsumptionResult {
  points:     ConsumptionPoint[];
  totals:     ConsumptionTotals;
  live:       ConsumptionLiveSnapshot | null;
  scope:      ConsumptionScope;
  breakdown:  ConsumptionBreakdownItem[];
  sites:      ConsumptionSiteDetail[];
  tanks:      ConsumptionTankDetail[];
  /** The site or tank the report was filtered on, as the server resolved it. */
  filters:    { site?: SiteBlock; tank?: TankBlock };
  fromDisplay: string;
  toDisplay:   string;
  count:      number;
  truncated:  boolean;
  interval:   string;
  groupBy:    string;
  from:       string;
  to:         string;
}

function numOf(v: any): number {
  return v == null || v === '' || Number.isNaN(Number(v)) ? 0 : Number(v);
}

export async function fetchConsumption(params: ConsumptionParams = {}): Promise<ConsumptionResult> {
  const qs = new URLSearchParams();
  if (params.siteId   != null) qs.set('siteId',   params.siteId);
  if (params.tankId   != null) qs.set('tankId',   params.tankId);
  if (params.from     != null) qs.set('from',     params.from);
  if (params.to       != null) qs.set('to',       params.to);
  if (params.interval != null) qs.set('interval', params.interval);
  if (params.hours    != null) qs.set('hours',    String(params.hours));
  if (params.groupBy  != null) qs.set('groupBy',  params.groupBy);
  if (params.since    != null) qs.set('since',    params.since);

  const res  = await apiFetch(`/api/analytics/consumption/${qs.toString() ? `?${qs}` : ''}`);
  const body = await res.json().catch(() => null);

  if (!res.ok) {
    const message = body?.detail ?? body?.message ?? 'Failed to fetch consumption data.';
    throw { message, status: res.status } as ApiError;
  }

  // The endpoint returns a full envelope: { data: [...], totals: {...}, live: {...},
  // scope: {...}, breakdown: [...], count, truncated, interval, groupBy, from, to }.
  // Older/bare-array responses are still supported as a fallback.
  const items: any[] = Array.isArray(body?.data)    ? body.data
                      : Array.isArray(body?.results) ? body.results
                      : Array.isArray(body)          ? body
                      : [];

  const RESERVED = new Set([
    'timestamp', 'time', 'date', 't', 'consumption', 'value', 'target', 'efficiency', 'prediction',
    'measuredLitres', 'recordedLitres', 'unexplainedLitres', 'leakLitres', 'refillLitres', 'readings',
    'label', 'netChangeLitres',
  ]);

  const points: ConsumptionPoint[] = items.map(item => {
    const ts = item.t ?? item.timestamp ?? item.time ?? '';
    // This endpoint's row is a mini reconciliation (measured/recorded/unexplained/
    // leak/refill), not a single "consumption" field — measuredLitres is what
    // sensors show actually left the tank in this period, so that's the trend line.
    const measured = item.measuredLitres != null ? Number(item.measuredLitres) : undefined;
    const point: ConsumptionPoint = {
      timestamp:   ts,
      date:        item.date ?? (typeof ts === 'string' ? ts.slice(0, 10) : ''),
      consumption: measured ?? Number(item.consumption ?? item.value ?? 0),
      target:      item.target     != null ? Number(item.target)     : undefined,
      efficiency:  item.efficiency != null ? Number(item.efficiency) : undefined,
      prediction:  item.prediction != null ? Number(item.prediction) : undefined,
      label:             item.label ?? undefined,
      netChangeLitres:   item.netChangeLitres != null ? Number(item.netChangeLitres) : undefined,
      recordedLitres:    item.recordedLitres    != null ? Number(item.recordedLitres)    : undefined,
      unexplainedLitres: item.unexplainedLitres != null ? Number(item.unexplainedLitres) : undefined,
      leakLitres:        item.leakLitres        != null ? Number(item.leakLitres)        : undefined,
      refillLitres:      item.refillLitres      != null ? Number(item.refillLitres)      : undefined,
      readings:          item.readings          != null ? Number(item.readings)          : undefined,
    };
    // Capture dynamic generator keys (generator1, generator2, …)
    Object.keys(item).forEach(k => {
      if (!RESERVED.has(k) && item[k] != null) point[k] = Number(item[k]);
    });
    return point;
  });

  const totalsRaw = body?.totals ?? {};
  const totals: ConsumptionTotals = {
    measuredLitres:    numOf(totalsRaw.measuredLitres),
    recordedLitres:    numOf(totalsRaw.recordedLitres),
    unexplainedLitres: numOf(totalsRaw.unexplainedLitres),
    leakLitres:        numOf(totalsRaw.leakLitres),
    refillLitres:      numOf(totalsRaw.refillLitres),
    unexplainedPct:    numOf(totalsRaw.unexplainedPct),
  };

  const liveRaw = body?.live ?? null;
  const live: ConsumptionLiveSnapshot | null = liveRaw ? {
    totalVolumeLitres:   numOf(liveRaw.totalVolumeLitres),
    totalCapacityLitres: numOf(liveRaw.totalCapacityLitres),
    utilizationPct:      numOf(liveRaw.utilizationPct),
    tankCount:           numOf(liveRaw.tankCount),
    tanksOffline:        numOf(liveRaw.tanksOffline),
    asOf:                String(liveRaw.asOf ?? ''),
  } : null;

  const scopeRaw = body?.scope ?? {};
  const scope: ConsumptionScope = {
    sites: numOf(scopeRaw.sites),
    tanks: numOf(scopeRaw.tanks),
  };

  const breakdown: ConsumptionBreakdownItem[] = (Array.isArray(body?.breakdown) ? body.breakdown : []).map((r: any) => {
    const row: ConsumptionBreakdownItem = { ...r };
    for (const k of ['measuredLitres', 'recordedLitres', 'unexplainedLitres', 'leakLitres', 'refillLitres', 'unexplainedPct']) {
      if (r?.[k] != null) row[k] = numOf(r[k]);
    }
    row.id   = r?.id != null ? String(r.id) : (r?.siteId ?? r?.tankId ?? undefined);
    row.name = r?.name ?? r?.label ?? r?.tankName ?? r?.siteName ?? r?.site?.name ?? undefined;
    row.site = mapSiteBlock(r?.site);
    return row;
  });

  const sites: ConsumptionSiteDetail[] = Array.isArray(body?.sites) ? body.sites.map(mapConsumptionDetail) : [];
  const tanks: ConsumptionTankDetail[] = Array.isArray(body?.tanks) ? body.tanks.map((t: any) => ({
    ...mapConsumptionDetail(t),
    siteId:   t?.siteId ?? t?.site?.id ?? undefined,
    siteName: t?.siteName ?? t?.site?.name ?? undefined,
    fuelType: t?.fuelType ?? undefined,
  })) : [];
  const filtersRaw = body?.filters ?? {};

  return {
    points,
    totals,
    live,
    scope,
    breakdown,
    sites,
    tanks,
    filters: {
      site: typeof filtersRaw.site === 'object' ? mapSiteBlock(filtersRaw.site) : undefined,
      tank: typeof filtersRaw.tank === 'object' ? mapTankBlock(filtersRaw.tank) : undefined,
    },
    fromDisplay: String(body?.fromDisplay ?? ''),
    toDisplay:   String(body?.toDisplay   ?? ''),
    count:     numOf(body?.count ?? points.length),
    truncated: Boolean(body?.truncated),
    interval:  String(body?.interval ?? params.interval ?? 'day'),
    groupBy:   String(body?.groupBy  ?? 'none'),
    from:      String(body?.from ?? ''),
    to:        String(body?.to   ?? ''),
  };
}

/* ── Acknowledge alert ── */
export async function acknowledgeAlert(alertId: string, note?: string): Promise<void> {
  const res = await apiFetch(`/api/alerts/${alertId}/acknowledge/`, {
    method: 'PUT',
    body: JSON.stringify({ note: note ?? '' }),
  });

  if (res.status === 200 || res.status === 204) return;

  const body = await res.json().catch(() => null);
  if (res.status === 404) throw { message: "This alert isn't available to your account.", status: 404 } as ApiError;
  throw toApiError(res.status, body, 'Failed to acknowledge alert.');
}

/* ── Resolve alert ── */
export async function resolveAlert(alertId: string, resolution: string, note?: string): Promise<void> {
  const res = await apiFetch(`/api/alerts/${alertId}/resolve/`, {
    method: 'PUT',
    // Body is { resolution, note } (October 2026 guide §7). `summary` is sent too for
    // servers still on the older field name; an unknown key is ignored.
    body: JSON.stringify({ resolution, summary: resolution, note: note ?? '' }),
  });

  if (res.status === 200 || res.status === 204) return;

  const body = await res.json().catch(() => null);
  if (res.status === 404) throw { message: "This alert isn't available to your account.", status: 404 } as ApiError;
  throw toApiError(res.status, body, 'Failed to resolve alert.');
}

/* ── Delete alert (admin only) ── */
export async function deleteAlert(alertId: string): Promise<void> {
  const res = await apiFetch(`/api/alerts/${alertId}/`, { method: 'DELETE' });

  if (res.status === 204 || res.status === 200) return;

  const body = await res.json().catch(() => null);
  if (res.status === 403) throw { message: 'Admin access required.', status: 403 } as ApiError;
  if (res.status === 404) throw { message: 'Alert not found.',       status: 404 } as ApiError;
  throw { message: body?.detail ?? body?.message ?? 'Failed to delete alert.', status: res.status } as ApiError;
}

/* ── Sites ── */
export interface SitesParams {
  search?: string;
  status?: 'active' | 'inactive' | 'critical' | 'warning';
  page?:   number;
  limit?:  number;
}

export interface SitesResult {
  count:    number;
  next:     string | null;
  previous: string | null;
  results:  import('../utils/mockData').SiteData[];
}

/** Number or undefined — keeps an absent level absent rather than turning it into 0. */
function numOrUndef(v: any): number | undefined {
  return v == null || v === '' || Number.isNaN(Number(v)) ? undefined : Number(v);
}

function mapTank(t: any): import('../utils/mockData').TankData {
  // Site detail now returns full tank blocks with the alarm levels nested under `thresholds`.
  const th = t.thresholds && typeof t.thresholds === 'object' ? t.thresholds : {};
  const capacity     = Number(t.capacity ?? t.tank_capacity ?? 0);
  const currentLevel = Number(t.current_level ?? t.currentLevel ?? t.level_percentage ?? t.level ?? 0);
  const currentVolume = t.current_volume != null
    ? Number(t.current_volume)
    : (t.currentVolume != null ? Number(t.currentVolume) : Math.round((currentLevel / 100) * capacity));

  return {
    id:           String(t.id ?? ''),
    name:         String(t.name ?? t.tank_name ?? 'Tank'),
    capacity,
    currentLevel,
    currentVolume,
    fuelType:     (t.fuel_type ?? t.fuelType ?? 'diesel') as 'diesel' | 'petrol',
    lastRefill:   String(t.last_refill ?? t.lastRefill ?? ''),
    quality: {
      status:       (t.quality?.status ?? t.quality_status ?? 'good') as 'good' | 'warning' | 'contaminated',
      waterContent: Number(t.quality?.water_content ?? t.quality?.waterContent ?? t.water_content ?? 0),
      sediment:     (t.quality?.sediment ?? t.sediment ?? 'normal') as 'normal' | 'high',
      temperature:  Number(t.quality?.temperature ?? t.temperature ?? 25),
    },
    sensors: {
      levelSensor:   Boolean(t.sensors?.level_sensor   ?? t.sensors?.levelSensor   ?? true),
      qualitySensor: Boolean(t.sensors?.quality_sensor ?? t.sensors?.qualitySensor ?? false),
      flowMeter:     Boolean(t.sensors?.flow_meter      ?? t.sensors?.flowMeter      ?? false),
    },
    status:               t.status ?? t.tank_status ?? undefined,
    criticalLevelPct:     numOrUndef(t.criticalLevelPct     ?? t.critical_level_pct ?? th.criticalLevelPct),
    minimumLevelPct:      numOrUndef(t.minimumLevelPct      ?? t.minimum_level_pct  ?? th.minimumLevelPct),
    reorderLevelPct:      numOrUndef(t.reorderLevelPct      ?? t.reorder_level_pct  ?? th.reorderLevelPct),
    overfillLevelPct:     numOrUndef(t.overfillLevelPct     ?? t.overfill_level_pct ?? th.overfillLevelPct),
    litresToReorderPoint: numOrUndef(t.litresToReorderPoint ?? t.litres_to_reorder_point),
    alertsEnabled:        t.alertsEnabled ?? t.alerts_enabled ?? undefined,
  };
}

function mapGenerator(g: any): import('../utils/mockData').GeneratorData {
  return {
    id:              String(g.id ?? ''),
    name:            String(g.name ?? g.generator_name ?? 'Generator'),
    status:          (g.status ?? 'stopped') as 'running' | 'stopped' | 'maintenance',
    fuelConsumption: Number(g.fuel_consumption ?? g.fuelConsumption ?? g.consumption_rate ?? 0),
    runtime:         Number(g.runtime ?? g.run_time ?? 0),
    efficiency:      Number(g.efficiency ?? g.efficiency_score ?? 0),
    lastMaintenance: String(g.last_maintenance ?? g.lastMaintenance ?? ''),
  };
}

function mapSite(s: any): import('../utils/mockData').SiteData {
  const lat = Number(s.location?.lat ?? s.location?.latitude ?? s.lat ?? s.latitude ?? 0);
  const lng = Number(s.location?.lng ?? s.location?.longitude ?? s.lng ?? s.longitude ?? 0);
  const address = String(s.location?.address ?? s.address ?? '');

  return {
    id:         String(s.id ?? ''),
    name:       String(s.name ?? s.site_name ?? 'Site'),
    location:   { lat, lng, address },
    tanks:      (s.tanks ?? s.tank_set ?? s.tanks_list ?? []).map(mapTank),
    generators: (s.generators ?? s.generator_set ?? s.generators_list ?? s.gen_list ?? []).map(mapGenerator),
    // Site detail includes its open alerts, newest first, at most 50.
    alerts:     (Array.isArray(s.alerts) ? s.alerts : []).map((a: any) => mapAlert(a) as any),
  };
}

/* ── Site detail (guide §5, FIXED): GET /api/v1/sites/{id}/ used to return 500 for every
   site. It now returns the site with tanks[] (full tank blocks), generators[] and its open
   alerts[]. The raw tank blocks are kept too, for their last-reading labels. */
export interface SiteDetail {
  site:       import('../utils/mockData').SiteData;
  status?:    string;
  tankBlocks: TankBlock[];
  alerts:     AlertRecord[];
}

export async function fetchSiteDetail(siteId: string): Promise<SiteDetail> {
  const res  = await apiFetch(`/api/v1/sites/${siteId}/`);
  const body = await res.json().catch(() => null);
  if (res.status === 404) throw { message: "This site isn't available to your account.", status: 404 } as ApiError;
  if (!res.ok) throw toApiError(res.status, body, 'Failed to load the site.');
  const raw = body?.data ?? body;
  return {
    site:       mapSite(raw),
    status:     raw?.status ?? undefined,
    tankBlocks: (Array.isArray(raw?.tanks) ? raw.tanks : []).map(mapTankBlock).filter(Boolean) as TankBlock[],
    alerts:     (Array.isArray(raw?.alerts) ? raw.alerts : []).map(mapAlert),
  };
}

export interface UpdateSitePayload {
  name?:    string;
  address?: string;
  lat?:     string;
  lng?:     string;
}

export async function updateSite(siteId: string, payload: UpdateSitePayload): Promise<import('../utils/mockData').SiteData> {
  const res  = await apiFetch(`/api/v1/sites/${siteId}/`, {
    method: 'PUT',
    body:   JSON.stringify(payload),
  });
  const body = await res.json().catch(() => null);

  if (res.status === 404) throw { message: 'Site not found.', status: 404 } as ApiError;
  if (!res.ok) throw toApiError(res.status, body, 'Failed to update site.');

  const raw = body?.data ?? body;
  return mapSite(raw);
}

export async function deleteSite(siteId: string): Promise<void> {
  const res  = await apiFetch(`/api/v1/sites/${siteId}/`, { method: 'DELETE' });

  if (res.status === 200 || res.status === 204) return;

  const body = await res.json().catch(() => null);
  if (res.status === 404) throw { message: 'Site not found.',     status: 404 } as ApiError;
  throw { message: body?.detail ?? body?.message ?? 'Failed to delete site.', status: res.status } as ApiError;
}

export async function fetchSiteById(siteId: string): Promise<import('../utils/mockData').SiteData> {
  const res  = await apiFetch(`/api/v1/sites/${siteId}/`);
  const body = await res.json().catch(() => null);

  if (res.status === 404) throw { message: 'Site not found.', status: 404 } as ApiError;
  if (!res.ok) {
    const message = body?.detail ?? body?.message ?? 'Failed to fetch site.';
    throw { message, status: res.status } as ApiError;
  }

  const raw = body?.data ?? body;
  return mapSite(raw);
}

export interface CreateSitePayload {
  name:    string;
  address: string;
  lat:     string;
  lng:     string;
}

export async function createSite(payload: CreateSitePayload): Promise<import('../utils/mockData').SiteData> {
  const res  = await apiFetch('/api/v1/sites/', {
    method: 'POST',
    body:   JSON.stringify(payload),
  });
  const body = await res.json().catch(() => null);

  if (!res.ok) throw toApiError(res.status, body, 'Failed to create site.');

  const raw = body?.data ?? body;
  return mapSite(raw);
}

export async function fetchSites(params: SitesParams = {}): Promise<SitesResult> {
  const qs = new URLSearchParams();
  if (params.search != null) qs.set('search', params.search);
  if (params.status != null) qs.set('status', params.status);
  if (params.page   != null) qs.set('page',   String(params.page));
  if (params.limit  != null) qs.set('limit',  String(params.limit));

  const res  = await apiFetch(`/api/v1/sites/${qs.toString() ? `?${qs}` : ''}`);
  const body = await res.json().catch(() => null);

  if (!res.ok) {
    const message = body?.detail ?? body?.message ?? 'Failed to fetch sites.';
    throw { message, status: res.status } as ApiError;
  }

  const raw     = body?.data ?? body;
  const items: any[] = Array.isArray(raw) ? raw : (raw?.results ?? []);

  return {
    count:    body?.total ?? raw?.count ?? items.length,
    next:     raw?.next     ?? null,
    previous: raw?.previous ?? null,
    results:  items.map(mapSite),
  };
}

/* ── Analytics: summary (dashboard KPI cards) ── */
/* The six original keys are unchanged; October 2026 adds grouped blocks (guide §5). */
export interface AttentionItem {
  tankId:    string;
  tankName:  string;
  siteId?:   string;
  siteName?: string;
  level?:    number;
  currentVolume?: number;
  capacity?: number;
  status?:   string;
  /** e.g. "At 11.80% — minimum threshold". Show verbatim. */
  reason:    string;
}

export interface AnalyticsSummary {
  totalSites:        number;
  totalTanks:        number;
  totalFuelVolume:   number;
  runningGenerators: number;
  activeAlerts:      number;
  criticalAlerts:    number;
  asOf?:             string;
  asOfDisplay?:      string;
  fuel?: {
    totalVolumeLitres: number; totalVolumeDisplay?: string; totalCapacityLitres: number; utilizationPct: number;
    byFuelType: { fuelType: string; tanks: number; volumeLitres: number; capacityLitres: number; utilizationPct: number }[];
  };
  sites?:      { total: number; withTanks: number; withoutTanks: number; withActiveAlerts: number };
  tanks?:      { total: number; normal: number; warning: number; critical: number; offline: number; belowReorder: number };
  generators?: { total: number; running: number; stopped: number; maintenance: number };
  alerts?:     { active: number; critical: number; unacknowledged: number; raisedLast24h: number; byType: Record<string, number> };
  sensors?:    { total: number; online: number; offline: number; lastReadingAt?: string; lastReadingDisplay?: string; lastReadingAgo?: string };
  /** Litres consumed, refilled, flagged as theft, dispensed and unexplained in the last 24 h. */
  last24h?:    { consumedLitres: number; refilledLitres: number; theftLitres: number; dispensedLitres: number; dispenses: number; unexplainedLitres: number };
  security?:   { openTheftIncidents: number; suspectedTheftLitres30d: number };
  /** Tanks that need action now. */
  attention:   AttentionItem[];
}

export async function fetchAnalyticsSummary(): Promise<AnalyticsSummary> {
  const res  = await apiFetch('/api/analytics/summary/');
  const body = await res.json().catch(() => null);

  if (!res.ok) {
    const message = body?.detail ?? body?.message ?? 'Failed to fetch analytics summary.';
    throw { message, status: res.status } as ApiError;
  }

  const raw = body?.data ?? body;
  const f = raw?.fuel, si = raw?.sites, tk = raw?.tanks, ge = raw?.generators, al = raw?.alerts;
  const se = raw?.sensors, l24 = raw?.last24h, sec = raw?.security;
  const pick = (o: any, ...keys: string[]) => { for (const k of keys) if (o?.[k] != null) return Number(o[k]); return 0; };

  return {
    totalSites:        Number(raw?.totalSites        ?? raw?.total_sites        ?? 0),
    totalTanks:        Number(raw?.totalTanks        ?? raw?.total_tanks        ?? 0),
    totalFuelVolume:   Number(raw?.totalFuelVolume   ?? raw?.total_fuel_volume  ?? 0),
    runningGenerators: Number(raw?.runningGenerators ?? raw?.running_generators ?? 0),
    activeAlerts:      Number(raw?.activeAlerts      ?? raw?.active_alerts      ?? 0),
    criticalAlerts:    Number(raw?.criticalAlerts    ?? raw?.critical_alerts    ?? 0),
    asOf:        raw?.asOf ?? undefined,
    asOfDisplay: raw?.asOfDisplay ?? undefined,
    fuel: f && typeof f === 'object' ? {
      totalVolumeLitres: numOf(f.totalVolumeLitres), totalVolumeDisplay: f.totalVolumeDisplay,
      totalCapacityLitres: numOf(f.totalCapacityLitres), utilizationPct: numOf(f.utilizationPct),
      byFuelType: (Array.isArray(f.byFuelType) ? f.byFuelType : []).map((x: any) => ({
        fuelType: String(x.fuelType ?? ''), tanks: numOf(x.tanks), volumeLitres: numOf(x.volumeLitres),
        capacityLitres: numOf(x.capacityLitres), utilizationPct: numOf(x.utilizationPct),
      })),
    } : undefined,
    sites: si && typeof si === 'object' ? { total: numOf(si.total), withTanks: numOf(si.withTanks), withoutTanks: numOf(si.withoutTanks), withActiveAlerts: numOf(si.withActiveAlerts) } : undefined,
    tanks: tk && typeof tk === 'object' ? { total: numOf(tk.total), normal: numOf(tk.normal), warning: numOf(tk.warning), critical: numOf(tk.critical), offline: numOf(tk.offline), belowReorder: numOf(tk.belowReorder) } : undefined,
    generators: ge && typeof ge === 'object' ? { total: numOf(ge.total), running: numOf(ge.running), stopped: numOf(ge.stopped), maintenance: numOf(ge.maintenance) } : undefined,
    alerts: al && typeof al === 'object' ? {
      active: numOf(al.active), critical: numOf(al.critical), unacknowledged: numOf(al.unacknowledged),
      raisedLast24h: numOf(al.raisedLast24h), byType: al.byType && typeof al.byType === 'object' ? al.byType : {},
    } : undefined,
    sensors: se && typeof se === 'object' ? {
      total: numOf(se.total), online: numOf(se.online), offline: numOf(se.offline),
      // lastReadingAt arrives as display text ("12 Oct 2026, 1:35 PM WAT"), not ISO.
      lastReadingAt: isIsoDate(se.lastReadingAt) ? se.lastReadingAt : undefined,
      lastReadingDisplay: se.lastReadingDisplay ?? (typeof se.lastReadingAt === 'string' && !isIsoDate(se.lastReadingAt) ? se.lastReadingAt : undefined),
      lastReadingAgo: se.lastReadingAgo ?? undefined,
    } : undefined,
    last24h: l24 && typeof l24 === 'object' ? {
      consumedLitres:    pick(l24, 'consumedLitres', 'consumed', 'consumptionLitres'),
      refilledLitres:    pick(l24, 'refilledLitres', 'refilled', 'refillLitres'),
      theftLitres:       pick(l24, 'theftFlaggedLitres', 'theftLitres', 'flaggedTheftLitres', 'theft'),
      dispensedLitres:   pick(l24, 'dispensedLitres', 'dispensed'),
      dispenses:         pick(l24, 'dispenses', 'dispenseCount'),
      unexplainedLitres: pick(l24, 'unexplainedLitres', 'unexplained'),
    } : undefined,
    security: sec && typeof sec === 'object' ? {
      openTheftIncidents: pick(sec, 'openTheftIncidents', 'openTheft', 'open'),
      suspectedTheftLitres30d: pick(sec, 'suspectedTheftLitres30d', 'suspectedTheftLitres', 'suspectedLitres'),
    } : undefined,
    attention: (Array.isArray(raw?.attention) ? raw.attention : []).map((a: any) => ({
      tankId:   String(a.tankId ?? a.id ?? a.tank?.id ?? ''),
      tankName: String(a.tankName ?? a.name ?? a.tank?.name ?? 'Tank'),
      siteId:   a.siteId ?? a.site?.id ?? undefined,
      siteName: a.siteName ?? a.site?.name ?? undefined,
      level:    numOrUndef(a.level ?? a.currentLevel ?? a.levelPct),
      currentVolume: numOrUndef(a.currentVolume ?? a.currentVolumeLitres),
      capacity:      numOrUndef(a.capacity ?? a.capacityLitres),
      status:   a.status ?? undefined,
      reason:   String(a.reason ?? ''),
    })),
  };
}

/* ── Analytics: fuel levels ── */
export interface FuelLevelsParams {
  siteId?: string;
  tankId?: string;
  /** Default 24, max 744 (31 days). */
  hours?:  number;
  /** 'tank' adds a byTank series per tank. */
  groupBy?: 'tank';
}

export type FuelLevelEventType = 'refill' | 'consumption' | 'leak';

export interface FuelLevelPoint {
  timestamp:  string;
  level:      number;
  volume:     number;
  capacity:   number;
  // Present on tank-history rows: what kind of change this reading reflects.
  // 'leak' marks a sharp unexplained fall — cross-reference against deliveries.
  eventType?: FuelLevelEventType;
  /* October 2026 (guide §3.6): `level` is now total volume ÷ COMBINED capacity (it used
     to be the average of each tank's %), and `capacity` is the combined capacity. A tank
     that didn't report in an hour has its last volume carried forward, so a point where
     tanksReporting < tanksWithData is partly estimated — draw it faded. */
  label?:          string;
  tanksReporting?: number;
  tanksWithData?:  number;
}

export async function fetchFuelLevels(params: FuelLevelsParams = {}): Promise<FuelLevelPoint[]> {
  const qs = new URLSearchParams();
  if (params.siteId != null) qs.set('siteId', params.siteId);
  if (params.tankId != null) qs.set('tankId', params.tankId);
  if (params.hours  != null) qs.set('hours',  String(Math.min(744, Math.max(1, params.hours))));
  if (params.groupBy)        qs.set('groupBy', params.groupBy);

  const res  = await apiFetch(`/api/analytics/fuel-levels/${qs.toString() ? `?${qs}` : ''}`);
  const body = await res.json().catch(() => null);

  if (!res.ok) {
    const message = body?.detail ?? body?.message ?? 'Failed to fetch fuel level data.';
    throw { message, status: res.status } as ApiError;
  }

  const raw = body?.data ?? body?.results ?? body;
  const items: any[] = Array.isArray(raw) ? raw : [];

  // The point list lives under data[]; `tanks`, `hours`, `asOf`, `totalCapacity` sit beside it.
  const list: any[] = Array.isArray(raw) ? raw : (Array.isArray(raw?.data) ? raw.data : Array.isArray(raw?.points) ? raw.points : items);
  const totalCapacity = numOrUndef(body?.totalCapacity ?? raw?.totalCapacity);
  return list.map(item => ({
    timestamp: item.timestamp ?? item.time ?? '',
    level:     Number(item.level ?? item.fuel_level ?? item.level_percentage ?? 0),
    volume:    Number(item.volume ?? item.current_volume ?? 0),
    capacity:  Number(item.capacity ?? item.tank_capacity ?? totalCapacity ?? 0),
    eventType: (item.eventType ?? item.event_type) as FuelLevelEventType | undefined,
    label:          item.label ?? undefined,
    tanksReporting: numOrUndef(item.tanksReporting),
    tanksWithData:  numOrUndef(item.tanksWithData),
  }));
}

/* ── Fleet ── */
export interface FleetParams {
  status?: 'en-route' | 'idle' | 'loading' | 'delivering' | 'maintenance';
  fuelType?: 'diesel' | 'petrol';
  page?: number;
  limit?: number;
}

export interface FleetResult {
  results: import('./mockData').TankerData[];
  total: number;
}

function resolveId(obj: any, ...keys: string[]): string {
  for (const key of keys) {
    const v = obj[key];
    if (v == null) continue;
    // nested object with an id field (DRF depth=1)
    if (typeof v === 'object' && v.id != null) return String(v.id);
    // primitive value
    if (v !== '') return String(v);
  }
  return '';
}

function mapFleetTanker(t: any): import('./mockData').TankerData {
  return {
    id: String(t.id ?? t._id ?? ''),
    registrationNumber: t.registration_number ?? t.registrationNumber ?? '',
    fleetNumber: t.fleet_number ?? t.fleetNumber ?? '',
    capacity: Number(t.capacity ?? 0),
    fuelType: t.fuel_type ?? t.fuelType ?? 'diesel',
    currentLoad: Number(t.current_load ?? t.currentLoad ?? 0),
    currentVolume: Number(t.current_volume ?? t.currentVolume ?? 0),
    status: t.status ?? 'idle',
    location: {
      lat: Number(t.location?.lat ?? t.lat ?? 0),
      lng: Number(t.location?.lng ?? t.lng ?? 0),
      address: t.location?.address ?? t.address ?? '',
    },
    assignedDriverId: resolveId(t, 'assigned_driver', 'driver', 'assigned_driver_id', 'driver_id', 'assignedDriverId'),
    assignedSiteId:   resolveId(t, 'assigned_site',   'site',   'assigned_site_id',   'site_id',   'assignedSiteId'),
    lastActivity: t.last_activity ?? t.lastActivity ?? new Date().toISOString(),
  };
}

export interface CreateTankerPayload {
  registrationNumber: string;
  fleetNumber?: string;
  capacity: string;
  fuelType: 'diesel' | 'petrol';
}

export interface UpdateTankerPayload {
  registrationNumber?: string;
  fleetNumber?: string;
  capacity?: string;
  fuelType?: 'diesel' | 'petrol';
  status?: 'en-route' | 'idle' | 'loading' | 'delivering' | 'maintenance';
  assignedDriverId?: number | null;
  assignedSiteId?: string | null;
}

export interface TankerLocation {
  lat: number;
  lng: number;
  address: string;
  speed?: number;
  heading?: number;
  timestamp?: string;
}

export interface TankerTripsParams {
  from?: string;
  to?: string;
  page?: number;
  limit?: number;
}

export interface TankerTrip {
  id: string;
  date: string;
  fromSite: string;
  toSite: string;
  volumeDelivered: number;
  fuelType: 'petrol' | 'diesel';
  status: 'completed' | 'in-progress' | 'cancelled';
}

export interface TankerTripsResult {
  results: TankerTrip[];
  total: number;
}

function mapTankerTrip(t: any): TankerTrip {
  return {
    id:              String(t.id ?? t._id ?? ''),
    date:            t.date ?? t.start_time ?? t.created_at ?? new Date().toISOString(),
    fromSite:        t.from_site ?? t.fromSite ?? t.origin ?? '',
    toSite:          t.to_site   ?? t.toSite   ?? t.destination ?? '',
    volumeDelivered: Number(t.volume_delivered ?? t.volumeDelivered ?? t.volume ?? 0),
    fuelType:        t.fuel_type ?? t.fuelType ?? 'diesel',
    status:          t.status ?? 'completed',
  };
}

export interface DriversParams {
  status?: 'on-duty' | 'off-duty' | 'on-leave';
  siteId?: string;
  page?: number;
  limit?: number;
}

export interface DriversResult {
  results: import('./mockData').DriverData[];
  total: number;
}

function mapDriver(d: any): import('./mockData').DriverData {
  const name: string = d.name ?? d.full_name ?? d.username ?? '';
  const initials = name.split(' ').filter(Boolean).slice(0, 2).map((w: string) => w[0].toUpperCase()).join('') || '??';
  return {
    id:               String(d.id ?? d._id ?? ''),
    name,
    phone:            d.phone ?? d.phone_number ?? '',
    email:            d.email ?? '',
    avatarInitials:   d.avatar_initials ?? d.avatarInitials ?? initials,
    licenseNumber:    d.license_number ?? d.licenseNumber ?? '',
    licenseExpiry:    d.license_expiry ?? d.licenseExpiry ?? '',
    assignedTankerId: resolveId(d, 'assigned_tanker', 'tanker', 'assigned_tanker_id', 'tanker_id', 'assignedTankerId'),
    assignedSiteId:   resolveId(d, 'assigned_site',   'site',   'assigned_site_id',   'site_id',   'assignedSiteId'),
    status:           d.status ?? 'off-duty',
    totalTrips:       Number(d.total_trips   ?? d.totalTrips   ?? 0),
    totalVolume:      Number(d.total_volume  ?? d.totalVolume  ?? 0),
    trips:            [],
  };
}

export interface CreateDriverPayload {
  name: string;
  phone?: string;
  email?: string;
  licenseNumber: string;
  licenseExpiry: string;
}

export interface UpdateDriverPayload {
  name?: string;
  phone?: string;
  email?: string;
  licenseNumber?: string;
  licenseExpiry?: string;
  status?: 'on-duty' | 'off-duty' | 'on-leave';
  assignedTankerId?: string | null;
  assignedSiteId?: string | null;
}

export interface DriverTripsParams {
  from?: string;
  to?: string;
  page?: number;
  limit?: number;
}

export async function fetchDriverTrips(driverId: string, params: DriverTripsParams = {}): Promise<TankerTripsResult> {
  const q = new URLSearchParams();
  if (params.from)  q.set('from',  params.from);
  if (params.to)    q.set('to',    params.to);
  if (params.page)  q.set('page',  String(params.page));
  if (params.limit) q.set('limit', String(params.limit));

  const res = await apiFetch(`/api/fleet/drivers/${driverId}/trips/${q.toString() ? `?${q}` : ''}`);
  const body = await res.json();
  const raw: any[] = Array.isArray(body?.data)    ? body.data
                   : Array.isArray(body?.results) ? body.results
                   : Array.isArray(body)           ? body
                   : [];
  return { results: raw.map(mapTankerTrip), total: body?.total ?? raw.length };
}

export async function deleteDriver(driverId: string): Promise<void> {
  const res = await apiFetch(`/api/fleet/drivers/${driverId}/`, { method: 'DELETE' });
  if (!res.ok && res.status !== 200 && res.status !== 204) {
    throw { message: 'Failed to remove driver', status: res.status };
  }
}

export async function updateDriver(driverId: string, payload: UpdateDriverPayload): Promise<import('./mockData').DriverData> {
  const res = await apiFetch(`/api/fleet/drivers/${driverId}/`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  const body = await res.json().catch(() => null);
  if (!res.ok) throw toApiError(res.status, body, 'Failed to update driver.');
  const raw = body?.data ?? body;
  return mapDriver(raw);
}

export async function fetchDriverById(driverId: string): Promise<import('./mockData').DriverData> {
  const res = await apiFetch(`/api/fleet/drivers/${driverId}/`);
  if (res.status === 404) throw { message: 'Driver not found', status: 404 };
  const body = await res.json();
  const raw = body?.data ?? body;
  const driver = mapDriver(raw);
  // API includes trip history on single-driver endpoint
  const rawTrips: any[] = Array.isArray(raw?.trips) ? raw.trips : [];
  driver.trips = rawTrips.map((t: any) => ({
    id:              String(t.id ?? ''),
    date:            t.date ?? t.start_time ?? t.created_at ?? '',
    fromSite:        t.from_site ?? t.fromSite ?? t.origin ?? '',
    toSite:          t.to_site   ?? t.toSite   ?? t.destination ?? '',
    volumeDelivered: Number(t.volume_delivered ?? t.volumeDelivered ?? t.volume ?? 0),
    fuelType:        t.fuel_type ?? t.fuelType ?? 'diesel',
    status:          t.status ?? 'completed',
  }));
  return driver;
}

export async function createDriver(payload: CreateDriverPayload): Promise<import('./mockData').DriverData> {
  const res = await apiFetch('/api/fleet/drivers/', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  const body = await res.json().catch(() => null);
  if (!res.ok) throw toApiError(res.status, body, 'Failed to add driver.');
  const raw = body?.data ?? body;
  return mapDriver(raw);
}

export async function fetchDrivers(params: DriversParams = {}): Promise<DriversResult> {
  const q = new URLSearchParams();
  if (params.status) q.set('status', params.status);
  if (params.siteId) q.set('siteId', params.siteId);
  if (params.page)   q.set('page',   String(params.page));
  if (params.limit)  q.set('limit',  String(params.limit));

  const res = await apiFetch(`/api/fleet/drivers/${q.toString() ? `?${q}` : ''}`);
  const body = await res.json();
  const raw: any[] = Array.isArray(body?.data)    ? body.data
                   : Array.isArray(body?.results) ? body.results
                   : Array.isArray(body)           ? body
                   : [];
  return { results: raw.map(mapDriver), total: body?.total ?? raw.length };
}

export async function fetchTankerTrips(tankerId: string, params: TankerTripsParams = {}): Promise<TankerTripsResult> {
  if (!tankerId) return { results: [], total: 0 };
  const q = new URLSearchParams();
  if (params.from)  q.set('from',  params.from);
  if (params.to)    q.set('to',    params.to);
  if (params.page)  q.set('page',  String(params.page));
  if (params.limit) q.set('limit', String(params.limit));

  const res = await apiFetch(`/api/fleet/${tankerId}/trips/${q.toString() ? `?${q}` : ''}`);
  const body = await res.json();
  const raw: any[] = Array.isArray(body?.data)    ? body.data
                   : Array.isArray(body?.results) ? body.results
                   : Array.isArray(body)           ? body
                   : [];
  return { results: raw.map(mapTankerTrip), total: body?.total ?? raw.length };
}

export async function fetchTankerLocation(tankerId: string): Promise<TankerLocation> {
  const res = await apiFetch(`/api/fleet/${tankerId}/location/`);
  if (res.status === 404) throw { message: 'No location data', status: 404 };
  const body = await res.json();
  const raw = body?.data ?? body;
  return {
    lat:       Number(raw?.lat ?? raw?.latitude  ?? 0),
    lng:       Number(raw?.lng ?? raw?.longitude ?? 0),
    address:   raw?.address ?? raw?.location_name ?? '',
    speed:     raw?.speed   != null ? Number(raw.speed)   : undefined,
    heading:   raw?.heading != null ? Number(raw.heading) : undefined,
    timestamp: raw?.timestamp ?? raw?.updated_at ?? undefined,
  };
}

export async function deleteTanker(tankerId: string): Promise<void> {
  const res = await apiFetch(`/api/fleet/${tankerId}/`, { method: 'DELETE' });
  if (!res.ok && res.status !== 200 && res.status !== 204) {
    throw { message: 'Failed to remove tanker', status: res.status };
  }
}

export async function updateTanker(tankerId: string, payload: UpdateTankerPayload): Promise<import('./mockData').TankerData> {
  const res = await apiFetch(`/api/fleet/${tankerId}/`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  const body = await res.json().catch(() => null);
  if (!res.ok) throw toApiError(res.status, body, 'Failed to update tanker.');
  const raw = body?.data ?? body;
  return mapFleetTanker(raw);
}

export async function fetchTankerById(tankerId: string): Promise<import('./mockData').TankerData> {
  const res = await apiFetch(`/api/fleet/${tankerId}/`);
  if (res.status === 404) throw { message: 'Tanker not found', status: 404 };
  const body = await res.json();
  const raw = body?.data ?? body;
  return mapFleetTanker(raw);
}

export async function createTanker(payload: CreateTankerPayload): Promise<import('./mockData').TankerData> {
  const res = await apiFetch('/api/fleet/', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  const body = await res.json().catch(() => null);
  if (!res.ok) throw toApiError(res.status, body, 'Failed to add tanker.');
  const raw = body?.data ?? body;
  return mapFleetTanker(raw);
}

export async function fetchFleet(params: FleetParams = {}): Promise<FleetResult> {
  const q = new URLSearchParams();
  if (params.status)   q.set('status',    params.status);
  if (params.fuelType) q.set('fuelType',  params.fuelType);
  if (params.page)     q.set('page',      String(params.page));
  if (params.limit)    q.set('limit',     String(params.limit));

  const res = await apiFetch(`/api/fleet/${q.toString() ? `?${q}` : ''}`);
  const body = await res.json();

  const raw: any[] = Array.isArray(body?.data)
    ? body.data
    : Array.isArray(body?.results)
    ? body.results
    : Array.isArray(body)
    ? body
    : [];

  return { results: raw.map(mapFleetTanker), total: body?.total ?? raw.length };
}

/* ── Reports: event activity log ── */
export interface ReportEventsParams {
  search?: string;
  type?: string;
  siteId?: string;
  from?: string;
  to?: string;
  page?: number;
  limit?: number;
}

export interface ReportEventsResult {
  results: AlertRecord[];
  total: number;
  page: number;
  pages: number;
}

export async function fetchReportEvents(params: ReportEventsParams = {}): Promise<ReportEventsResult> {
  const qs = new URLSearchParams();
  if (params.search) qs.set('search', params.search);
  if (params.type)   qs.set('type',   params.type);
  if (params.siteId) qs.set('siteId', params.siteId);
  if (params.from)   qs.set('from',   params.from);
  if (params.to)     qs.set('to',     params.to);
  if (params.page)   qs.set('page',   String(params.page));
  if (params.limit)  qs.set('limit',  String(params.limit));

  const res  = await apiFetch(`/api/reports/events/${qs.toString() ? `?${qs}` : ''}`);
  const body = await res.json().catch(() => null);

  if (!res.ok) {
    const message = body?.detail ?? body?.message ?? 'Failed to fetch events.';
    throw { message, status: res.status } as ApiError;
  }

  const raw   = body?.data ?? body;
  const items: any[] = Array.isArray(raw) ? raw : (raw?.results ?? []);

  return {
    results: items.map((e: any) => ({
      id:           String(e.id           ?? ''),
      type:         e.type                ?? 'low_fuel',
      severity:     e.severity            ?? 'low',
      title:        e.title               ?? '',
      message:      e.message             ?? '',
      timestamp:    e.timestamp           ?? e.created_at ?? '',
      siteId:       e.siteId              ?? e.site_id    ?? '',
      tankId:       e.tankId              ?? e.tank_id,
      generatorId:  e.generatorId         ?? e.generator_id,
      acknowledged: Boolean(e.acknowledged ?? false),
      resolved:     Boolean(e.resolved     ?? false),
    })),
    total: body?.total ?? raw?.count ?? items.length,
    page:  body?.page  ?? raw?.page  ?? 1,
    pages: body?.pages ?? raw?.pages ?? 1,
  };
}

/* ── Report issues ── */
export interface ReportIssuesParams {
  search?: string;
  severity?: string;
  status?: string;
  siteId?: string;
  from?: string;
  to?: string;
  page?: number;
  limit?: number;
}

export async function fetchReportIssues(params: ReportIssuesParams = {}): Promise<ReportEventsResult> {
  const qs = new URLSearchParams();
  if (params.search)   qs.set('search',   params.search);
  if (params.severity) qs.set('severity', params.severity);
  if (params.status)   qs.set('status',   params.status);
  if (params.siteId)   qs.set('siteId',   params.siteId);
  if (params.from)     qs.set('from',     params.from);
  if (params.to)       qs.set('to',       params.to);
  if (params.page)     qs.set('page',     String(params.page));
  if (params.limit)    qs.set('limit',    String(params.limit));

  const res  = await apiFetch(`/api/reports/issues/${qs.toString() ? `?${qs}` : ''}`);
  const body = await res.json().catch(() => null);

  if (!res.ok) {
    const message = body?.detail ?? body?.message ?? 'Failed to fetch issues.';
    throw { message, status: res.status } as ApiError;
  }

  const raw   = body?.data ?? body;
  const items: any[] = Array.isArray(raw) ? raw : (raw?.results ?? []);

  return {
    results: items.map((e: any) => ({
      id:           String(e.id           ?? ''),
      type:         e.type                ?? 'low_fuel',
      severity:     e.severity            ?? 'low',
      title:        e.title               ?? '',
      message:      e.message             ?? '',
      timestamp:    e.timestamp           ?? e.created_at ?? '',
      siteId:       e.siteId              ?? e.site_id    ?? '',
      tankId:       e.tankId              ?? e.tank_id,
      generatorId:  e.generatorId         ?? e.generator_id,
      acknowledged: Boolean(e.acknowledged ?? false),
      resolved:     Boolean(e.resolved     ?? false),
    })),
    total: body?.total ?? raw?.count ?? items.length,
    page:  body?.page  ?? raw?.page  ?? 1,
    pages: body?.pages ?? raw?.pages ?? 1,
  };
}

/* ── Security events ──
   Status moves open → investigating → resolved. A leak event is suppressed
   while an open one stands on the same tank, so resolving is what lets the
   next one be raised. */
export type SecurityEventStatus = 'open' | 'investigating' | 'resolved';

export interface SecurityEvent {
  id: string;
  /** Use this — not `id` — for acknowledge / investigate / resolve. */
  eventId: string;
  type: 'theft' | 'leak';
  severity: 'low' | 'medium' | 'high' | 'critical';
  siteId: string;
  siteName?: string;
  tankId?: string;
  tankName?: string;
  timestamp: string;
  acknowledged: boolean;
  resolved: boolean;
  title: string;
  message: string;
  volumeLoss?: number;
  estimatedLoss?: number;
  status: SecurityEventStatus;
  acknowledgedBy?: UserBlock;
  acknowledgedAtDisplay?: string;
  acknowledgeNote?: string;
  investigator?: UserBlock;
  investigationNote?: string;
}

export interface SecurityEventsParams {
  siteId?: string;
  type?: string;
  severity?: string;
  from?: string;
  to?: string;
  page?: number;
  limit?: number;
}

export interface SecurityEventsResult {
  results: SecurityEvent[];
  total: number;
  page: number;
  pages: number;
}

/** Shared by /api/security/events/, theft-detections, and leak-detections —
 *  the latter two are "the event plus extra fields" per the frontend guide. */
function mapSecurityEvent(e: any): SecurityEvent {
  const resolved    = Boolean(e.resolved     ?? false);
  const acknowledged = Boolean(e.acknowledged ?? false);
  return {
    id:            String(e.id             ?? ''),
    eventId:       String(e.eventId ?? e.event_id ?? e.id ?? ''),
    type:          e.type                  ?? 'theft',
    severity:      e.severity              ?? 'low',
    siteId:        String(e.siteId         ?? e.site_id      ?? ''),
    siteName:      e.siteName              ?? e.site_name    ?? undefined,
    tankId:        e.tankId                ?? e.tank_id,
    tankName:      e.tankName              ?? e.tank_name    ?? undefined,
    timestamp:     e.timestamp             ?? e.created_at   ?? '',
    acknowledged,
    resolved,
    title:         e.title                 ?? '',
    message:       e.message               ?? '',
    volumeLoss:    e.volumeLoss  != null ? Number(e.volumeLoss)  : e.volume_loss  != null ? Number(e.volume_loss)  : undefined,
    estimatedLoss: e.estimatedLoss != null ? Number(e.estimatedLoss) : e.estimated_loss != null ? Number(e.estimated_loss) : undefined,
    status:        (e.status as SecurityEventStatus) ?? (resolved ? 'resolved' : acknowledged ? 'investigating' : 'open'),
    acknowledgedBy:        mapUserBlock(e.acknowledgedBy),
    acknowledgedAtDisplay: e.acknowledgedAtDisplay ?? e.acknowledgedAt ?? undefined,
    acknowledgeNote:       e.acknowledgeNote ?? undefined,
    investigator:          mapUserBlock(e.investigator),
    investigationNote:     e.investigationNote ?? undefined,
  };
}

export async function fetchSecurityEvents(params: SecurityEventsParams = {}): Promise<SecurityEventsResult> {
  const qs = new URLSearchParams();
  if (params.siteId)   qs.set('siteId',    params.siteId);
  if (params.type)     qs.set('type',      params.type);
  if (params.severity) qs.set('severity',  params.severity);
  if (params.from)     qs.set('from',      params.from);
  if (params.to)       qs.set('to',        params.to);
  if (params.page)     qs.set('page',      String(params.page));
  if (params.limit)    qs.set('limit',     String(params.limit));

  const res  = await apiFetch(`/api/security/events/${qs.toString() ? `?${qs}` : ''}`);
  const body = await res.json().catch(() => null);

  if (!res.ok) {
    const message = body?.detail ?? body?.message ?? 'Failed to fetch security events.';
    throw { message, status: res.status } as ApiError;
  }

  const raw   = body?.data ?? body;
  const items: any[] = Array.isArray(raw) ? raw : (raw?.results ?? []);

  return {
    results: items.map(mapSecurityEvent),
    total: body?.total ?? raw?.count ?? items.length,
    page:  body?.page  ?? raw?.page  ?? 1,
    pages: body?.pages ?? raw?.pages ?? 1,
  };
}

/* ── Generator detail ── */
export interface GeneratorDetail {
  id: string;
  name: string;
  status: 'running' | 'stopped' | 'maintenance';
  fuelConsumption: number;
  runtime: number;
  efficiency: number;
  lastMaintenance: string;
  siteId?: string;
  siteName?: string;
  model?: string;
  serialNumber?: string;
  manufacturer?: string;
  nextMaintenance?: string;
  totalRuntime?: number;
  fuelType?: string;
  maxCapacity?: number;
  location?: string;
  notes?: string;
  installDate?: string;
}

export interface UpdateGeneratorPayload {
  name?: string;
  status?: 'running' | 'stopped' | 'maintenance';
  fuelConsumption?: string;
  lastMaintenance?: string | null;
}

export async function updateGenerator(generatorId: string, payload: UpdateGeneratorPayload): Promise<GeneratorDetail> {
  const res  = await apiFetch(`/api/v1/gen/generators/${generatorId}/`, {
    method:  'PUT',
    headers: { 'Content-Type': 'application/json' },
    body:    JSON.stringify(payload),
  });
  const body = await res.json().catch(() => null);

  if (res.status === 400) {
    // Alarm-level failures come back keyed under `errors`, e.g.
    // { reorderLevelPct: ["Must be greater than minimumLevelPct (18)…"] }.
    const fieldErrors = body?.errors ?? (body && typeof body === 'object' ? body : null);
    const msgs = fieldErrors && typeof fieldErrors === 'object'
      ? Object.values(fieldErrors).flat().join('; ')
      : null;
    throw {
      message: msgs ?? body?.detail ?? body?.message ?? 'Validation error.',
      status: 400,
      errors: body?.errors,
    } as ApiError & { errors?: Record<string, string[]> };
  }
  if (!res.ok) {
    throw { message: body?.detail ?? body?.message ?? 'Failed to update generator.', status: res.status } as ApiError;
  }

  const g = body?.data ?? body;
  return {
    id:              String(g.id              ?? generatorId),
    name:            String(g.name            ?? g.generator_name   ?? ''),
    status:          g.status                 ?? 'stopped',
    fuelConsumption: Number(g.fuel_consumption ?? g.fuelConsumption ?? 0),
    runtime:         Number(g.runtime          ?? g.run_time         ?? 0),
    efficiency:      Number(g.efficiency       ?? 0),
    lastMaintenance: String(g.last_maintenance ?? g.lastMaintenance  ?? ''),
    siteId:          g.siteId                  ?? g.site_id,
    siteName:        g.siteName                ?? g.site_name,
    model:           g.model                   ?? g.generator_model,
    serialNumber:    g.serialNumber            ?? g.serial_number,
    manufacturer:    g.manufacturer,
    nextMaintenance: g.nextMaintenance         ?? g.next_maintenance,
    totalRuntime:    g.totalRuntime            != null ? Number(g.totalRuntime)  :
                     g.total_runtime           != null ? Number(g.total_runtime) : undefined,
    fuelType:        g.fuelType                ?? g.fuel_type,
    maxCapacity:     g.maxCapacity             != null ? Number(g.maxCapacity)   :
                     g.max_capacity            != null ? Number(g.max_capacity)  : undefined,
    location:        g.location                ?? g.location_name,
    notes:           g.notes,
    installDate:     g.installDate             ?? g.install_date,
  };
}

export async function deleteGenerator(generatorId: string): Promise<void> {
  const res  = await apiFetch(`/api/v1/gen/generators/${generatorId}/`, { method: 'DELETE' });
  if (res.status === 200 || res.status === 204) return;
  const body = await res.json().catch(() => null);
  if (res.status === 404) throw { message: 'Generator not found.', status: 404 } as ApiError;
  throw { message: body?.detail ?? body?.message ?? 'Failed to delete generator.', status: res.status } as ApiError;
}

export interface CreateGeneratorPayload {
  name: string;
  fuelConsumption: string;
  lastMaintenance?: string | null;
}

export async function createSiteGenerator(
  siteId: string,
  payload: CreateGeneratorPayload,
): Promise<import('../utils/mockData').GeneratorData> {
  const res  = await apiFetch(`/api/v1/gen/sites/${siteId}/generators/`, {
    method:  'POST',
    headers: { 'Content-Type': 'application/json' },
    body:    JSON.stringify(payload),
  });
  const body = await res.json().catch(() => null);

  if (res.status === 400) {
    // Alarm-level failures come back keyed under `errors`, e.g.
    // { reorderLevelPct: ["Must be greater than minimumLevelPct (18)…"] }.
    const fieldErrors = body?.errors ?? (body && typeof body === 'object' ? body : null);
    const msgs = fieldErrors && typeof fieldErrors === 'object'
      ? Object.values(fieldErrors).flat().join('; ')
      : null;
    throw {
      message: msgs ?? body?.detail ?? body?.message ?? 'Validation error.',
      status: 400,
      errors: body?.errors,
    } as ApiError & { errors?: Record<string, string[]> };
  }
  if (!res.ok) {
    throw { message: body?.detail ?? body?.message ?? 'Failed to create generator.', status: res.status } as ApiError;
  }

  const raw = body?.data ?? body;
  return mapGenerator(raw);
}

export async function fetchSiteGenerators(siteId: string): Promise<import('../utils/mockData').GeneratorData[]> {
  const res  = await apiFetch(`/api/v1/gen/sites/${siteId}/generators/`);
  const body = await res.json().catch(() => null);

  if (!res.ok) {
    const message = body?.detail ?? body?.message ?? 'Failed to fetch site generators.';
    throw { message, status: res.status } as ApiError;
  }

  const raw   = body?.data ?? body;
  const items: any[] = Array.isArray(raw) ? raw : (raw?.results ?? []);

  return items.map(mapGenerator);
}

export async function fetchGeneratorById(generatorId: string): Promise<GeneratorDetail> {
  const res  = await apiFetch(`/api/v1/gen/generators/${generatorId}/`);
  const body = await res.json().catch(() => null);

  if (res.status === 404) throw { message: 'Generator not found.', status: 404 } as ApiError;
  if (!res.ok) {
    const message = body?.detail ?? body?.message ?? 'Failed to fetch generator.';
    throw { message, status: res.status } as ApiError;
  }

  const g = body?.data ?? body;
  return {
    id:              String(g.id              ?? ''),
    name:            String(g.name            ?? g.generator_name  ?? 'Generator'),
    status:          g.status                 ?? 'stopped',
    fuelConsumption: Number(g.fuel_consumption ?? g.fuelConsumption ?? g.consumption_rate ?? 0),
    runtime:         Number(g.runtime          ?? g.run_time        ?? 0),
    efficiency:      Number(g.efficiency       ?? g.efficiency_score ?? 0),
    lastMaintenance: String(g.last_maintenance ?? g.lastMaintenance  ?? ''),
    siteId:          g.siteId                  ?? g.site_id,
    siteName:        g.siteName                ?? g.site_name,
    model:           g.model                   ?? g.generator_model,
    serialNumber:    g.serialNumber            ?? g.serial_number,
    manufacturer:    g.manufacturer,
    nextMaintenance: g.nextMaintenance         ?? g.next_maintenance,
    totalRuntime:    g.totalRuntime            != null ? Number(g.totalRuntime)  :
                     g.total_runtime           != null ? Number(g.total_runtime) : undefined,
    fuelType:        g.fuelType                ?? g.fuel_type,
    maxCapacity:     g.maxCapacity             != null ? Number(g.maxCapacity)   :
                     g.max_capacity            != null ? Number(g.max_capacity)  : undefined,
    location:        g.location                ?? g.location_name,
    notes:           g.notes,
    installDate:     g.installDate             ?? g.install_date,
  };
}

/* ── Theft detections (volume-loss enriched list) ──
   A theft check compares two consecutive readings, so by construction it
   cannot see a leak — a tank weeping 30L/hr never moves more than a
   fraction of a percent between two readings. That's why this is a
   separate detector from leak, and why confidencePct is a heuristic
   (60–95, never 100) rather than a certainty. */
/* ── Theft incidents (guide §5: /api/security/theft-detections/) ──
   Each row keeps its old flat fields and adds the site and tank blocks, the incident's
   eventId (use it for every action), number versions of the figures, display labels, a
   plain-English confidence explanation, who acknowledged / investigated / resolved it,
   the outcome, and the linked alert. The list is now paged.
   Watch out: acknowledgedAt and resolvedAt are DISPLAY strings — sort by detectedAt. */
export type TheftOutcome = 'confirmed' | 'false_alarm' | 'resolved';

export interface TheftDetection extends SecurityEvent {
  /** Use this, not `id`, for acknowledge / investigate / resolve. */
  eventId:               string;
  statusLabel?:          string;
  volumeLostLitres?:     number;
  volumeLostDisplay?:    string;
  lossPctOfCapacity?:    number;
  confidencePct?:        number;
  confidenceLabel?:      'high' | 'medium' | 'low' | string;
  confidenceExplanation?: string;
  /** ISO — sort on this. */
  detectedAt?:           string;
  detectedAtDisplay?:    string;
  timeAgo?:              string;
  site?:                 SiteBlock;
  tank?:                 TankBlock;
  acknowledgedBy?:       UserBlock;
  acknowledgedAtDisplay?: string;
  acknowledgeNote?:      string;
  investigator?:         UserBlock;
  investigationNote?:    string;
  resolutionOutcome?:    TheftOutcome;
  resolutionLabel?:      string;
  resolutionNote?:       string;
  resolvedBy?:           UserBlock;
  resolvedAtDisplay?:    string;
  alert?:                { id: string; title: string; acknowledged: boolean; resolved: boolean } | null;
}

export function mapTheftDetection(e: any): TheftDetection {
  const base = mapSecurityEvent(e);
  const site = mapSiteBlock(e.site ?? (typeof e.siteId === 'object' ? e.siteId : undefined));
  const tank = mapTankBlock(e.tank ?? (typeof e.tankId === 'object' ? e.tankId : undefined));
  return {
    ...base,
    siteId:    site?.id ?? base.siteId,
    siteName:  site?.name ?? base.siteName,
    tankId:    tank?.id ?? base.tankId,
    tankName:  tank?.name ?? base.tankName,
    timestamp: e.detectedAt ?? base.timestamp,
    eventId:   String(e.eventId ?? e.event_id ?? e.id ?? ''),
    statusLabel:          e.statusLabel ?? undefined,
    volumeLostLitres:     numOrUndef(e.volumeLostLitres ?? e.volume_lost_litres ?? e.volumeLost),
    volumeLostDisplay:    e.volumeLostDisplay ?? undefined,
    lossPctOfCapacity:    numOrUndef(e.lossPctOfCapacity),
    confidencePct:        numOrUndef(e.confidencePct ?? e.confidence_pct ?? e.confidence),
    confidenceLabel:      e.confidenceLabel ?? undefined,
    confidenceExplanation: e.confidenceExplanation ?? undefined,
    detectedAt:           e.detectedAt ?? undefined,
    detectedAtDisplay:    e.detectedAtDisplay ?? undefined,
    timeAgo:              e.timeAgo ?? undefined,
    site, tank,
    acknowledgedBy:        mapUserBlock(e.acknowledgedBy),
    acknowledgedAtDisplay: e.acknowledgedAt ?? e.acknowledgedAtDisplay ?? undefined,
    acknowledgeNote:       e.acknowledgeNote ?? undefined,
    investigator:          mapUserBlock(e.investigator),
    investigationNote:     e.investigationNote ?? undefined,
    resolutionOutcome:     e.resolutionOutcome ?? undefined,
    resolutionLabel:       e.resolutionLabel ?? undefined,
    resolutionNote:        e.resolutionNote ?? undefined,
    resolvedBy:            mapUserBlock(e.resolvedBy),
    resolvedAtDisplay:     e.resolvedAt ?? e.resolvedAtDisplay ?? undefined,
    alert: e.alert ? {
      id: String(e.alert.id ?? ''), title: String(e.alert.title ?? ''),
      acknowledged: Boolean(e.alert.acknowledged), resolved: Boolean(e.alert.resolved),
    } : null,
  };
}

export interface TheftDetectionsParams {
  status?:        SecurityEventStatus;
  resolved?:      boolean;
  acknowledged?:  boolean;
  outcome?:       TheftOutcome;
  minConfidence?: number;
  siteId?:        string;
  tankId?:        string;
  from?:          string;
  to?:            string;
  page?:          number;
  limit?:         number;
}

export interface TheftDetectionsResult {
  results: TheftDetection[];
  total:   number;
  page:    number;
  limit:   number;
  pages:   number;
}

export async function fetchTheftDetections(params: TheftDetectionsParams = {}): Promise<TheftDetectionsResult> {
  // limit: default 20, max 100. minConfidence: 0–100. Booleans go as "true" / "false".
  const query: TheftDetectionsParams = {
    ...params,
    limit: params.limit != null ? Math.min(100, Math.max(1, Math.round(params.limit))) : undefined,
    minConfidence: params.minConfidence != null ? Math.min(100, Math.max(0, params.minConfidence)) : undefined,
  };
  const qs = new URLSearchParams();
  (Object.entries(query) as [string, unknown][]).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== '') qs.set(k, String(v));
  });

  const res  = await apiFetch(`/api/security/theft-detections/${qs.toString() ? `?${qs}` : ''}`);
  const body = await res.json().catch(() => null);

  if (!res.ok) throw toApiError(res.status, body, 'Failed to fetch theft detections.');

  const raw   = body?.data ?? body;
  const items: any[] = Array.isArray(raw) ? raw : (raw?.results ?? []);
  const results = items.map(mapTheftDetection);

  const total = numOf(body?.total ?? raw?.total ?? raw?.count ?? results.length);
  const limit = numOf(body?.limit ?? raw?.limit ?? query.limit ?? 20) || 20;
  return {
    results,
    total,
    page:  numOf(body?.page ?? raw?.page ?? query.page ?? 1) || 1,
    limit,
    // Older responses leave pages out — work it out from total and limit.
    pages: numOf(body?.pages ?? raw?.pages) || Math.max(1, Math.ceil(total / limit)),
  };
}

/* ── Theft summary (guide §4.1): everything the top of the Theft page needs ── */
export interface TheftSummaryParams { days?: number; from?: string; to?: string; siteId?: string; tankId?: string }

export interface TheftRankRow { id: string; name: string; incidents: number; litres: number; litresDisplay?: string; siteName?: string }
export interface TheftDailyPoint { date: string; label?: string; incidents: number; litres: number }

export interface TheftSummary {
  period: { from?: string; to?: string; fromDisplay?: string; toDisplay?: string; days?: number };
  incidents: {
    total: number; open: number; investigating: number; resolved: number; unresolved: number;
    unacknowledged: number; confirmed: number; falseAlarms: number;
    previousPeriod: number; changePct: number | null;
  };
  litres: {
    suspectedLitres: number; suspectedDisplay?: string;
    confirmedLitres: number; confirmedDisplay?: string;
    unresolvedLitres: number; unresolvedDisplay?: string;
    falseAlarmLitres: number; averagePerIncident: number;
    previousPeriodSuspected: number; changePct: number | null;
  };
  alerts: { raised: number; unacknowledged: number; unresolved: number };
  confidence: { averagePct: number | null; highConfidence: number; howItIsCalculated?: string };
  largestIncident?: {
    eventId: string; litres: number; litresDisplay?: string; confidencePct?: number;
    detectedAt?: string; detectedAtDisplay?: string; status?: string; site?: SiteBlock; tank?: TankBlock;
  } | null;
  lastIncidentAt?: string; lastIncidentDisplay?: string; lastIncidentAgo?: string;
  bySite: TheftRankRow[];
  byTank: TheftRankRow[];
  daily:  TheftDailyPoint[];
}

const pctOrNull = (v: any): number | null => (v == null || v === '' || Number.isNaN(Number(v)) ? null : Number(v));

export async function fetchTheftSummary(params: TheftSummaryParams = {}): Promise<TheftSummary> {
  const qs = new URLSearchParams();
  (Object.entries(params) as [string, unknown][]).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== '') qs.set(k, String(v));
  });
  const res  = await apiFetch(`/api/security/theft-summary/${qs.toString() ? `?${qs}` : ''}`);
  const body = await res.json().catch(() => null);
  if (!res.ok) throw toApiError(res.status, body, 'Failed to load the theft summary.');

  const d = body?.data ?? body ?? {};
  const inc = d.incidents ?? {}, lit = d.litres ?? {}, al = d.alerts ?? {}, conf = d.confidence ?? {};
  const rank = (rows: any): TheftRankRow[] => (Array.isArray(rows) ? rows : []).map((r: any) => ({
    id: String(r.id ?? ''), name: String(r.name ?? ''), incidents: numOf(r.incidents),
    litres: numOf(r.litres), litresDisplay: r.litresDisplay ?? undefined,
    siteName: r.site?.name ?? r.siteName ?? undefined,   // byTank rows may name their site
  }));
  const li = d.largestIncident;
  return {
    period: {
      from: d.period?.from, to: d.period?.to, fromDisplay: d.period?.fromDisplay, toDisplay: d.period?.toDisplay,
      days: numOrUndef(d.period?.days),
    },
    incidents: {
      total: numOf(inc.total), open: numOf(inc.open), investigating: numOf(inc.investigating),
      resolved: numOf(inc.resolved), unresolved: numOf(inc.unresolved), unacknowledged: numOf(inc.unacknowledged),
      confirmed: numOf(inc.confirmed), falseAlarms: numOf(inc.falseAlarms),
      previousPeriod: numOf(inc.previousPeriod), changePct: pctOrNull(inc.changePct),
    },
    litres: {
      suspectedLitres: numOf(lit.suspectedLitres), suspectedDisplay: lit.suspectedDisplay,
      confirmedLitres: numOf(lit.confirmedLitres), confirmedDisplay: lit.confirmedDisplay,
      unresolvedLitres: numOf(lit.unresolvedLitres), unresolvedDisplay: lit.unresolvedDisplay,
      falseAlarmLitres: numOf(lit.falseAlarmLitres), averagePerIncident: numOf(lit.averagePerIncident),
      previousPeriodSuspected: numOf(lit.previousPeriodSuspected), changePct: pctOrNull(lit.changePct),
    },
    alerts: { raised: numOf(al.raised), unacknowledged: numOf(al.unacknowledged), unresolved: numOf(al.unresolved) },
    confidence: { averagePct: pctOrNull(conf.averagePct), highConfidence: numOf(conf.highConfidence), howItIsCalculated: conf.howItIsCalculated },
    largestIncident: li ? {
      eventId: String(li.eventId ?? ''), litres: numOf(li.litres), litresDisplay: li.litresDisplay,
      confidencePct: numOrUndef(li.confidencePct), detectedAt: li.detectedAt, detectedAtDisplay: li.detectedAtDisplay,
      status: li.status, site: mapSiteBlock(li.site), tank: mapTankBlock(li.tank),
    } : null,
    lastIncidentAt: d.lastIncidentAt ?? undefined,
    lastIncidentDisplay: d.lastIncidentDisplay ?? undefined,
    lastIncidentAgo: d.lastIncidentAgo ?? undefined,
    bySite: rank(d.bySite),
    byTank: rank(d.byTank),
    daily: (Array.isArray(d.daily) ? d.daily : []).map((p: any) => ({
      date: String(p.date ?? p.day ?? ''), label: p.label ?? p.dateDisplay ?? undefined,
      incidents: numOf(p.incidents), litres: numOf(p.litres),
    })),
  };
}

/* ── Acknowledge a security incident (guide §4.2) — "I've seen this."
   manage_alerts (Operator and up). Also acknowledges the incident's theft alert.
   400 if already acknowledged or resolved. Returns the full incident row and the server's message.
   Always the incident's eventId — never its row id. */
export interface AcknowledgeEventResult {
  incident: TheftDetection | null;
  /** e.g. "Security event acknowledged." */
  message:  string;
}

export async function acknowledgeSecurityEvent(eventId: string, note?: string): Promise<AcknowledgeEventResult> {
  if (!eventId) throw { message: 'This incident has no event id to acknowledge.', status: 0 } as ApiError;
  const res = await apiFetch(`/api/security/events/${encodeURIComponent(eventId)}/acknowledge/`, {
    method: 'POST',
    // The body object is required; `note` defaults to empty.
    body:   JSON.stringify({ note: note?.trim() ?? '' }),
  });
  const body = await res.json().catch(() => null);
  if (res.status === 404) throw { message: "This incident isn't available to your account.", status: 404 } as ApiError;
  if (res.status === 403) throw toApiError(403, body, 'Your role cannot acknowledge security incidents (needs manage_alerts).');
  if (!res.ok) throw toApiError(res.status, body, res.status === 400 ? 'Already acknowledged or resolved.' : 'Failed to acknowledge the incident.');
  const row = body?.data ?? null;
  return {
    incident: row && typeof row === 'object' ? mapTheftDetection(row) : null,
    message:  body?.message ?? 'Incident acknowledged.',
  };
}

/* ── Leak detections (leak-rate enriched list) ──
   Often legitimately empty — pair with fetchLeakDiagnostics so an empty
   list has an answer ("why") rather than reading as a broken page. */
export interface LeakDetection extends SecurityEvent {
  leakRateLitresPerHour?: number;
}

export async function fetchLeakDetections(
  params: { siteId?: string; from?: string; to?: string } = {},
): Promise<LeakDetection[]> {
  const qs = new URLSearchParams();
  if (params.siteId) qs.set('siteId', params.siteId);
  if (params.from)   qs.set('from',   params.from);
  if (params.to)     qs.set('to',     params.to);

  const res  = await apiFetch(`/api/security/leak-detections/${qs.toString() ? `?${qs}` : ''}`);
  const body = await res.json().catch(() => null);

  if (!res.ok) {
    const message = body?.detail ?? body?.message ?? 'Failed to fetch leak detections.';
    throw { message, status: res.status } as ApiError;
  }

  const raw   = body?.data ?? body;
  const items: any[] = Array.isArray(raw) ? raw : (raw?.results ?? []);

  return items.map((e: any) => ({
    ...mapSecurityEvent(e),
    leakRateLitresPerHour: numOrUndef(e.leakRateLitresPerHour ?? e.leak_rate_litres_per_hour ?? e.leakRate ?? e.leak_rate),
  }));
}

/* ── Leak diagnostics — why each tank is or isn't flagged as leaking.
   The check declines to speak (isLeak stays false) in three cases: fewer
   than six readings in the window, a refill inside the window, or a single
   drop accounting for more than half the loss. A tank in one of those
   states is a tank the check cannot assess, not a tank that's fine. ── */
export interface LeakDiagnostic {
  tankId?:               string;
  tankName:              string;
  windowHours:           number;
  measuredLitres:        number;
  bookedLitres:          number;
  unexplainedLitres:     number;
  rateLitresPerHour:     number;
  thresholdLitresPerHour: number;
  readings:              number;
  isLeak:                boolean;
  /** Written to be shown to a user verbatim. */
  reason:                string;
}

function mapLeakDiagnostic(d: any): LeakDiagnostic {
  return {
    tankId:                d.tankId ?? d.tank_id ?? undefined,
    tankName:              String(d.tankName ?? d.tank_name ?? ''),
    windowHours:           numOf(d.windowHours ?? d.window_hours),
    measuredLitres:        numOf(d.measuredLitres ?? d.measured_litres),
    bookedLitres:          numOf(d.bookedLitres ?? d.booked_litres),
    unexplainedLitres:     numOf(d.unexplainedLitres ?? d.unexplained_litres),
    rateLitresPerHour:     numOf(d.rateLitresPerHour ?? d.rate_litres_per_hour),
    thresholdLitresPerHour: numOf(d.thresholdLitresPerHour ?? d.threshold_litres_per_hour),
    readings:              numOf(d.readings),
    isLeak:                Boolean(d.isLeak ?? d.is_leak),
    reason:                String(d.reason ?? ''),
  };
}

export async function fetchLeakDiagnostics(
  params: { tankId?: string; siteId?: string } = {},
): Promise<LeakDiagnostic[]> {
  const qs = new URLSearchParams();
  if (params.tankId) qs.set('tankId', params.tankId);
  if (params.siteId) qs.set('siteId', params.siteId);

  const res  = await apiFetch(`/api/security/leak-diagnostics/${qs.toString() ? `?${qs}` : ''}`);
  const body = await res.json().catch(() => null);

  if (!res.ok) {
    const message = body?.detail ?? body?.message ?? 'Failed to fetch leak diagnostics.';
    throw { message, status: res.status } as ApiError;
  }

  const raw = body?.data ?? body;
  // A single tankId may come back as one object rather than a one-item array.
  const items: any[] = Array.isArray(raw) ? raw : (raw?.results ?? (raw && typeof raw === 'object' ? [raw] : []));

  return items.map(mapLeakDiagnostic);
}

/* ── Start an investigation (Owner and Admin — manage_security) ──
   POST /api/security/events/{eventId}/investigate/  { investigatorId, note }
   investigatorId is REQUIRED and must be an integer account id. Left out, the API answers
   400 "This field is required." — so "assign to me" sends the signed-in user's own id.
   The note is added to the incident's investigationNote. Always the eventId. */
export interface InvestigateEventParams {
  /** The integer account id of the investigator. Defaults to the signed-in user. */
  investigatorId?: number;
  note?: string;
}

export interface InvestigateEventResult {
  incident: TheftDetection | null;
  message:  string;
}

export async function investigateSecurityEvent(eventId: string, params: InvestigateEventParams = {}): Promise<InvestigateEventResult> {
  if (!eventId) throw { message: 'This incident has no event id to investigate.', status: 0 } as ApiError;
  const investigatorId = params.investigatorId != null && Number.isInteger(params.investigatorId) && params.investigatorId > 0
    ? params.investigatorId
    : currentUserId();
  if (!investigatorId) {
    throw { message: "Couldn't work out your account id to assign the investigation. Sign out and back in, then try again.", status: 0 } as ApiError;
  }
  const payload = { investigatorId, note: params.note?.trim() ?? '' };
  const res = await apiFetch(`/api/security/events/${encodeURIComponent(eventId)}/investigate/`, {
    method:  'POST',
    headers: { 'Content-Type': 'application/json' },
    body:    JSON.stringify(payload),
  });

  const body = await res.json().catch(() => null);
  if (res.ok) {
    const row = body?.data;
    return {
      incident: row && typeof row === 'object' && (row.eventId || row.status) ? mapTheftDetection(row) : null,
      message:  body?.message ?? 'Marked as under investigation.',
    };
  }
  if (res.status === 400) throw toApiError(400, body, 'Already investigating or resolved.');
  if (res.status === 403) throw toApiError(403, body, 'Only an Owner or Admin can investigate incidents (needs manage_security).');
  if (res.status === 404) throw { message: "This incident isn't available to your account.", status: 404 } as ApiError;
  throw toApiError(res.status, body, 'Failed to flag event for investigation.');
}

/* ── Close out a security event. A leak event is suppressed while an open
   one stands on the tank, so this is what lets the next one be raised. ── */
/* Body: { resolution: confirmed | false_alarm | resolved, note }. A false_alarm stops its
   litres counting as stolen in the theft summary. The response is now the FULL incident
   row (guide §3.3): resolvedBy is an object, resolvedAt a display string. Resolving also
   acknowledges the incident and resolves its linked theft alert. manage_security. */
export async function resolveSecurityEvent(
  eventId: string, resolution: TheftOutcome = 'resolved', note?: string,
): Promise<TheftDetection | null> {
  const res = await apiFetch(`/api/security/events/${eventId}/resolve/`, {
    method:  'POST',
    headers: { 'Content-Type': 'application/json' },
    body:    JSON.stringify({ resolution, ...(note ? { note } : {}) }),
  });

  const body = await res.json().catch(() => null);
  if (res.ok) {
    const row = body?.data;
    return row && typeof row === 'object' && (row.eventId || row.status) ? mapTheftDetection(row) : null;
  }
  if (res.status === 400) throw toApiError(400, body, 'Already resolved.');
  if (res.status === 404) throw { message: "This incident isn't available to your account.", status: 404 } as ApiError;
  throw toApiError(res.status, body, 'Failed to resolve event.');
}

/* ════════════════════════════════════════════════════════════════════════
   Security overview, movement feed, watch windows — /api/v1/tank/ and
   /api/security/. See "Security & Movement — Frontend Guide" for the full
   contract this section implements.
   ════════════════════════════════════════════════════════════════════════ */

export interface MovementSummary {
  totalMovements: number;
  outMovements:   number;
  inMovements:    number;
  outLitres:      number;
  inLitres:       number;
  netLitres:      number;
  inWatchWindow:  number;
  alertsRaised:   number;
}

export interface WatchWindowsSummary {
  total:     number;
  active:    number;
  activeNow: number;
}

export interface MovementAlertsSummary {
  total:          number;
  unacknowledged: number;
  unresolved:     number;
}

export interface SecurityCategorySummary {
  total:         number;
  open:          number;
  investigating: number;
  resolved:      number;
  /** Present on theft. */
  litresLost?:   number;
  recent:        SecurityEvent[];
}

/** Per-tank state row inside the overview — lets the page say which tanks
 *  are actually monitored rather than implying all of them are. */
export interface OverviewTankState {
  tankId:                  string;
  tankName?:               string;
  siteId?:                 string;
  siteName?:               string;
  /** false means movement tracking is not armed on this tank — show it, don't hide it. */
  tracking:                boolean;
  movementThresholdLitres?: number;
  watchWindowActive:       boolean;
  /** The threshold in force right now — lower than the baseline when a window is live. */
  activeThresholdLitres?:  number;
  activeWindowLabel?:      string;
}

export interface SecurityOverview {
  asOf:   string;
  window: { from: string; to: string; hours: number };
  scope:  { sites: number; tanks: number };
  movement: {
    summary:      MovementSummary;
    trackedTanks: number;
    watchWindows: WatchWindowsSummary;
    recent:       TankMovement[];
  };
  movementAlerts: MovementAlertsSummary;
  theft:  SecurityCategorySummary;
  leak:   SecurityCategorySummary;
  tanks:  OverviewTankState[];
}

function mapCategorySummary(c: any): SecurityCategorySummary {
  return {
    total:         numOf(c?.total),
    open:          numOf(c?.open),
    investigating: numOf(c?.investigating),
    resolved:      numOf(c?.resolved),
    litresLost:    numOrUndef(c?.litresLost ?? c?.litres_lost),
    recent:        (Array.isArray(c?.recent) ? c.recent : []).map(mapSecurityEvent),
  };
}

export interface SecurityOverviewParams {
  hours?:  number; // default 24, capped at 90 days
  from?:   string;
  to?:     string;
  siteId?: string;
}

export async function fetchSecurityOverview(params: SecurityOverviewParams = {}): Promise<SecurityOverview> {
  const qs = new URLSearchParams();
  if (params.hours  != null) qs.set('hours',  String(params.hours));
  if (params.from   != null) qs.set('from',   params.from);
  if (params.to     != null) qs.set('to',     params.to);
  if (params.siteId != null) qs.set('siteId', params.siteId);

  const res  = await apiFetch(`/api/security/overview/${qs.toString() ? `?${qs}` : ''}`);
  const body = await res.json().catch(() => null);

  if (!res.ok) {
    const message = body?.detail ?? body?.message ?? 'Failed to fetch security overview.';
    throw { message, status: res.status } as ApiError;
  }

  const raw = body?.data ?? body;
  const movementRaw = raw?.movement ?? {};
  const summaryRaw  = movementRaw?.summary ?? {};

  return {
    asOf:   String(raw?.asOf ?? raw?.as_of ?? ''),
    window: {
      from:  String(raw?.window?.from ?? ''),
      to:    String(raw?.window?.to   ?? ''),
      hours: numOf(raw?.window?.hours),
    },
    scope: {
      sites: numOf(raw?.scope?.sites),
      tanks: numOf(raw?.scope?.tanks),
    },
    movement: {
      summary: {
        totalMovements: numOf(summaryRaw.totalMovements),
        outMovements:   numOf(summaryRaw.outMovements),
        inMovements:    numOf(summaryRaw.inMovements),
        outLitres:      numOf(summaryRaw.outLitres),
        inLitres:       numOf(summaryRaw.inLitres),
        netLitres:      numOf(summaryRaw.netLitres),
        inWatchWindow:  numOf(summaryRaw.inWatchWindow),
        alertsRaised:   numOf(summaryRaw.alertsRaised),
      },
      trackedTanks: numOf(movementRaw.trackedTanks),
      watchWindows: {
        total:     numOf(movementRaw.watchWindows?.total),
        active:    numOf(movementRaw.watchWindows?.active),
        activeNow: numOf(movementRaw.watchWindows?.activeNow),
      },
      recent: (Array.isArray(movementRaw.recent) ? movementRaw.recent : []).map(mapTankMovement),
    },
    movementAlerts: {
      total:          numOf(raw?.movementAlerts?.total),
      unacknowledged: numOf(raw?.movementAlerts?.unacknowledged),
      unresolved:     numOf(raw?.movementAlerts?.unresolved),
    },
    theft: mapCategorySummary(raw?.theft),
    leak:  mapCategorySummary(raw?.leak),
    tanks: (Array.isArray(raw?.tanks) ? raw.tanks : []).map((t: any): OverviewTankState => ({
      tankId:                  String(t.tankId ?? t.tank_id ?? ''),
      tankName:                t.tankName ?? t.tank_name ?? undefined,
      siteId:                  t.siteId   ?? t.site_id   ?? undefined,
      siteName:                t.siteName ?? t.site_name ?? undefined,
      tracking:                Boolean(t.tracking),
      movementThresholdLitres: numOrUndef(t.movementThresholdLitres ?? t.movement_threshold_litres),
      watchWindowActive:       Boolean(t.watchWindowActive ?? t.watch_window_active),
      activeThresholdLitres:   numOrUndef(t.activeThresholdLitres ?? t.active_threshold_litres),
      activeWindowLabel:       t.activeWindowLabel ?? t.active_window_label ?? undefined,
    })),
  };
}

/* ── Movement feed — one row per recorded change in a tank's contents.
   Each row carries the level and volume on both sides, so the table never
   has to join back to the raw readings to answer "out of what". ── */
export type MovementDirection = 'out' | 'in';
export type MovementEventType = 'refill' | 'consumption' | 'leak';

export interface TankMovement {
  id:                  string;
  tankId:              string;
  tankName?:           string;
  siteId?:             string;
  siteName?:           string;
  fuelType?:           string;
  direction:           MovementDirection;
  litres:              number;
  eventType:           MovementEventType;
  levelBeforePct?:     number;
  levelAfterPct?:      number;
  volumeBeforeLitres?: number;
  volumeAfterLitres?:  number;
  /** Caught inside a watch window — a tighter threshold than the tank's usual one. */
  inWatchWindow:       boolean;
  windowId?:           string;
  windowLabel?:        string;
  /** The threshold in force when this row was recorded. */
  thresholdLitres?:    number;
  notified:            boolean;
  /** An alert was raised for this exact movement — link to it. */
  alertId?:            string;
  /** Ties this row to an exact point on the volume chart — match on this, not timestamps. */
  readingId?:          string;
  recordedAt:          string;
}

function mapTankMovement(m: any): TankMovement {
  return {
    id:                  String(m.id ?? ''),
    tankId:              String(m.tankId ?? m.tank_id ?? ''),
    tankName:            m.tankName ?? m.tank_name ?? undefined,
    siteId:              m.siteId   ?? m.site_id   ?? undefined,
    siteName:            m.siteName ?? m.site_name ?? undefined,
    fuelType:            m.fuelType ?? m.fuel_type  ?? undefined,
    direction:           (m.direction ?? 'out') as MovementDirection,
    litres:              numOf(m.litres),
    eventType:           (m.eventType ?? m.event_type ?? 'consumption') as MovementEventType,
    levelBeforePct:      numOrUndef(m.levelBeforePct ?? m.level_before_pct),
    levelAfterPct:       numOrUndef(m.levelAfterPct  ?? m.level_after_pct),
    volumeBeforeLitres:  numOrUndef(m.volumeBeforeLitres ?? m.volume_before_litres),
    volumeAfterLitres:   numOrUndef(m.volumeAfterLitres  ?? m.volume_after_litres),
    inWatchWindow:       Boolean(m.inWatchWindow ?? m.in_watch_window),
    windowId:            m.windowId    ?? m.window_id    ?? undefined,
    windowLabel:         m.windowLabel ?? m.window_label ?? undefined,
    thresholdLitres:     numOrUndef(m.thresholdLitres ?? m.threshold_litres),
    notified:            Boolean(m.notified),
    alertId:             m.alertId  ?? m.alert_id  ?? undefined,
    readingId:           m.readingId ?? m.reading_id ?? undefined,
    recordedAt:          String(m.recordedAt ?? m.recorded_at ?? ''),
  };
}

export interface MovementsParams {
  direction?:   MovementDirection;
  eventType?:   MovementEventType;
  minLitres?:   number;
  watchOnly?:   boolean;
  alertedOnly?: boolean;
  windowId?:    string;
  /** Scope-wide feed only (the per-tank endpoint is already scoped). */
  siteId?:      string;
  tankId?:      string;
  from?:        string;
  to?:          string;
  since?:       string;
  page?:        number;
  limit?:       number; // default 100, cap 500
}

export interface MovementsResult {
  data:    TankMovement[];
  summary: MovementSummary;
  total:   number;
  page:    number;
  limit:   number;
  pages:   number;
  asOf:    string;
}

function movementsQuery(params: MovementsParams): URLSearchParams {
  const qs = new URLSearchParams();
  if (params.direction   != null) qs.set('direction',   params.direction);
  if (params.eventType   != null) qs.set('eventType',   params.eventType);
  if (params.minLitres   != null) qs.set('minLitres',   String(params.minLitres));
  if (params.watchOnly)            qs.set('watchOnly',   'true');
  if (params.alertedOnly)          qs.set('alertedOnly', 'true');
  if (params.windowId    != null) qs.set('windowId',    params.windowId);
  if (params.from        != null) qs.set('from',        params.from);
  if (params.to          != null) qs.set('to',          params.to);
  if (params.since       != null) qs.set('since',       params.since);
  if (params.page        != null) qs.set('page',        String(params.page));
  const limit = Math.min(params.limit ?? 100, 500);
  qs.set('limit', String(limit));
  return qs;
}

function mapMovementsResult(body: any): MovementsResult {
  const raw = body?.data ?? body;
  const items: any[] = Array.isArray(raw) ? raw : (Array.isArray(raw?.data) ? raw.data : []);
  const summaryRaw = body?.summary ?? raw?.summary ?? {};
  return {
    data: items.map(mapTankMovement),
    summary: {
      totalMovements: numOf(summaryRaw.totalMovements),
      outMovements:   numOf(summaryRaw.outMovements),
      inMovements:    numOf(summaryRaw.inMovements),
      outLitres:      numOf(summaryRaw.outLitres),
      inLitres:       numOf(summaryRaw.inLitres),
      netLitres:      numOf(summaryRaw.netLitres),
      inWatchWindow:  numOf(summaryRaw.inWatchWindow),
      alertsRaised:   numOf(summaryRaw.alertsRaised),
    },
    total: numOf(body?.total ?? raw?.total ?? items.length),
    page:  numOf(body?.page  ?? raw?.page  ?? 1),
    limit: numOf(body?.limit ?? raw?.limit ?? items.length),
    pages: numOf(body?.pages ?? raw?.pages ?? 1),
    asOf:  String(body?.asOf ?? raw?.asOf ?? ''),
  };
}

/** Scope-wide movement feed across every site in scope. */
export async function fetchTankMovements(params: MovementsParams = {}): Promise<MovementsResult> {
  const qs = movementsQuery(params);
  if (params.siteId != null) qs.set('siteId', params.siteId);
  if (params.tankId != null) qs.set('tankId', params.tankId);

  const res  = await apiFetch(`/api/v1/tank/movements/${qs.toString() ? `?${qs}` : ''}`);
  const body = await res.json().catch(() => null);

  if (!res.ok) {
    const message = body?.detail ?? body?.message ?? 'Failed to fetch movements.';
    throw { message, status: res.status } as ApiError;
  }
  return mapMovementsResult(body);
}

/** One tank's movements — same shape and filters as the scope-wide feed. */
export async function fetchTankMovementsForTank(
  tankId: string,
  params: Omit<MovementsParams, 'siteId' | 'tankId'> = {},
): Promise<MovementsResult> {
  const qs = movementsQuery(params);

  const res  = await apiFetch(`/api/v1/tank/tanks/${tankId}/movements/${qs.toString() ? `?${qs}` : ''}`);
  const body = await res.json().catch(() => null);

  if (res.status === 404) throw { message: 'Tank not found.', status: 404 } as ApiError;
  if (!res.ok) {
    const message = body?.detail ?? body?.message ?? 'Failed to fetch tank movements.';
    throw { message, status: res.status } as ApiError;
  }
  return mapMovementsResult(body);
}

/* ── Movement thresholds and sensor resolution ──
   The single most important thing for the UI to get right: a level sensor
   reports a raw count between calibrated empty and full points. Spread the
   tank's capacity over that span and one count is capacity ÷ tankRange
   litres — on a 40,000L tank with a sender scaled 200–1000, one count is
   50L, so a 40L draw may not move the reading at all. ── */
export interface MovementSettingsResolution {
  capacityLitres:        number;
  sensorSerial?:         string;
  tankRange?:            number;
  litresPerCount?:       number;
  storageStepLitres?:    number;
  floorLitres?:          number;
  recommendedMinLitres?: number;
  /** Written to be shown to a user verbatim. */
  note?:                 string;
}

export interface MovementSettings {
  movementThresholdLitres: number;
  movementAlertsEnabled:   boolean;
  windows:                 WatchWindow[];
  resolution:               MovementSettingsResolution | null;
}

function mapResolution(r: any): MovementSettingsResolution | null {
  if (!r) return null;
  return {
    capacityLitres:        numOf(r.capacityLitres ?? r.capacity_litres),
    sensorSerial:          r.sensorSerial ?? r.sensor_serial ?? undefined,
    tankRange:             numOrUndef(r.tankRange ?? r.tank_range),
    litresPerCount:        numOrUndef(r.litresPerCount ?? r.litres_per_count),
    storageStepLitres:     numOrUndef(r.storageStepLitres ?? r.storage_step_litres),
    floorLitres:           numOrUndef(r.floorLitres ?? r.floor_litres),
    recommendedMinLitres:  numOrUndef(r.recommendedMinLitres ?? r.recommended_min_litres),
    note:                  r.note ?? undefined,
  };
}

export async function fetchMovementSettings(tankId: string): Promise<MovementSettings> {
  const res  = await apiFetch(`/api/v1/tank/tanks/${tankId}/movement-settings/`);
  const body = await res.json().catch(() => null);

  if (res.status === 404) throw { message: 'Tank not found.', status: 404 } as ApiError;
  if (!res.ok) {
    const message = body?.detail ?? body?.message ?? 'Failed to fetch movement settings.';
    throw { message, status: res.status } as ApiError;
  }

  const raw = body?.data ?? body;
  return {
    movementThresholdLitres: numOf(raw?.movementThresholdLitres ?? raw?.movement_threshold_litres),
    movementAlertsEnabled:   Boolean(raw?.movementAlertsEnabled ?? raw?.movement_alerts_enabled ?? true),
    windows:                 (Array.isArray(raw?.windows) ? raw.windows : []).map(mapWatchWindow),
    resolution:               mapResolution(raw?.resolution),
  };
}

export interface UpdateMovementSettingsPayload {
  movementThresholdLitres?: number;
  movementAlertsEnabled?:   boolean;
}

/** Returns a non-empty `warning` when the new threshold is below the
 *  sensor's recommendedMinLitres — render it, don't block the save. */
export async function updateMovementSettings(
  tankId: string,
  payload: UpdateMovementSettingsPayload,
): Promise<{ settings: MovementSettings; warning?: string }> {
  const body_payload: Record<string, any> = {};
  if (payload.movementThresholdLitres != null) body_payload.movementThresholdLitres = payload.movementThresholdLitres;
  if (payload.movementAlertsEnabled   != null) body_payload.movementAlertsEnabled   = payload.movementAlertsEnabled;

  const res  = await apiFetch(`/api/v1/tank/tanks/${tankId}/movement-settings/`, {
    method:  'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body:    JSON.stringify(body_payload),
  });
  const body = await res.json().catch(() => null);

  if (res.status === 404) throw { message: 'Tank not found.', status: 404 } as ApiError;
  if (res.status === 400) throwFieldErrors(body, 'Invalid data.', 400);
  if (!res.ok) throw { message: body?.detail ?? body?.message ?? 'Failed to update movement settings.', status: res.status } as ApiError;

  const raw = body?.data ?? body;
  return {
    settings: {
      movementThresholdLitres: numOf(raw?.movementThresholdLitres ?? raw?.movement_threshold_litres),
      movementAlertsEnabled:   Boolean(raw?.movementAlertsEnabled ?? raw?.movement_alerts_enabled ?? true),
      windows:                 (Array.isArray(raw?.windows) ? raw.windows : []).map(mapWatchWindow),
      resolution:               mapResolution(raw?.resolution),
    },
    warning: body?.warning ?? undefined,
  };
}

/* ── Watch windows — override the baseline threshold for chosen times of
   day, normally with a much smaller number, and decide whether a
   qualifying movement inside them also raises its own alert.
   The most sensitive window covering the moment wins: the smallest
   threshold covering a given time can never be cancelled by a looser one
   already there. A midnight-crossing window belongs to the day it started
   on — a Friday-only 22:00–05:00 window covers Saturday's small hours, not
   Friday's. ── */
export type MovementDirectionFilter = 'out' | 'in' | 'both';

export interface WatchWindow {
  id:                  string;
  tankId?:             string;
  label:               string;
  startTime:           string; // "22:00"
  endTime:             string; // "05:00"
  /** Monday is 0, Sunday is 6. Empty means every day. */
  daysOfWeek:           number[];
  timezone:            string; // IANA, default Africa/Lagos
  minMovementLitres?:  number;
  direction:           MovementDirectionFilter;
  /** Raises an alert for every qualifying movement — bypasses the six-hour suppression. */
  notify:              boolean;
  /** Also emails each one. Default off. */
  email:               boolean;
  /** Server-computed — the UI never has to reimplement timezone/midnight-wrap rules. */
  activeNow:           boolean;
  wrapsMidnight:       boolean;
  allDay:              boolean;
}

function mapWatchWindow(w: any): WatchWindow {
  return {
    id:                 String(w.id ?? ''),
    tankId:             w.tankId ?? w.tank_id ?? undefined,
    label:              String(w.label ?? ''),
    startTime:          String(w.startTime ?? w.start_time ?? ''),
    endTime:            String(w.endTime   ?? w.end_time   ?? ''),
    daysOfWeek:         Array.isArray(w.daysOfWeek ?? w.days_of_week) ? (w.daysOfWeek ?? w.days_of_week).map(Number) : [],
    timezone:           String(w.timezone ?? 'Africa/Lagos'),
    minMovementLitres:  numOrUndef(w.minMovementLitres ?? w.min_movement_litres),
    direction:          (w.direction ?? 'out') as MovementDirectionFilter,
    notify:             Boolean(w.notify),
    email:              Boolean(w.email),
    activeNow:          Boolean(w.activeNow ?? w.active_now),
    wrapsMidnight:      Boolean(w.wrapsMidnight ?? w.wraps_midnight),
    allDay:             Boolean(w.allDay ?? w.all_day),
  };
}

export interface WatchWindowPayload {
  label?:              string;
  startTime?:          string;
  endTime?:            string;
  daysOfWeek?:         number[];
  timezone?:           string;
  minMovementLitres?:  number;
  direction?:          MovementDirectionFilter;
  notify?:             boolean;
  email?:              boolean;
}

export async function fetchWatchWindows(tankId: string): Promise<WatchWindow[]> {
  const res  = await apiFetch(`/api/v1/tank/tanks/${tankId}/watch-windows/`);
  const body = await res.json().catch(() => null);

  if (res.status === 404) throw { message: 'Tank not found.', status: 404 } as ApiError;
  if (!res.ok) {
    const message = body?.detail ?? body?.message ?? 'Failed to fetch watch windows.';
    throw { message, status: res.status } as ApiError;
  }

  const raw   = body?.data ?? body;
  const items: any[] = Array.isArray(raw) ? raw : (raw?.results ?? []);
  return items.map(mapWatchWindow);
}

/** Returns a non-empty `warning` when minMovementLitres is below the
 *  tank's sensor resolution (see fetchMovementSettings).resolution). */
export async function createWatchWindow(
  tankId: string,
  payload: WatchWindowPayload,
): Promise<{ window: WatchWindow; warning?: string }> {
  const res  = await apiFetch(`/api/v1/tank/tanks/${tankId}/watch-windows/`, {
    method:  'POST',
    headers: { 'Content-Type': 'application/json' },
    body:    JSON.stringify(payload),
  });
  const body = await res.json().catch(() => null);

  if (res.status === 404) throw { message: 'Tank not found.', status: 404 } as ApiError;
  if (res.status === 400) throwFieldErrors(body, 'Invalid window.', 400);
  if (!res.ok) throw toApiError(res.status, body, 'Failed to create watch window.');

  return { window: mapWatchWindow(body?.data ?? body), warning: body?.warning ?? undefined };
}

export async function fetchWatchWindow(windowId: string): Promise<WatchWindow> {
  const res  = await apiFetch(`/api/v1/tank/watch-windows/${windowId}/`);
  const body = await res.json().catch(() => null);

  if (res.status === 404) throw { message: 'Window not found.', status: 404 } as ApiError;
  if (!res.ok) throw { message: body?.detail ?? body?.message ?? 'Failed to fetch watch window.', status: res.status } as ApiError;

  return mapWatchWindow(body?.data ?? body);
}

export async function updateWatchWindow(
  windowId: string,
  payload: Partial<WatchWindowPayload>,
): Promise<{ window: WatchWindow; warning?: string }> {
  const res  = await apiFetch(`/api/v1/tank/watch-windows/${windowId}/`, {
    method:  'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body:    JSON.stringify(payload),
  });
  const body = await res.json().catch(() => null);

  if (res.status === 404) throw { message: 'Window not found.', status: 404 } as ApiError;
  if (res.status === 400) throwFieldErrors(body, 'Invalid window.', 400);
  if (!res.ok) throw toApiError(res.status, body, 'Failed to update watch window.');

  return { window: mapWatchWindow(body?.data ?? body), warning: body?.warning ?? undefined };
}

export async function deleteWatchWindow(windowId: string): Promise<void> {
  const res = await apiFetch(`/api/v1/tank/watch-windows/${windowId}/`, { method: 'DELETE' });
  if (res.status === 200 || res.status === 204) return;

  const body = await res.json().catch(() => null);
  if (res.status === 404) throw { message: 'Window not found.', status: 404 } as ApiError;
  throw { message: body?.detail ?? body?.message ?? 'Failed to delete watch window.', status: res.status } as ApiError;
}

/* ── Report summary ── */
export interface SiteSummaryItem {
  siteId: string;
  siteName: string;
  tanks: number;
  avgFuelLevel?: number;
  openAlerts: number;
  criticalAlerts: number;
  consumption?: number;
  efficiency?: number;
}

export interface SummaryReport {
  totalEvents: number;
  openIssues: number;
  criticalUnacked: number;
  resolved: number;
  typeBreakdown?: Record<string, number>;
  sites?: SiteSummaryItem[];
}

export async function fetchReportSummary(
  params: { siteId?: string; from?: string; to?: string } = {},
): Promise<SummaryReport> {
  const qs = new URLSearchParams();
  if (params.siteId) qs.set('siteId', params.siteId);
  if (params.from)   qs.set('from',   params.from);
  if (params.to)     qs.set('to',     params.to);

  const res  = await apiFetch(`/api/reports/summary/${qs.toString() ? `?${qs}` : ''}`);
  const body = await res.json().catch(() => null);

  if (!res.ok) {
    const message = body?.detail ?? body?.message ?? 'Failed to fetch summary.';
    throw { message, status: res.status } as ApiError;
  }

  const d   = body?.data ?? body ?? {};
  const raw = d.sites ?? d.site_summaries ?? d.site_summary ?? [];

  const sites: SiteSummaryItem[] = (Array.isArray(raw) ? raw : []).map((s: any) => ({
    siteId:         s.siteId         ?? s.site_id         ?? s.id   ?? '',
    siteName:       s.siteName       ?? s.site_name       ?? s.name ?? '',
    tanks:          s.tanks          ?? s.tank_count      ?? 0,
    avgFuelLevel:   s.avgFuelLevel   ?? s.avg_fuel_level  ?? s.fuelLevel ?? s.fuel_level,
    openAlerts:     s.openAlerts     ?? s.open_alerts     ?? 0,
    criticalAlerts: s.criticalAlerts ?? s.critical_alerts ?? 0,
    consumption:    s.consumption    ?? s.fuel_consumption,
    efficiency:     s.efficiency     ?? s.fuel_efficiency,
  }));

  const tb = d.typeBreakdown ?? d.type_breakdown ?? d.breakdown;
  const typeBreakdown: Record<string, number> | undefined = tb
    ? Object.fromEntries(Object.entries(tb).map(([k, v]) => [k, Number(v)]))
    : undefined;

  return {
    totalEvents:     d.totalEvents     ?? d.total_events     ?? 0,
    openIssues:      d.openIssues      ?? d.open_issues      ?? 0,
    criticalUnacked: d.criticalUnacked ?? d.critical_unacked ?? 0,
    resolved:        d.resolved        ?? d.resolved_count   ?? 0,
    typeBreakdown,
    sites: sites.length > 0 ? sites : undefined,
  };
}

/* ── Report tank status ── */
export interface TankStatusItem {
  tankId: string;
  tankName: string;
  siteId: string;
  siteName: string;
  fuelType: string;
  capacity: number;
  currentLevel: number;
  currentVolume?: number;
  qualityStatus: string;
  lastRefill?: string;
  activeAlerts: number;
  /** Section 3.0 — days of cover left at the current burn rate. */
  daysRemaining?: number;
}

export async function fetchReportTankStatus(params: { siteId?: string } = {}): Promise<TankStatusItem[]> {
  const qs = new URLSearchParams();
  if (params.siteId) qs.set('siteId', params.siteId);

  const res  = await apiFetch(`/api/reports/tank-status/${qs.toString() ? `?${qs}` : ''}`);
  const body = await res.json().catch(() => null);

  if (!res.ok) {
    const message = body?.detail ?? body?.message ?? 'Failed to fetch tank status.';
    throw { message, status: res.status } as ApiError;
  }

  const raw   = body?.data ?? body;
  const items: any[] = Array.isArray(raw) ? raw : (raw?.results ?? raw?.tanks ?? []);

  return items.map((t: any) => ({
    tankId:        String(t.tankId        ?? t.tank_id        ?? t.id     ?? ''),
    tankName:      t.tankName             ?? t.tank_name      ?? t.name   ?? '',
    siteId:        String(t.siteId        ?? t.site_id        ?? ''),
    siteName:      t.siteName             ?? t.site_name      ?? '',
    fuelType:      t.fuelType             ?? t.fuel_type      ?? '',
    capacity:      Number(t.capacity      ?? 0),
    currentLevel:  Number(t.currentLevel  ?? t.current_level  ?? t.level  ?? 0),
    currentVolume: t.currentVolume        ?? t.current_volume,
    qualityStatus: t.qualityStatus        ?? t.quality_status ?? t.quality?.status ?? 'good',
    lastRefill:    t.lastRefill           ?? t.last_refill,
    activeAlerts:  Number(t.activeAlerts  ?? t.active_alerts  ?? 0),
    daysRemaining: numOrUndef(t.daysRemaining ?? t.days_remaining),
  }));
}

/* ── Issue escalation ── */
export interface EscalateIssueParams {
  escalateTo: number;
  reason: string;
  newSeverity?: 'low' | 'medium' | 'high' | 'critical' | null;
}

export async function escalateIssue(issueId: string, params: EscalateIssueParams): Promise<void> {
  const payload: Record<string, any> = {
    escalateTo: params.escalateTo,
    reason:     params.reason,
  };
  if (params.newSeverity) payload.newSeverity = params.newSeverity;

  const res = await apiFetch(`/api/reports/issues/${issueId}/escalate/`, {
    method:  'POST',
    headers: { 'Content-Type': 'application/json' },
    body:    JSON.stringify(payload),
  });

  if (!res.ok) {
    const body = await res.json().catch(() => null);
    const message = body?.detail ?? body?.message ?? 'Failed to escalate issue.';
    throw { message, status: res.status } as ApiError;
  }
}

/* ── Report export ── */
export interface ReportExportParams {
  reportType: 'summary' | 'events' | 'issues' | 'tank-status';
  format: 'csv' | 'pdf';
  filters?: Record<string, string>;
}

export async function fetchReportExport(params: ReportExportParams): Promise<void> {
  const res = await apiFetch('/api/reports/export/', {
    method:  'POST',
    headers: { 'Content-Type': 'application/json' },
    body:    JSON.stringify({
      reportType: params.reportType,
      format:     params.format,
      filters:    params.filters ?? {},
    }),
  });

  if (!res.ok) {
    const body = await res.json().catch(() => null);
    const message = body?.detail ?? body?.message ?? 'Export failed.';
    throw { message, status: res.status } as ApiError;
  }

  const contentType = res.headers.get('content-type') ?? '';
  if (
    contentType.includes('text/csv') ||
    contentType.includes('application/pdf') ||
    contentType.includes('octet-stream')
  ) {
    const blob = await res.blob();
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href     = url;
    a.download = `report_${params.reportType}.${params.format}`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  } else {
    const body = await res.json().catch(() => null);
    const url  = body?.data?.url ?? body?.url;
    if (url) window.open(url, '_blank');
  }
}

/* ── Vendor / company profile ── */
export interface VendorProfile {
  username:           string;
  email:              string;
  companyName:        string;
  industryType:       string;
  registrationNumber: string;
  address:            string;
  country:            string;
  stateProvince:      string;
  phoneNumber:        string;
  website:            string;
  isVerified:         boolean;
  dateJoined:         string;
}

export type IndustryType = 'oil_and_gas' | 'banking' | 'government' | 'other';

export interface UpdateProfilePayload {
  companyName?:         string;
  industryType?:        IndustryType;
  registrationNumber?:  string;
  address?:             string;
  country?:             string;
  stateProvince?:       string;
  phoneNumber?:         string;
  website?:             string;
}

function mapProfile(d: any): VendorProfile {
  return {
    username:           d.username                    ?? '',
    email:              d.email                       ?? '',
    companyName:        d.company_name                ?? d.companyName        ?? '',
    industryType:       d.industry_type               ?? d.industryType       ?? '',
    registrationNumber: d.company_registration_number ?? d.registrationNumber ?? '',
    address:            d.company_address             ?? d.address            ?? '',
    country:            d.country                     ?? '',
    stateProvince:      d.state_province              ?? d.stateProvince      ?? '',
    phoneNumber:        d.phone_number                ?? d.phoneNumber        ?? '',
    website:            d.website                     ?? '',
    isVerified:         Boolean(d.is_verified         ?? d.isVerified         ?? false),
    dateJoined:         d.date_joined                 ?? d.dateJoined         ?? '',
  };
}

export async function fetchProfile(): Promise<VendorProfile> {
  const res  = await apiFetch('/api/v1/onboard/profile/');
  const body = await res.json().catch(() => null);

  if (!res.ok) {
    const message = body?.detail ?? body?.message ?? 'Failed to fetch profile.';
    throw { message, status: res.status } as ApiError;
  }

  const raw = body?.data ?? body;
  return mapProfile(raw);
}

export async function updateProfile(payload: UpdateProfilePayload): Promise<VendorProfile> {
  const mapped: Record<string, string | undefined> = {
    company_name:                payload.companyName,
    industry_type:               payload.industryType,
    company_registration_number: payload.registrationNumber,
    company_address:             payload.address,
    country:                     payload.country,
    state_province:              payload.stateProvince,
    phone_number:                payload.phoneNumber,
    website:                     payload.website,
  };
  // strip undefined
  const body_payload = Object.fromEntries(
    Object.entries(mapped).filter(([, v]) => v !== undefined),
  );

  const res  = await apiFetch('/api/v1/onboard/profile/', {
    method:  'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body:    JSON.stringify(body_payload),
  });
  const body = await res.json().catch(() => null);

  if (res.status === 400) {
    // Alarm-level failures come back keyed under `errors`, e.g.
    // { reorderLevelPct: ["Must be greater than minimumLevelPct (18)…"] }.
    const fieldErrors = body?.errors ?? (body && typeof body === 'object' ? body : null);
    const msgs = fieldErrors && typeof fieldErrors === 'object'
      ? Object.values(fieldErrors).flat().join('; ')
      : null;
    throw {
      message: msgs ?? body?.detail ?? body?.message ?? 'Validation error.',
      status: 400,
      errors: body?.errors,
    } as ApiError & { errors?: Record<string, string[]> };
  }
  if (!res.ok) {
    throw { message: body?.detail ?? body?.message ?? 'Failed to update profile.', status: res.status } as ApiError;
  }

  const raw = body?.data ?? body;
  return mapProfile(raw);
}

/* ── Users ── */
export interface UserRecord {
  id:        string;
  /** The login account's integer id — what the security endpoints call investigatorId.
      The users list's own `id` is often a UUID, which those endpoints reject. */
  userId?:   number;
  name:      string;
  email:     string;
  role:      'owner' | 'Admin' | 'Manager' | 'Operator' | 'Auditor';
  status:    'Active' | 'Inactive';
  lastLogin: string;
}

export interface UsersParams {
  role?:   'Admin' | 'Manager' | 'Operator' | 'Auditor';
  status?: 'active' | 'inactive';
  search?: string;
  page?:   number;
  limit?:  number;
}

export interface UsersResult {
  count:    number;
  next:     string | null;
  previous: string | null;
  results:  UserRecord[];
}

function mapUser(u: any): UserRecord {
  const rawRole  = u.role ?? u.user_role ?? '';
  const role: UserRecord['role'] =
    (['owner', 'Admin', 'Manager', 'Operator', 'Auditor'] as const).includes(rawRole) ? rawRole : 'Operator';

  const rawStatus = String(u.status ?? (u.is_active != null ? (u.is_active ? 'active' : 'inactive') : 'active')).toLowerCase();
  const status: 'Active' | 'Inactive' = rawStatus === 'active' ? 'Active' : 'Inactive';

  const intOf = (v: any) => (v != null && v !== '' && Number.isInteger(Number(v)) ? Number(v) : undefined);
  return {
    id:        String(u.id ?? ''),
    userId:    intOf(u.userId) ?? intOf(u.user_id) ?? intOf(u.user?.id) ?? intOf(u.accountId) ?? intOf(u.authUserId) ?? intOf(u.id),
    name:      u.name ?? u.full_name ?? u.username ?? u.email ?? '',
    email:     u.email ?? '',
    role,
    status,
    lastLogin: u.lastLogin ?? u.last_login ?? u.last_active ?? new Date().toISOString(),
  };
}

export interface CreateUserPayload {
  name:      string;
  email:     string;
  role:      'Admin' | 'Manager' | 'Operator' | 'Auditor';
  password:  string;
  status?:   'active' | 'inactive';
  siteIds?:  string[];
}

export async function createUser(payload: CreateUserPayload): Promise<UserRecord> {
  const res  = await apiFetch('/api/v1/onboard/users/', {
    method:  'POST',
    headers: { 'Content-Type': 'application/json' },
    body:    JSON.stringify({
      name:     payload.name,
      email:    payload.email,
      role:     payload.role,
      password: payload.password,
      status:   payload.status ?? 'active',
      siteIds:  payload.siteIds ?? [],
    }),
  });
  const body = await res.json().catch(() => null);

  if (res.status === 400) {
    // Alarm-level failures come back keyed under `errors`, e.g.
    // { reorderLevelPct: ["Must be greater than minimumLevelPct (18)…"] }.
    const fieldErrors = body?.errors ?? (body && typeof body === 'object' ? body : null);
    const msgs = fieldErrors && typeof fieldErrors === 'object'
      ? Object.values(fieldErrors).flat().join('; ')
      : null;
    throw {
      message: msgs ?? body?.detail ?? body?.message ?? 'Validation error.',
      status: 400,
      errors: body?.errors,
    } as ApiError & { errors?: Record<string, string[]> };
  }
  if (!res.ok) {
    throw { message: body?.detail ?? body?.message ?? 'Failed to create user.', status: res.status } as ApiError;
  }

  const raw = body?.data ?? body;
  return mapUser(raw);
}

export interface UpdateUserPayload {
  name?:    string;
  email?:   string;
  role?:    'Admin' | 'Manager' | 'Operator' | 'Auditor';
  status?:  'active' | 'inactive';
  siteIds?: string[];
}

export async function updateUser(userId: string, payload: UpdateUserPayload): Promise<UserRecord> {
  const body_payload: Record<string, any> = {};
  if (payload.name    != null) body_payload.name    = payload.name;
  if (payload.email   != null) body_payload.email   = payload.email;
  if (payload.role    != null) body_payload.role    = payload.role;
  if (payload.status  != null) body_payload.status  = payload.status;
  if (payload.siteIds != null) body_payload.siteIds = payload.siteIds;

  const res  = await apiFetch(`/api/v1/onboard/users/${userId}/`, {
    method:  'PUT',
    headers: { 'Content-Type': 'application/json' },
    body:    JSON.stringify(body_payload),
  });
  const body = await res.json().catch(() => null);

  if (res.status === 404) throw { message: 'User not found.', status: 404 } as ApiError;
  if (res.status === 400) {
    // Alarm-level failures come back keyed under `errors`, e.g.
    // { reorderLevelPct: ["Must be greater than minimumLevelPct (18)…"] }.
    const fieldErrors = body?.errors ?? (body && typeof body === 'object' ? body : null);
    const msgs = fieldErrors && typeof fieldErrors === 'object'
      ? Object.values(fieldErrors).flat().join('; ')
      : null;
    throw {
      message: msgs ?? body?.detail ?? body?.message ?? 'Validation error.',
      status: 400,
      errors: body?.errors,
    } as ApiError & { errors?: Record<string, string[]> };
  }
  if (!res.ok) {
    throw { message: body?.detail ?? body?.message ?? 'Failed to update user.', status: res.status } as ApiError;
  }

  const raw = body?.data ?? body;
  return mapUser(raw);
}

export interface ResetUserPasswordPayload {
  newPassword:      string;
  currentPassword?: string;
}

export async function resetUserPassword(userId: string, payload: ResetUserPasswordPayload): Promise<void> {
  const body_payload: Record<string, string> = { newPassword: payload.newPassword };
  if (payload.currentPassword) body_payload.currentPassword = payload.currentPassword;

  const res  = await apiFetch(`/api/v1/onboard/users/${userId}/password/`, {
    method:  'PUT',
    headers: { 'Content-Type': 'application/json' },
    body:    JSON.stringify(body_payload),
  });
  if (res.status === 200) return;
  const body = await res.json().catch(() => null);
  if (res.status === 400) throw toApiError(400, body, 'Wrong current password or validation error.');
  if (res.status === 404) throw { message: 'User not found.', status: 404 } as ApiError;
  throw { message: body?.detail ?? body?.message ?? 'Failed to reset password.', status: res.status } as ApiError;
}

export async function deleteUser(userId: string): Promise<void> {
  const res  = await apiFetch(`/api/v1/onboard/users/${userId}/`, { method: 'DELETE' });
  if (res.status === 200 || res.status === 204) return;
  const body = await res.json().catch(() => null);
  if (res.status === 400) throw { message: body?.detail ?? body?.message ?? 'Cannot delete self.', status: 400 } as ApiError;
  if (res.status === 404) throw { message: 'User not found.',                                      status: 404 } as ApiError;
  throw { message: body?.detail ?? body?.message ?? 'Failed to delete user.', status: res.status } as ApiError;
}

export async function fetchUserById(userId: string): Promise<UserRecord> {
  const res  = await apiFetch(`/api/v1/onboard/users/${userId}/`);
  const body = await res.json().catch(() => null);

  if (res.status === 404) throw { message: 'User not found.', status: 404 } as ApiError;
  if (!res.ok) {
    const message = body?.detail ?? body?.message ?? 'Failed to fetch user.';
    throw { message, status: res.status } as ApiError;
  }

  const raw = body?.data ?? body;
  return mapUser(raw);
}

export async function fetchUsers(params: UsersParams = {}): Promise<UsersResult> {
  const qs = new URLSearchParams();
  if (params.role   != null) qs.set('role',   params.role);
  if (params.status != null) qs.set('status', params.status);
  if (params.search != null) qs.set('search', params.search);
  if (params.page   != null) qs.set('page',   String(params.page));
  if (params.limit  != null) qs.set('limit',  String(params.limit));

  const res  = await apiFetch(`/api/v1/onboard/users/${qs.toString() ? `?${qs}` : ''}`);
  const body = await res.json().catch(() => null);

  if (!res.ok) {
    const message = body?.detail ?? body?.message ?? 'Failed to fetch users.';
    throw { message, status: res.status } as ApiError;
  }

  const raw   = body?.data ?? body;
  const items: any[] = Array.isArray(raw) ? raw : (raw?.results ?? []);

  return {
    count:    body?.total ?? raw?.count ?? items.length,
    next:     raw?.next     ?? null,
    previous: raw?.previous ?? null,
    results:  items.map(mapUser),
  };
}

/* ── Fuel suppliers ── */
export interface SupplierRecord {
  id:         string;
  name:       string;
  contact:    string;
  phone:      string;
  email:      string;
  fuelTypes:  string[];
  status:     'Active' | 'Inactive';
  deliveries: number;
}

export interface SuppliersParams {
  search?: string;
  page?:   number;
  limit?:  number;
}

export interface SuppliersResult {
  count:    number;
  next:     string | null;
  previous: string | null;
  results:  SupplierRecord[];
}

function mapSupplier(s: any): SupplierRecord {
  const rawFuelTypes = s.fuel_types ?? s.fuelTypes ?? [];
  const fuelTypes: string[] = Array.isArray(rawFuelTypes)
    ? rawFuelTypes.map((f: any) => String(f))
    : typeof rawFuelTypes === 'string' && rawFuelTypes
    ? rawFuelTypes.split(',').map((f: string) => f.trim())
    : [];

  const rawStatus = s.status ?? '';
  const status: 'Active' | 'Inactive' =
    rawStatus.toLowerCase() === 'active' ? 'Active' : 'Inactive';

  return {
    id:         String(s.id ?? ''),
    name:       s.name ?? s.companyName ?? s.company_name ?? '',
    contact:    s.contact ?? s.contactPerson ?? s.contact_person ?? s.contact_name ?? '',
    phone:      s.phone ?? s.phone_number ?? '',
    email:      s.email ?? s.contact_email ?? '',
    fuelTypes,
    status,
    deliveries: Number(s.deliveries ?? s.delivery_count ?? s.total_deliveries ?? 0),
  };
}

export interface CreateSupplierPayload {
  companyName:    string;
  contactPerson?: string;
  phone?:         string;
  email?:         string;
}

export async function createSupplier(payload: CreateSupplierPayload): Promise<SupplierRecord> {
  const body_payload: Record<string, string> = { companyName: payload.companyName };
  if (payload.contactPerson) body_payload.contactPerson = payload.contactPerson;
  if (payload.phone)         body_payload.phone         = payload.phone;
  if (payload.email)         body_payload.email         = payload.email;

  const res  = await apiFetch('/api/v1/onboard/suppliers/', {
    method:  'POST',
    headers: { 'Content-Type': 'application/json' },
    body:    JSON.stringify(body_payload),
  });
  const body = await res.json().catch(() => null);

  if (res.status === 400) {
    // Alarm-level failures come back keyed under `errors`, e.g.
    // { reorderLevelPct: ["Must be greater than minimumLevelPct (18)…"] }.
    const fieldErrors = body?.errors ?? (body && typeof body === 'object' ? body : null);
    const msgs = fieldErrors && typeof fieldErrors === 'object'
      ? Object.values(fieldErrors).flat().join('; ')
      : null;
    throw {
      message: msgs ?? body?.detail ?? body?.message ?? 'Validation error.',
      status: 400,
      errors: body?.errors,
    } as ApiError & { errors?: Record<string, string[]> };
  }
  if (!res.ok) {
    throw { message: body?.detail ?? body?.message ?? 'Failed to create supplier.', status: res.status } as ApiError;
  }

  const raw = body?.data ?? body;
  return mapSupplier(raw);
}

export interface UpdateSupplierPayload {
  companyName?:    string;
  contactPerson?:  string;
  phone?:          string;
  email?:          string;
}

export async function updateSupplier(supplierId: string, payload: UpdateSupplierPayload): Promise<SupplierRecord> {
  const body_payload: Record<string, string> = {};
  if (payload.companyName   != null) body_payload.companyName   = payload.companyName;
  if (payload.contactPerson != null) body_payload.contactPerson = payload.contactPerson;
  if (payload.phone         != null) body_payload.phone         = payload.phone;
  if (payload.email         != null) body_payload.email         = payload.email;

  const res  = await apiFetch(`/api/v1/onboard/suppliers/${supplierId}/`, {
    method:  'PUT',
    headers: { 'Content-Type': 'application/json' },
    body:    JSON.stringify(body_payload),
  });
  const body = await res.json().catch(() => null);

  if (res.status === 404) throw { message: 'Supplier not found.', status: 404 } as ApiError;
  if (res.status === 400) {
    // Alarm-level failures come back keyed under `errors`, e.g.
    // { reorderLevelPct: ["Must be greater than minimumLevelPct (18)…"] }.
    const fieldErrors = body?.errors ?? (body && typeof body === 'object' ? body : null);
    const msgs = fieldErrors && typeof fieldErrors === 'object'
      ? Object.values(fieldErrors).flat().join('; ')
      : null;
    throw {
      message: msgs ?? body?.detail ?? body?.message ?? 'Validation error.',
      status: 400,
      errors: body?.errors,
    } as ApiError & { errors?: Record<string, string[]> };
  }
  if (!res.ok) {
    throw { message: body?.detail ?? body?.message ?? 'Failed to update supplier.', status: res.status } as ApiError;
  }

  const raw = body?.data ?? body;
  return mapSupplier(raw);
}

export async function deleteSupplier(supplierId: string): Promise<void> {
  const res  = await apiFetch(`/api/v1/onboard/suppliers/${supplierId}/`, { method: 'DELETE' });
  if (res.status === 200 || res.status === 204) return;
  const body = await res.json().catch(() => null);
  if (res.status === 404) throw { message: 'Supplier not found.', status: 404 } as ApiError;
  throw { message: body?.detail ?? body?.message ?? 'Failed to delete supplier.', status: res.status } as ApiError;
}

export async function fetchSupplierById(supplierId: string): Promise<SupplierRecord> {
  const res  = await apiFetch(`/api/v1/onboard/suppliers/${supplierId}/`);
  const body = await res.json().catch(() => null);

  if (res.status === 404) throw { message: 'Supplier not found.', status: 404 } as ApiError;
  if (!res.ok) {
    const message = body?.detail ?? body?.message ?? 'Failed to fetch supplier.';
    throw { message, status: res.status } as ApiError;
  }

  const raw = body?.data ?? body;
  return mapSupplier(raw);
}

export async function fetchSuppliers(params: SuppliersParams = {}): Promise<SuppliersResult> {
  const qs = new URLSearchParams();
  if (params.search != null) qs.set('search', params.search);
  if (params.page   != null) qs.set('page',   String(params.page));
  if (params.limit  != null) qs.set('limit',  String(params.limit));

  const res  = await apiFetch(`/api/v1/onboard/suppliers/${qs.toString() ? `?${qs}` : ''}`);
  const body = await res.json().catch(() => null);

  if (!res.ok) {
    const message = body?.detail ?? body?.message ?? 'Failed to fetch suppliers.';
    throw { message, status: res.status } as ApiError;
  }

  const raw   = body?.data ?? body;
  const items: any[] = Array.isArray(raw) ? raw : (raw?.results ?? []);

  return {
    count:    body?.total ?? raw?.count ?? items.length,
    next:     raw?.next     ?? null,
    previous: raw?.previous ?? null,
    results:  items.map(mapSupplier),
  };
}

/* ── Mark one notification as read ── */
export async function markNotificationRead(notificationId: string): Promise<void> {
  const res = await apiFetch(`/api/v1/onboard/notifications/${notificationId}/read/`, { method: 'PUT' });
  if (res.status === 200) return;
  const body = await res.json().catch(() => null);
  if (res.status === 404) throw { message: 'Notification not found.', status: 404 } as ApiError;
  throw { message: body?.detail ?? body?.message ?? 'Failed to mark notification as read.', status: res.status } as ApiError;
}

/* ── Mark all notifications as read ── */
export async function markAllNotificationsRead(): Promise<void> {
  const res = await apiFetch('/api/v1/onboard/notifications/read-all/', { method: 'PUT' });
  if (!res.ok) {
    const body = await res.json().catch(() => null);
    throw { message: body?.detail ?? body?.message ?? 'Failed to mark notifications as read.', status: res.status } as ApiError;
  }
}

/* ── Alert / notification type ──
   The server's own type wins when it is one we know. Otherwise map common synonyms, then
   read the title — notifications such as "Abnormal fuel use: Truck A32R (Emeka Obi)" are
   raised alongside a `consumption` alert and may not carry that exact type string. */
const KNOWN_ALERT_TYPES = new Set(['theft', 'leak', 'low_fuel', 'contamination', 'maintenance', 'refill', 'overfill', 'movement', 'consumption']);
const ALERT_TYPE_SYNONYMS: Record<string, string> = {
  abnormal_fuel_use: 'consumption', abnormal_consumption: 'consumption', fuel_consumption: 'consumption',
  high_consumption: 'consumption', consumption_alert: 'consumption', abnormal_use: 'consumption',
  tank_movement: 'movement', watch_window: 'movement', movement_alert: 'movement',
  low_level: 'low_fuel', low_fuel_level: 'low_fuel', reorder: 'low_fuel', minimum_level: 'low_fuel', critical_level: 'low_fuel',
  delivery: 'refill', fuel_delivery: 'refill', quality: 'contamination', fuel_quality: 'contamination', suspected_theft: 'theft',
};
export function normaliseAlertType(raw: unknown, title?: string, message?: string): import('../utils/mockData').AlertData['type'] {
  const key = String(raw ?? '').trim().toLowerCase().replace(/[\s-]+/g, '_');
  if (KNOWN_ALERT_TYPES.has(key)) return key as any;
  if (ALERT_TYPE_SYNONYMS[key]) return ALERT_TYPE_SYNONYMS[key] as any;
  const text = `${title ?? ''} ${message ?? ''}`.toLowerCase();
  if (/abnormal fuel use|consumption|fuel use/.test(text)) return 'consumption';
  if (/theft|siphon/.test(text))                        return 'theft';
  if (/leak/.test(text))                                return 'leak';
  if (/overfill/.test(text))                            return 'overfill';
  if (/refill|deliver/.test(text))                      return 'refill';
  if (/movement|watch window/.test(text))               return 'movement';
  if (/contaminat|quality|water content|sediment/.test(text)) return 'contamination';
  if (/maintenance|service/.test(text))                 return 'maintenance';
  return 'low_fuel';
}

/* ── User notifications ── */
export interface NotificationsParams {
  read?: 'all' | 'unread';
  limit?: number;
}

export async function fetchNotifications(
  params: NotificationsParams = {},
): Promise<import('../utils/mockData').AlertData[]> {
  const qs = new URLSearchParams();
  if (params.read  != null) qs.set('read',  params.read);
  if (params.limit != null) qs.set('limit', String(params.limit));

  const res  = await apiFetch(`/api/v1/onboard/notifications/${qs.toString() ? `?${qs}` : ''}`);
  const body = await res.json().catch(() => null);

  if (!res.ok) {
    const message = body?.detail ?? body?.message ?? 'Failed to fetch notifications.';
    throw { message, status: res.status } as ApiError;
  }

  const raw   = body?.data ?? body;
  const items: any[] = Array.isArray(raw) ? raw : (raw?.results ?? raw?.notifications ?? []);

  // Includes the new "abnormal fuel use" notifications, raised alongside a consumption alert.
  return items.map((n: any) => {
    const siteRaw = n.siteId ?? n.site_id;
    const tankRaw = n.tankId ?? n.tank_id;
    const created = n.createdAt ?? n.created_at ?? n.sent_at ?? (isIsoDate(n.timestamp) ? n.timestamp : undefined);
    return {
      id:           String(n.id           ?? ''),
      type:         normaliseAlertType(n.type ?? n.alert_type ?? n.event_type ?? n.kind ?? n.category, n.title ?? n.subject, n.message ?? n.body),
      severity:     n.severity            ?? n.level      ?? n.priority   ?? 'low',
      title:        n.title               ?? n.subject    ?? '',
      message:      n.message             ?? n.body       ?? n.description ?? '',
      timestamp:    created ?? new Date().toISOString(),
      createdAt:    created,
      timestampDisplay: isIsoDate(n.timestamp) ? undefined : (n.timestamp ?? undefined),
      timeAgo:      n.timeAgo ?? undefined,
      siteId:       String((siteRaw && typeof siteRaw === 'object' ? siteRaw.id : siteRaw) ?? ''),
      siteName:     siteRaw && typeof siteRaw === 'object' ? siteRaw.name : (n.siteName ?? undefined),
      tankId:       tankRaw && typeof tankRaw === 'object' ? String(tankRaw.id ?? '') : tankRaw,
      tankName:     tankRaw && typeof tankRaw === 'object' ? tankRaw.name : (n.tankName ?? undefined),
      generatorId:  n.generatorId         ?? n.generator_id,
      acknowledged: Boolean(n.acknowledged ?? n.read ?? n.is_read ?? false),
      resolved:     Boolean(n.resolved    ?? n.is_resolved ?? false),
      // Where the notification points: its alert (the consumption alert for abnormal fuel use) or a link.
      alertId:      n.alertId ?? n.alert_id ?? (n.alert && typeof n.alert === 'object' ? n.alert.id : undefined) ?? undefined,
      link:         n.link ?? n.url ?? undefined,
    };
  });
}

/* ── Poll alerts (replaces WebSocket) ── */
/** True when a string parses as a real date (ISO), not a display string like "14 Oct 2026, 3:46 PM WAT". */
function isIsoDate(v: unknown): v is string {
  return typeof v === 'string' && /^\d{4}-\d{2}-\d{2}T/.test(v) && !Number.isNaN(Date.parse(v));
}

export function mapAlert(a: any): AlertRecord {
  // Safe migration while old and new backends may both be deployed (guide §3.2).
  const siteRaw = a.siteId ?? a.site_id ?? a.site;
  const tankRaw = a.tankId ?? a.tank_id ?? a.tank;
  const site = typeof siteRaw === 'object' ? mapSiteBlock(siteRaw) : undefined;
  const tank = typeof tankRaw === 'object' ? mapTankBlock(tankRaw) : undefined;
  const createdAt = a.createdAt ?? a.created_at ?? (isIsoDate(a.timestamp) ? a.timestamp : undefined);
  return {
    id:           String(a.id ?? ''),
    type:         normaliseAlertType(a.type, a.title, a.message),
    severity:     a.severity    ?? 'low',
    title:        a.title       ?? '',
    message:      a.message     ?? '',
    timestamp:    createdAt ?? new Date().toISOString(),
    createdAt,
    timestampDisplay: isIsoDate(a.timestamp) ? undefined : (a.timestamp ?? undefined),
    timeAgo:      a.timeAgo ?? undefined,
    siteId:       site ? site.id : String(siteRaw ?? ''),
    siteName:     site?.name ?? a.siteName ?? a.site_name ?? undefined,
    site,
    tankId:       tank ? tank.id : (tankRaw != null ? String(tankRaw) : undefined),
    tankName:     tank?.name ?? a.tankName ?? a.tank_name ?? undefined,
    tank,
    generatorId:  a.generatorId ?? a.generator_id ?? undefined,
    acknowledged: Boolean(a.acknowledged ?? false),
    resolved:     Boolean(a.resolved     ?? false),
    acknowledgedAtDisplay: a.acknowledgedAtDisplay ?? undefined,
    resolvedAtDisplay:     a.resolvedAtDisplay ?? undefined,
  };
}

export interface PollAlertsResult {
  /** The full current alert list (used to seed state on the first poll). */
  results:       AlertRecord[];
  /** Only what's genuinely new since the `since` cursor — use this to append on later polls. */
  newAlerts:     AlertRecord[];
  unreadCount:   number;
  totalCount:    number;
  criticalCount: number;
  truncated:     boolean;
  /** Pass this back as `since` on the next call. */
  timestamp:     string;
}

export async function fetchPollAlerts(params: { since?: string; type?: string } = {}): Promise<PollAlertsResult> {
  const qs = new URLSearchParams();
  if (params.since) qs.set('since', params.since);
  if (params.type)  qs.set('type',  params.type);

  const res  = await apiFetch(`/api/poll/alerts/${qs.toString() ? `?${qs}` : ''}`);
  const body = await res.json().catch(() => null);

  if (!res.ok) {
    const message = body?.detail ?? body?.message ?? 'Failed to poll alerts.';
    throw { message, status: res.status } as ApiError;
  }

  // Real shape: { status, type: "alerts_update", payload: { alerts, newAlerts,
  // newCount, unreadCount, totalCount, criticalCount, truncated, timestamp } }.
  // Older/simpler shapes ({ data: [...] } or a bare array) are still supported
  // as a fallback.
  const payload = body?.payload ?? body?.data ?? body;

  const alertsRaw: any[] = Array.isArray(payload?.alerts) ? payload.alerts
                          : Array.isArray(payload)         ? payload
                          : Array.isArray(payload?.results) ? payload.results
                          : [];
  const newAlertsRaw: any[] = Array.isArray(payload?.newAlerts) ? payload.newAlerts : [];

  const results   = alertsRaw.map(mapAlert);
  const newAlerts = newAlertsRaw.map(mapAlert);

  // Server may return a timestamp to use as `since` on the next poll
  const timestamp = payload?.timestamp ?? body?.timestamp ?? body?.server_time ?? body?.next_since ?? new Date().toISOString();

  return {
    results,
    newAlerts,
    unreadCount:   numOf(payload?.unreadCount   ?? payload?.unread_count),
    totalCount:    numOf(payload?.totalCount    ?? payload?.total_count ?? results.length),
    criticalCount: numOf(payload?.criticalCount ?? payload?.critical_count),
    truncated:     Boolean(payload?.truncated),
    timestamp,
  };
}

/* ── Poll tanker GPS location (replaces ws/tankers/:id/location) ── */
export async function fetchPollTankerLocation(tankerId: string): Promise<TankerLocation> {
  const res  = await apiFetch(`/api/poll/tankers/${tankerId}/location/`);
  if (res.status === 404) throw { message: 'Tanker not found', status: 404 } as ApiError;
  const body = await res.json().catch(() => null);
  if (!res.ok) {
    const message = body?.detail ?? body?.message ?? 'Failed to fetch tanker location.';
    throw { message, status: res.status } as ApiError;
  }
  const raw = body?.data ?? body;
  return {
    lat:       Number(raw?.lat       ?? raw?.latitude     ?? 0),
    lng:       Number(raw?.lng       ?? raw?.longitude    ?? 0),
    address:   raw?.address          ?? raw?.location_name ?? '',
    speed:     raw?.speed   != null  ? Number(raw.speed)   : undefined,
    heading:   raw?.heading != null  ? Number(raw.heading) : undefined,
    timestamp: raw?.timestamp        ?? raw?.updated_at    ?? undefined,
  };
}

/* ── Poll live data (replaces WebSocket ws/live-data) ── */
export interface LiveDataSnapshot {
  sites: import('./mockData').SiteData[];
}

export async function fetchPollLiveData(params: { siteIds?: string[] } = {}): Promise<LiveDataSnapshot> {
  const qs = new URLSearchParams();
  if (params.siteIds && params.siteIds.length > 0) qs.set('siteIds', params.siteIds.join(','));

  const res  = await apiFetch(`/api/poll/live-data/${qs.toString() ? `?${qs}` : ''}`);
  const body = await res.json().catch(() => null);

  if (!res.ok) {
    const message = body?.detail ?? body?.message ?? 'Failed to fetch live data.';
    throw { message, status: res.status } as ApiError;
  }

  const raw   = body?.data ?? body;
  const items: any[] = Array.isArray(raw) ? raw : (raw?.sites ?? []);

  return { sites: items.map(mapSite) };
}

/* ── Sensors ── */
export type SensorDeviceType = 'level_sensor' | 'flow_meter' | 'quality_sensor' | 'temperature_sensor';
export type SensorStatus     = 'online' | 'offline' | 'error';

export interface SensorRecord {
  id:             string;
  serialNumber:   string;
  name:           string;
  sensorType:     string;
  deviceType:     SensorDeviceType | null;
  unit:           string;
  siteId:         string;
  siteName:       string;
  tankId?:        string;
  tankName?:      string;
  /** Full tank envelope (capacity, current level/volume, fuel type, alarm bands) when the API includes it. */
  tank?:          ReadingTank;
  isActive:       boolean;
  status?:        SensorStatus;
  tankStart?:     number;
  tankEnd?:       number;
  engineStart?:   number;
  engineEnd?:     number;
  lastReading?:   number;
  batteryPct?:    number;
  signalPct?:     number;
  firmwareVersion?: string;
  needsUpdate?:   boolean;
  lastSeen?:      string;
}

export interface SensorsParams {
  isActive?: boolean;
}

export interface SensorsResult {
  count:    number;
  results:  SensorRecord[];
}

const SENSOR_DEVICE_TYPES: SensorDeviceType[] = [
  'level_sensor', 'flow_meter', 'quality_sensor', 'temperature_sensor',
];

const SENSOR_STATUS_VALUES: SensorStatus[] = ['online', 'offline', 'error'];

// The backend has been observed sending device_type in varying case/spacing
// ("Level Sensor", "level-sensor", etc.) instead of the exact snake_case enum
// below. An exact-match check silently nulled deviceType for every real
// device, which broke both the type badge (fell back to "—") and the Type
// filter (which matches on this same field, so it always returned nothing).
function normalizeDeviceType(raw: unknown): SensorDeviceType | null {
  if (typeof raw !== 'string') return null;
  const normalized = raw.trim().toLowerCase().replace(/[\s-]+/g, '_');
  return (SENSOR_DEVICE_TYPES as string[]).includes(normalized) ? (normalized as SensorDeviceType) : null;
}

/** Shared by the device-list and device-readings endpoints — both nest the same tank envelope. */
function mapReadingTank(t: any): ReadingTank | undefined {
  if (!t) return undefined;
  return {
    id:                   String(t.id ?? ''),
    name:                 String(t.name ?? ''),
    fuelType:             t.fuelType ?? t.fuel_type ?? undefined,
    status:               t.status ?? undefined,
    capacityLitres:       numOrUndef(t.capacityLitres      ?? t.capacity_litres),
    currentLevelPct:      numOrUndef(t.currentLevelPct     ?? t.current_level_pct),
    currentVolumeLitres:  numOrUndef(t.currentVolumeLitres ?? t.current_volume_litres),
    lastRefillAt:         t.lastRefillAt ?? t.last_refill_at ?? null,
    criticalLevelPct:     numOrUndef(t.criticalLevelPct    ?? t.critical_level_pct),
    minimumLevelPct:      numOrUndef(t.minimumLevelPct     ?? t.minimum_level_pct),
    reorderLevelPct:      numOrUndef(t.reorderLevelPct     ?? t.reorder_level_pct),
    overfillLevelPct:     numOrUndef(t.overfillLevelPct    ?? t.overfill_level_pct),
  };
}

function mapSensor(s: any): SensorRecord {
  const rawDeviceType = s.device_type ?? s.deviceType ?? null;
  const deviceType: SensorDeviceType | null = normalizeDeviceType(rawDeviceType);

  const rawStatus = s.status ?? null;
  const status: SensorStatus | undefined =
    SENSOR_STATUS_VALUES.includes(rawStatus) ? rawStatus : undefined;

  return {
    id:              String(s.id              ?? ''),
    serialNumber:    s.serial_number          ?? s.serialNumber     ?? '',
    name:            s.name                   ?? s.sensor_name      ?? '',
    sensorType:      s.sensor_type            ?? s.sensorType       ?? s.type ?? '',
    deviceType,
    unit:            s.unit                   ?? 'liters',
    siteId:          String(s.siteId          ?? s.site_id          ?? s.site?.id   ?? ''),
    siteName:        s.siteName               ?? s.site_name        ?? s.site?.name ?? '',
    tankId:          s.tankId                 ?? s.tank_id          ?? s.tank?.id,
    tankName:        s.tankName               ?? s.tank_name        ?? s.tank?.name,
    tank:            mapReadingTank(s.tank),
    isActive:        Boolean(s.is_active      ?? s.isActive         ?? s.active     ?? true),
    status,
    // The API names these tankRangeStart/End (matching the create/update bodies);
    // older shapes used tank_start / tankStart.
    tankStart:       numOrUndef(s.tankRangeStart   ?? s.tank_range_start   ?? s.tank_start   ?? s.tankStart),
    tankEnd:         numOrUndef(s.tankRangeEnd     ?? s.tank_range_end     ?? s.tank_end     ?? s.tankEnd),
    engineStart:     numOrUndef(s.engineRangeStart ?? s.engine_range_start ?? s.engine_start ?? s.engineStart),
    engineEnd:       numOrUndef(s.engineRangeEnd   ?? s.engine_range_end   ?? s.engine_end   ?? s.engineEnd),
    lastReading:     s.last_reading  != null  ? Number(s.last_reading)  : s.lastReading  != null ? Number(s.lastReading)  :
                     s.value         != null  ? Number(s.value)         : undefined,
    batteryPct:      s.battery_pct   != null  ? Number(s.battery_pct)   : s.batteryPct   != null ? Number(s.batteryPct)   :
                     s.battery_level != null  ? Number(s.battery_level) : s.batteryLevel != null ? Number(s.batteryLevel) : undefined,
    signalPct:       s.signal_pct    != null  ? Number(s.signal_pct)    : s.signalPct    != null ? Number(s.signalPct)    : undefined,
    firmwareVersion: s.firmware_version       ?? s.firmware             ?? undefined,
    needsUpdate:     s.needs_update  != null  ? Boolean(s.needs_update) : s.needsUpdate  != null ? Boolean(s.needsUpdate) : undefined,
    lastSeen:        s.last_seen              ?? s.lastSeen             ?? s.lastUpdate  ?? s.last_update ?? s.updated_at ?? undefined,
  };
}

export interface SensorDevicesParams {
  siteId?:  string;
  status?:  SensorStatus;
  type?:    SensorDeviceType;
  search?:  string;
  page?:    number;
  limit?:   number;
}

export interface SensorDevicesResult {
  count:    number;
  next:     string | null;
  previous: string | null;
  results:  SensorRecord[];
}

export interface CreateDevicePayload {
  name:              string;
  deviceType:        SensorDeviceType;
  siteId:            string;
  serialNumber:      string;
  tankId?:           string | null;
  firmware?:         string;
  tankRangeStart?:   number | null;
  tankRangeEnd?:     number | null;
  engineRangeStart?: number | null;
  engineRangeEnd?:   number | null;
  unit?:             string;
}

export async function createSensorDevice(payload: CreateDevicePayload): Promise<SensorRecord> {
  const body_payload: Record<string, any> = {
    name:         payload.name,
    deviceType:   payload.deviceType,
    siteId:       payload.siteId,
    serialNumber: payload.serialNumber,
  };
  if (payload.tankId           !== undefined) body_payload.tankId           = payload.tankId;
  if (payload.firmware         !== undefined) body_payload.firmware         = payload.firmware;
  if (payload.tankRangeStart   !== undefined) body_payload.tankRangeStart   = payload.tankRangeStart;
  if (payload.tankRangeEnd     !== undefined) body_payload.tankRangeEnd     = payload.tankRangeEnd;
  if (payload.engineRangeStart !== undefined) body_payload.engineRangeStart = payload.engineRangeStart;
  if (payload.engineRangeEnd   !== undefined) body_payload.engineRangeEnd   = payload.engineRangeEnd;
  if (payload.unit             !== undefined) body_payload.unit             = payload.unit;

  const res  = await apiFetch('/api/v1/sensor/devices/', {
    method:  'POST',
    headers: { 'Content-Type': 'application/json' },
    body:    JSON.stringify(body_payload),
  });
  const body = await res.json().catch(() => null);

  if (res.status === 400) {
    // Alarm-level failures come back keyed under `errors`, e.g.
    // { reorderLevelPct: ["Must be greater than minimumLevelPct (18)…"] }.
    const fieldErrors = body?.errors ?? (body && typeof body === 'object' ? body : null);
    const msgs = fieldErrors && typeof fieldErrors === 'object'
      ? Object.values(fieldErrors).flat().join('; ')
      : null;
    throw {
      message: msgs ?? body?.detail ?? body?.message ?? 'Validation error.',
      status: 400,
      errors: body?.errors,
    } as ApiError & { errors?: Record<string, string[]> };
  }
  if (res.status === 403) throw { message: 'Only an Owner or Admin with the manage_sensors permission can register devices.', status: 403 } as ApiError;
  if (res.status === 409) {
    const message = body?.detail ?? body?.message ?? 'A device with this serial number already exists.';
    throw { message, status: 409, errors: { serialNumber: [message] } } as ApiError;
  }
  if (!res.ok) {
    throw { message: body?.detail ?? body?.message ?? 'Failed to register device.', status: res.status } as ApiError;
  }

  const raw = body?.data ?? body;
  return mapSensor(raw);
}

/* PUT /api/v1/sensor/devices/{device_id}/ — a partial update: send only what changed.
   `null` is meaningful for the nullable fields (tankId, the four ranges, sensorType)
   and clears them, so it is passed through; `undefined` leaves the field untouched. */
export interface UpdateDevicePayload {
  name?:             string;
  siteId?:           string | null;
  tankId?:           string | null;
  status?:           SensorStatus;
  tankRangeStart?:   number | null;
  tankRangeEnd?:     number | null;
  engineRangeStart?: number | null;
  engineRangeEnd?:   number | null;
  sensorType?:       string | null;
  unit?:             string;
}

/* A telemetry row as the device actually sends it.

   Each row carries TWO measurement channels — the storage tank and the
   equipment's own engine tank — and each channel arrives twice: the raw sensor
   figure (a 9-decimal string) and the calibrated "actual" value the server
   derives from the sensor's configured range. The calibrated values are the
   meaningful ones; the raw figures are kept for diagnosing a bad calibration.

   It also carries two clocks and a GPS fix. `x` is longitude and `y` is
   latitude — the opposite order to how coordinates are usually written, so they
   are renamed here rather than left as x/y for a caller to mix up. */
export interface DeviceReading {
  id:        string;
  /** Device's own clock. Can drift — see clockSkewMs. */
  timeSent:  string;
  /** When the server received it. Authoritative for ordering. */
  serverTimestamp: string;
  /** Canonical time used for charting: server time, falling back to device time. */
  timestamp: string;
  /** Positive when the device clock runs ahead of the server. */
  clockSkewMs?: number;

  /** Calibrated tank level. */
  tankFuelLevel?:      number;
  /** Calibrated engine-tank level. */
  engineFuelLevel?:    number;
  /** Raw sensor figure before calibration. */
  tankFuelLevelRaw?:   number;
  engineFuelLevelRaw?: number;

  latitude?:  number;   // y
  longitude?: number;   // x

  deviceId:  string;
  siteId?:   string;
  tankId?:   string;

  /** Kept so older callers keep working; mirrors tankFuelLevel. */
  value:     number;
  unit:      string;
  raw?:      number;
}

/* The readings endpoint returns more than the readings: it carries the site and
   the tank the device is attached to, including that tank's four alarm levels.
   Those are what make a reading meaningful — a level is only "low" relative to
   them — so the whole envelope is surfaced rather than just `data`. */
export interface ReadingSite {
  id:        string;
  name:      string;
  status?:   string;
  address?:  string;
  latitude?: number;
  longitude?: number;
}

export interface ReadingTank {
  id:                   string;
  name:                 string;
  fuelType?:            string;
  status?:              string;
  capacityLitres?:      number;
  currentLevelPct?:     number;
  currentVolumeLitres?: number;
  lastRefillAt?:        string | null;
  criticalLevelPct?:    number;
  minimumLevelPct?:     number;
  reorderLevelPct?:     number;
  overfillLevelPct?:    number;
}

/** One telemetry row, shared by the device- and sensor-readings endpoints. */
function mapDeviceReading(r: any, fallbackDeviceId: string): DeviceReading {

    const timeSent = r.timesent ?? r.timeSent ?? r.time_sent ?? '';
    const serverTs = r.server_timestamp ?? r.serverTimestamp ?? r.timestamp ?? r.created_at ?? '';

    // Both clocks are present, so the drift between them is measurable — and it
    // matters: a device an hour ahead makes its own timestamps useless.
    const skew = timeSent && serverTs
      ? new Date(timeSent).getTime() - new Date(serverTs).getTime()
      : undefined;

    const tankActual   = numOrUndef(r.actualTankFuelLevel   ?? r.actual_tank_fuel_level);
    const engineActual = numOrUndef(r.actualEngineFuelLevel ?? r.actual_engine_fuel_level);

    return {
      id:              String(r.id ?? ''),
      timeSent,
      serverTimestamp: serverTs,
      // Chart and sort on server time; the device clock cannot be trusted.
      timestamp:       serverTs || timeSent,
      clockSkewMs:     Number.isFinite(skew as number) ? skew : undefined,

      tankFuelLevel:      tankActual,
      engineFuelLevel:    engineActual,
      tankFuelLevelRaw:   numOrUndef(r.tankFuelLevel   ?? r.tank_fuel_level),
      engineFuelLevelRaw: numOrUndef(r.engineFuelLevel ?? r.engine_fuel_level),

      // y is latitude, x is longitude — deliberately not left as x/y.
      latitude:  numOrUndef(r.y ?? r.latitude),
      longitude: numOrUndef(r.x ?? r.longitude),

      deviceId:  String(r.deviceId ?? r.device_id ?? fallbackDeviceId),
      siteId:    r.siteId ?? r.site_id ?? undefined,
      tankId:    r.tankId ?? r.tank_id ?? undefined,

      // Back-compat for callers that just want "the" value.
      value: tankActual ?? numOrUndef(r.value ?? r.reading ?? r.level) ?? 0,
      unit:  r.unit ?? 'L',
      raw:   numOrUndef(r.tankFuelLevel ?? r.raw ?? r.raw_value),
    };
}

export interface DeviceReadingsResult {
  deviceId: string;
  site?:    ReadingSite;
  tank?:    ReadingTank;
  readings: DeviceReading[];
}

export interface DeviceReadingsParams {
  from?:  string;
  to?:    string;
  limit?: number;
}

export async function fetchDeviceReadings(
  deviceId: string,
  params: DeviceReadingsParams = {},
): Promise<DeviceReadingsResult> {
  const qs = new URLSearchParams();
  if (params.from  != null) qs.set('from',  params.from);
  if (params.to    != null) qs.set('to',    params.to);
  if (params.limit != null) qs.set('limit', String(params.limit));

  const res  = await apiFetch(`/api/v1/sensor/devices/${deviceId}/readings/${qs.toString() ? `?${qs}` : ''}`);
  const body = await res.json().catch(() => null);

  if (res.status === 404) throw { message: 'Device not found.', status: 404 } as ApiError;
  if (!res.ok) {
    const message = body?.detail ?? body?.message ?? 'Failed to fetch device readings.';
    throw { message, status: res.status } as ApiError;
  }

  const raw   = body?.data ?? body;
  const items: any[] = Array.isArray(raw) ? raw : (raw?.results ?? raw?.readings ?? []);

  const readings: DeviceReading[] = items.map(r => mapDeviceReading(r, deviceId));

  // Numeric fields arrive as strings ("5000.00"), so coerce rather than render them raw.
  const site = body?.site ? {
    id:        String(body.site.id ?? ''),
    name:      String(body.site.name ?? ''),
    status:    body.site.status ?? undefined,
    address:   body.site.address ?? undefined,
    latitude:  numOrUndef(body.site.latitude),
    longitude: numOrUndef(body.site.longitude),
  } : undefined;

  const tank = mapReadingTank(body?.tank);

  return { deviceId: String(body?.deviceId ?? deviceId), site, tank, readings };
}

export interface SensorReadingIngestPayload {
  serialNumber:    string;
  timesent:        string;
  tankFuelLevel:   string;
  engineFuelLevel: string;
  y?:              string | null;
  x?:              string | null;
}

export async function ingestSensorReading(payload: SensorReadingIngestPayload): Promise<void> {
  const body_payload: Record<string, any> = {
    serial_number:     payload.serialNumber,
    timesent:          payload.timesent,
    tankFuelLevel:     payload.tankFuelLevel,
    engineFuelLevel:   payload.engineFuelLevel,
  };
  if (payload.y !== undefined) body_payload.y = payload.y;
  if (payload.x !== undefined) body_payload.x = payload.x;

  // Device endpoint (X-Device-Key, firmware side). Kept off apiFetch so a device 401 can
  // never trigger the user's token refresh or sign them out.
  const res  = await guardedFetch(`${BASE_URL}/api/v1/sensor/ingest/`, {
    method:  'POST',
    headers: { 'Content-Type': 'application/json' },
    body:    JSON.stringify(body_payload),
  });

  if (res.status === 200 || res.status === 201) return;

  const body = await res.json().catch(() => null);
  // An unknown serial now answers 401 "Device authentication failed" (same as a wrong key).
  if (res.status === 401) throw { message: body?.message ?? body?.detail ?? 'Device authentication failed.', status: 401 } as ApiError;
  if (res.status === 400) {
    // Alarm-level failures come back keyed under `errors`, e.g.
    // { reorderLevelPct: ["Must be greater than minimumLevelPct (18)…"] }.
    const fieldErrors = body?.errors ?? (body && typeof body === 'object' ? body : null);
    const msgs = fieldErrors && typeof fieldErrors === 'object'
      ? Object.values(fieldErrors).flat().join('; ')
      : null;
    throw {
      message: msgs ?? body?.detail ?? body?.message ?? 'Validation error.',
      status: 400,
      errors: body?.errors,
    } as ApiError & { errors?: Record<string, string[]> };
  }
  if (res.status === 404) throw { message: 'Sensor not found.', status: 404 } as ApiError;
  throw { message: body?.detail ?? body?.message ?? 'Failed to ingest sensor reading.', status: res.status } as ApiError;
}

export async function ingestSensorReadingsBulk(readings: SensorReadingIngestPayload[]): Promise<void> {
  const body_payload = {
    readings: readings.map(r => {
      const item: Record<string, any> = {
        serial_number:   r.serialNumber,
        timesent:        r.timesent,
        tankFuelLevel:   r.tankFuelLevel,
        engineFuelLevel: r.engineFuelLevel,
      };
      if (r.y !== undefined) item.y = r.y;
      if (r.x !== undefined) item.x = r.x;
      return item;
    }),
  };

  const res  = await guardedFetch(`${BASE_URL}/api/v1/sensor/ingest/bulk/`, {
    method:  'POST',
    headers: { 'Content-Type': 'application/json' },
    body:    JSON.stringify(body_payload),
  });

  if (res.status === 200 || res.status === 201) return;

  const body = await res.json().catch(() => null);
  if (res.status === 401) throw { message: body?.message ?? body?.detail ?? 'Device authentication failed.', status: 401 } as ApiError;
  if (res.status === 400) {
    // Alarm-level failures come back keyed under `errors`, e.g.
    // { reorderLevelPct: ["Must be greater than minimumLevelPct (18)…"] }.
    const fieldErrors = body?.errors ?? (body && typeof body === 'object' ? body : null);
    const msgs = fieldErrors && typeof fieldErrors === 'object'
      ? Object.values(fieldErrors).flat().join('; ')
      : null;
    throw {
      message: msgs ?? body?.detail ?? body?.message ?? 'Validation error.',
      status: 400,
      errors: body?.errors,
    } as ApiError & { errors?: Record<string, string[]> };
  }
  throw { message: body?.detail ?? body?.message ?? 'Failed to bulk ingest sensor readings.', status: res.status } as ApiError;
}

export async function triggerFirmwareUpdate(deviceId: string, targetVersion: string): Promise<void> {
  const res  = await apiFetch(`/api/v1/sensor/devices/${deviceId}/firmware/`, {
    method:  'PUT',
    headers: { 'Content-Type': 'application/json' },
    body:    JSON.stringify({ targetVersion }),
  });

  if (res.status === 200) return;

  const body = await res.json().catch(() => null);
  if (res.status === 400) throw { message: body?.detail ?? body?.message ?? 'Firmware update already in progress.', status: 400 } as ApiError;
  if (res.status === 404) throw { message: 'Device not found.', status: 404 } as ApiError;
  throw { message: body?.detail ?? body?.message ?? 'Failed to queue firmware update.', status: res.status } as ApiError;
}

export async function fetchSensorReadings(sensorId: string, limit?: number): Promise<DeviceReading[]> {
  const qs = new URLSearchParams();
  if (limit != null) qs.set('limit', String(limit));

  const res  = await apiFetch(`/api/v1/sensor/${sensorId}/readings/${qs.toString() ? `?${qs}` : ''}`);
  const body = await res.json().catch(() => null);

  if (res.status === 404) throw { message: 'Sensor not found.', status: 404 } as ApiError;
  if (!res.ok) {
    const message = body?.detail ?? body?.message ?? 'Failed to fetch sensor readings.';
    throw { message, status: res.status } as ApiError;
  }

  const raw   = body?.data ?? body;
  const items: any[] = Array.isArray(raw) ? raw : (raw?.results ?? raw?.readings ?? []);

  return items.map(r => mapDeviceReading(r, sensorId));
}

export async function deleteSensorDevice(deviceId: string): Promise<void> {
  const res = await apiFetch(`/api/v1/sensor/devices/${deviceId}/`, { method: 'DELETE' });

  if (res.status === 200 || res.status === 204) return;

  const body = await res.json().catch(() => null);
  if (res.status === 404) throw { message: 'Device not found.', status: 404 } as ApiError;
  throw { message: body?.detail ?? body?.message ?? 'Failed to remove device.', status: res.status } as ApiError;
}

export async function updateSensorDevice(deviceId: string, payload: UpdateDevicePayload): Promise<SensorRecord> {
  const body_payload: Record<string, any> = {};
  (['name', 'siteId', 'tankId', 'status', 'tankRangeStart', 'tankRangeEnd',
    'engineRangeStart', 'engineRangeEnd', 'sensorType', 'unit'] as const).forEach(k => {
    if (payload[k] !== undefined) body_payload[k] = payload[k];
  });

  const res  = await apiFetch(`/api/v1/sensor/devices/${deviceId}/`, {
    method:  'PUT',
    headers: { 'Content-Type': 'application/json' },
    body:    JSON.stringify(body_payload),
  });
  const body = await res.json().catch(() => null);

  if (res.status === 403) throw { message: 'Only an Owner or Admin with the manage_sensors permission can change devices.', status: 403 } as ApiError;
  if (res.status === 404) throw { message: 'Device not found.', status: 404 } as ApiError;
  if (res.status === 400) {
    // Alarm-level failures come back keyed under `errors`, e.g.
    // { reorderLevelPct: ["Must be greater than minimumLevelPct (18)…"] }.
    const fieldErrors = body?.errors ?? (body && typeof body === 'object' ? body : null);
    const msgs = fieldErrors && typeof fieldErrors === 'object'
      ? Object.values(fieldErrors).flat().join('; ')
      : null;
    throw {
      message: msgs ?? body?.detail ?? body?.message ?? 'Validation error.',
      status: 400,
      errors: body?.errors,
    } as ApiError & { errors?: Record<string, string[]> };
  }
  if (!res.ok) {
    throw { message: body?.detail ?? body?.message ?? 'Failed to update device.', status: res.status } as ApiError;
  }

  const raw = body?.data ?? body;
  return mapSensor(raw);
}

export async function fetchSensorDeviceById(deviceId: string): Promise<SensorRecord> {
  const res  = await apiFetch(`/api/v1/sensor/devices/${deviceId}/`);
  const body = await res.json().catch(() => null);

  if (res.status === 404) throw { message: 'Device not found.', status: 404 } as ApiError;
  if (!res.ok) {
    const message = body?.detail ?? body?.message ?? 'Failed to fetch device.';
    throw { message, status: res.status } as ApiError;
  }

  const raw = body?.data ?? body;
  return mapSensor(raw);
}

export async function fetchSensorDevices(params: SensorDevicesParams = {}): Promise<SensorDevicesResult> {
  const qs = new URLSearchParams();
  if (params.siteId  != null) qs.set('siteId',  params.siteId);
  if (params.status  != null) qs.set('status',  params.status);
  if (params.type    != null) qs.set('type',     params.type);
  if (params.search  != null) qs.set('search',  params.search);
  if (params.page    != null) qs.set('page',     String(params.page));
  if (params.limit   != null) qs.set('limit',    String(params.limit));

  const res  = await apiFetch(`/api/v1/sensor/devices/${qs.toString() ? `?${qs}` : ''}`);
  const body = await res.json().catch(() => null);

  if (!res.ok) {
    const message = body?.detail ?? body?.message ?? 'Failed to fetch sensor devices.';
    throw { message, status: res.status } as ApiError;
  }

  const raw   = body?.data ?? body;
  const items: any[] = Array.isArray(raw) ? raw : (raw?.results ?? []);

  return {
    count:    body?.total ?? raw?.count ?? items.length,
    next:     raw?.next     ?? null,
    previous: raw?.previous ?? null,
    results:  items.map(mapSensor),
  };
}

export async function fetchSensors(params: SensorsParams = {}): Promise<SensorsResult> {
  const qs = new URLSearchParams();
  if (params.isActive != null) qs.set('is_active', String(params.isActive));

  const res  = await apiFetch(`/api/v1/sensor/${qs.toString() ? `?${qs}` : ''}`);
  const body = await res.json().catch(() => null);

  if (!res.ok) {
    const message = body?.detail ?? body?.message ?? 'Failed to fetch sensors.';
    throw { message, status: res.status } as ApiError;
  }

  const raw   = body?.data ?? body;
  const items: any[] = Array.isArray(raw) ? raw : (raw?.results ?? []);

  return {
    count:   body?.total ?? raw?.count ?? items.length,
    results: items.map(mapSensor),
  };
}

export async function deleteSensor(sensorId: string): Promise<void> {
  const res = await apiFetch(`/api/v1/sensor/${sensorId}/delete/`, { method: 'DELETE' });

  if (res.status === 204) return;

  const body = await res.json().catch(() => null);
  if (res.status === 404) throw { message: 'Sensor not found.', status: 404 } as ApiError;
  throw { message: body?.detail ?? body?.message ?? 'Failed to delete sensor.', status: res.status } as ApiError;
}

export async function fetchSensorById(sensorId: string): Promise<SensorRecord> {
  const res  = await apiFetch(`/api/v1/sensor/${sensorId}/`);
  const body = await res.json().catch(() => null);

  if (res.status === 404) throw { message: 'Sensor not found.', status: 404 } as ApiError;
  if (!res.ok) {
    const message = body?.detail ?? body?.message ?? 'Failed to fetch sensor.';
    throw { message, status: res.status } as ApiError;
  }

  const raw = body?.data ?? body;
  return mapSensor(raw);
}

export interface UpdateSensorPayload {
  serialNumber:    string;
  name?:           string;
  sensorType?:     string;
  deviceType?:     SensorDeviceType | null;
  unit?:           string;
  tankStart?:      number | null;
  tankEnd?:        number | null;
  engineStart?:    number | null;
  engineEnd?:      number | null;
  isActive?:       boolean;
  status?:         SensorStatus;
  batteryPct?:     number | null;
  signalPct?:      number | null;
  firmwareVersion?: string | null;
  needsUpdate?:    boolean;
}

export async function updateSensor(sensorId: string, payload: UpdateSensorPayload): Promise<SensorRecord> {
  const body_payload: Record<string, any> = {
    serial_number: payload.serialNumber,
  };
  if (payload.name            !== undefined) body_payload.name             = payload.name;
  if (payload.sensorType      !== undefined) body_payload.sensor_type      = payload.sensorType;
  if (payload.deviceType      !== undefined) body_payload.device_type      = payload.deviceType;
  if (payload.unit            !== undefined) body_payload.unit             = payload.unit;
  if (payload.tankStart       !== undefined) body_payload.tank_start       = payload.tankStart;
  if (payload.tankEnd         !== undefined) body_payload.tank_end         = payload.tankEnd;
  if (payload.engineStart     !== undefined) body_payload.engine_start     = payload.engineStart;
  if (payload.engineEnd       !== undefined) body_payload.engine_end       = payload.engineEnd;
  if (payload.isActive        !== undefined) body_payload.is_active        = payload.isActive;
  if (payload.status          !== undefined) body_payload.status           = payload.status;
  if (payload.batteryPct      !== undefined) body_payload.battery_pct      = payload.batteryPct;
  if (payload.signalPct       !== undefined) body_payload.signal_pct       = payload.signalPct;
  if (payload.firmwareVersion !== undefined) body_payload.firmware_version = payload.firmwareVersion;
  if (payload.needsUpdate     !== undefined) body_payload.needs_update     = payload.needsUpdate;

  const res  = await apiFetch(`/api/v1/sensor/${sensorId}/update/`, {
    method:  'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body:    JSON.stringify(body_payload),
  });
  const body = await res.json().catch(() => null);

  if (res.status === 404) throw { message: 'Sensor not found.', status: 404 } as ApiError;
  if (res.status === 400) {
    // Alarm-level failures come back keyed under `errors`, e.g.
    // { reorderLevelPct: ["Must be greater than minimumLevelPct (18)…"] }.
    const fieldErrors = body?.errors ?? (body && typeof body === 'object' ? body : null);
    const msgs = fieldErrors && typeof fieldErrors === 'object'
      ? Object.values(fieldErrors).flat().join('; ')
      : null;
    throw {
      message: msgs ?? body?.detail ?? body?.message ?? 'Validation error.',
      status: 400,
      errors: body?.errors,
    } as ApiError & { errors?: Record<string, string[]> };
  }
  if (!res.ok) {
    throw { message: body?.detail ?? body?.message ?? 'Failed to update sensor.', status: res.status } as ApiError;
  }

  const raw = body?.data ?? body;
  return mapSensor(raw);
}

export interface RegisterSensorPayload {
  serialNumber:  string;
  name?:         string;
  sensorType?:   string;
  deviceType?:   SensorDeviceType;
  unit?:         string;
  tankStart?:    number;
  tankEnd?:      number;
  engineStart?:  number;
  engineEnd?:    number;
  isActive?:     boolean;
}

export async function registerSensor(payload: RegisterSensorPayload): Promise<SensorRecord> {
  const body_payload: Record<string, any> = {
    serial_number: payload.serialNumber,
  };
  if (payload.name         != null) body_payload.name          = payload.name;
  if (payload.sensorType   != null) body_payload.sensor_type   = payload.sensorType;
  if (payload.deviceType   != null) body_payload.device_type   = payload.deviceType;
  if (payload.unit         != null) body_payload.unit          = payload.unit;
  if (payload.tankStart    != null) body_payload.tank_start    = payload.tankStart;
  if (payload.tankEnd      != null) body_payload.tank_end      = payload.tankEnd;
  if (payload.engineStart  != null) body_payload.engine_start  = payload.engineStart;
  if (payload.engineEnd    != null) body_payload.engine_end    = payload.engineEnd;
  if (payload.isActive     != null) body_payload.is_active     = payload.isActive;

  const res  = await apiFetch('/api/v1/sensor/register/', {
    method:  'POST',
    headers: { 'Content-Type': 'application/json' },
    body:    JSON.stringify(body_payload),
  });
  const body = await res.json().catch(() => null);

  if (res.status === 400) {
    // Alarm-level failures come back keyed under `errors`, e.g.
    // { reorderLevelPct: ["Must be greater than minimumLevelPct (18)…"] }.
    const fieldErrors = body?.errors ?? (body && typeof body === 'object' ? body : null);
    const msgs = fieldErrors && typeof fieldErrors === 'object'
      ? Object.values(fieldErrors).flat().join('; ')
      : null;
    throw {
      message: msgs ?? body?.detail ?? body?.message ?? 'Validation error.',
      status: 400,
      errors: body?.errors,
    } as ApiError & { errors?: Record<string, string[]> };
  }
  if (!res.ok) {
    throw { message: body?.detail ?? body?.message ?? 'Failed to register sensor.', status: res.status } as ApiError;
  }

  const raw = body?.data ?? body;
  return mapSensor(raw);
}

/* ── Site tanks ── */
export async function fetchSiteTanks(siteId: string): Promise<import('../utils/mockData').TankData[]> {
  const res  = await apiFetch(`/api/v1/tank/sites/${siteId}/tanks/`);
  const body = await res.json().catch(() => null);

  if (res.status === 404) throw { message: 'Site not found.', status: 404 } as ApiError;
  if (!res.ok) {
    const message = body?.detail ?? body?.message ?? 'Failed to fetch site tanks.';
    throw { message, status: res.status } as ApiError;
  }

  const raw   = body?.data ?? body;
  const items: any[] = Array.isArray(raw) ? raw : (raw?.results ?? raw?.tanks ?? []);

  return items.map(mapTank);
}


/* ── Section B — Reorder-level alarms ────────────────────────────────────
   Four levels per tank, read and written on the ordinary tank endpoints;
   there is no separate thresholds resource. Each is a decimal 0–100, and they
   must STRICTLY increase: critical < minimum < reorder < overfill.

   The server enforces this and returns 400 with the offending field keyed in
   `errors`, but validate client-side too so the user finds out before
   submitting — and validate against the tank's *current* other levels, not the
   defaults, since a PUT may carry only a subset. */
export interface AlarmLevels {
  criticalLevelPct?: number;
  minimumLevelPct?:  number;
  reorderLevelPct?:  number;
  overfillLevelPct?: number;
}

/** Defaults the API applies when a level is omitted on create. */
export const ALARM_LEVEL_DEFAULTS: Required<AlarmLevels> = {
  criticalLevelPct: 10,
  minimumLevelPct:  15,
  reorderLevelPct:  30,
  overfillLevelPct: 95,
};

export const ALARM_LEVEL_ORDER: (keyof AlarmLevels)[] = [
  'criticalLevelPct', 'minimumLevelPct', 'reorderLevelPct', 'overfillLevelPct',
];

export const ALARM_LEVEL_LABELS: Record<keyof AlarmLevels, string> = {
  criticalLevelPct: 'Critical',
  minimumLevelPct:  'Minimum',
  reorderLevelPct:  'Reorder',
  overfillLevelPct: 'Maximum / overfill',
};

/**
 * Validates a proposed set of levels against the tank's current ones.
 * `proposed` may be partial — omitted levels fall back to `current`, then to
 * the API defaults, exactly as the server resolves them.
 * Returns a field-keyed error map, empty when valid.
 */
export function validateAlarmLevels(
  proposed: AlarmLevels,
  current: AlarmLevels = {},
): Partial<Record<keyof AlarmLevels, string>> {
  const errors: Partial<Record<keyof AlarmLevels, string>> = {};

  const resolved = ALARM_LEVEL_ORDER.reduce((acc, key) => {
    acc[key] = proposed[key] ?? current[key] ?? ALARM_LEVEL_DEFAULTS[key];
    return acc;
  }, {} as Required<AlarmLevels>);

  // Range first — an out-of-range number makes the ordering message confusing.
  for (const key of ALARM_LEVEL_ORDER) {
    const value = resolved[key];
    if (Number.isNaN(value))        errors[key] = `${ALARM_LEVEL_LABELS[key]} must be a number.`;
    else if (value < 0 || value > 100) errors[key] = `${ALARM_LEVEL_LABELS[key]} must be between 0 and 100.`;
  }
  if (Object.keys(errors).length > 0) return errors;

  // Then the strictly-increasing chain, reported on the field that breaks it.
  for (let i = 1; i < ALARM_LEVEL_ORDER.length; i++) {
    const key  = ALARM_LEVEL_ORDER[i];
    const prev = ALARM_LEVEL_ORDER[i - 1];
    if (resolved[key] <= resolved[prev]) {
      errors[key] =
        `Must be greater than ${ALARM_LEVEL_LABELS[prev]} (${resolved[prev]}). ` +
        'Levels must increase: critical < minimum < reorder < overfill.';
    }
  }
  return errors;
}

/** Maps a low-level alert back to the band that raised it (Section B). */
export function alarmLevelForAlert(
  type: string,
  severity: string | undefined,
): keyof AlarmLevels | undefined {
  if (type === 'overfill') return 'overfillLevelPct';
  if (type !== 'low_fuel') return undefined;
  // The three low bands all arrive as low_fuel, separated by severity.
  if (severity === 'critical') return 'criticalLevelPct';
  if (severity === 'high')     return 'minimumLevelPct';
  if (severity === 'medium')   return 'reorderLevelPct';
  return undefined;
}

export interface CreateTankPayload extends AlarmLevels {
  name:     string;
  capacity: string;
  fuelType: 'diesel' | 'petrol' | 'other';
  sensors?: {
    levelSensor?:   string | null;
    qualitySensor?: string | null;
    flowMeter?:     string | null;
  };
  alertsEnabled?: boolean;
}

export async function createSiteTank(
  siteId: string,
  payload: CreateTankPayload,
): Promise<import('../utils/mockData').TankData> {
  const body_payload: Record<string, any> = {
    name:     payload.name,
    capacity: payload.capacity,
    fuelType: payload.fuelType,
  };
  if (payload.sensors !== undefined) body_payload.sensors = payload.sensors;
  if (payload.alertsEnabled !== undefined) body_payload.alertsEnabled = payload.alertsEnabled;
  // Omit any level and the API's own default applies (10/15/30/95).
  for (const key of ALARM_LEVEL_ORDER) {
    if (payload[key] !== undefined) body_payload[key] = payload[key];
  }

  const res  = await apiFetch(`/api/v1/tank/sites/${siteId}/tanks/`, {
    method:  'POST',
    headers: { 'Content-Type': 'application/json' },
    body:    JSON.stringify(body_payload),
  });
  const body = await res.json().catch(() => null);

  if (res.status === 400) {
    // Alarm-level failures come back keyed under `errors`, e.g.
    // { reorderLevelPct: ["Must be greater than minimumLevelPct (18)…"] }.
    const fieldErrors = body?.errors ?? (body && typeof body === 'object' ? body : null);
    const msgs = fieldErrors && typeof fieldErrors === 'object'
      ? Object.values(fieldErrors).flat().join('; ')
      : null;
    throw {
      message: msgs ?? body?.detail ?? body?.message ?? 'Validation error.',
      status: 400,
      errors: body?.errors,
    } as ApiError & { errors?: Record<string, string[]> };
  }
  if (res.status === 404) throw { message: 'Site not found.', status: 404 } as ApiError;
  if (!res.ok) {
    throw { message: body?.detail ?? body?.message ?? 'Failed to create tank.', status: res.status } as ApiError;
  }

  const raw = body?.data ?? body;
  return mapTank(raw);
}

export interface UpdateTankPayload extends AlarmLevels {
  name?:     string;
  capacity?: string;
  fuelType?: 'diesel' | 'petrol' | 'other';
  sensors?: {
    levelSensor?:   string | null;
    qualitySensor?: string | null;
    flowMeter?:     string | null;
  };
  alertsEnabled?: boolean;
}

export async function updateTank(tankId: string, payload: UpdateTankPayload): Promise<import('../utils/mockData').TankData> {
  const body_payload: Record<string, any> = {};
  if (payload.name     !== undefined) body_payload.name     = payload.name;
  if (payload.capacity !== undefined) body_payload.capacity = payload.capacity;
  if (payload.fuelType !== undefined) body_payload.fuelType = payload.fuelType;
  if (payload.sensors  !== undefined) body_payload.sensors  = payload.sensors;
  if (payload.alertsEnabled !== undefined) body_payload.alertsEnabled = payload.alertsEnabled;
  // Any subset is allowed; omitted levels keep their current value server-side.
  for (const key of ALARM_LEVEL_ORDER) {
    if (payload[key] !== undefined) body_payload[key] = payload[key];
  }

  const res  = await apiFetch(`/api/v1/tank/tanks/${tankId}/`, {
    method:  'PUT',
    headers: { 'Content-Type': 'application/json' },
    body:    JSON.stringify(body_payload),
  });
  const body = await res.json().catch(() => null);

  if (res.status === 404) throw { message: 'Tank not found.', status: 404 } as ApiError;
  if (res.status === 400) {
    // Alarm-level failures come back keyed under `errors`, e.g.
    // { reorderLevelPct: ["Must be greater than minimumLevelPct (18)…"] }.
    const fieldErrors = body?.errors ?? (body && typeof body === 'object' ? body : null);
    const msgs = fieldErrors && typeof fieldErrors === 'object'
      ? Object.values(fieldErrors).flat().join('; ')
      : null;
    throw {
      message: msgs ?? body?.detail ?? body?.message ?? 'Validation error.',
      status: 400,
      errors: body?.errors,
    } as ApiError & { errors?: Record<string, string[]> };
  }
  if (!res.ok) {
    throw { message: body?.detail ?? body?.message ?? 'Failed to update tank.', status: res.status } as ApiError;
  }

  const raw = body?.data ?? body;
  return mapTank(raw);
}

export interface TankHistoryParams {
  hours?: number;
  from?:  string;
  to?:    string;
}

export async function fetchTankHistory(tankId: string, params: TankHistoryParams = {}): Promise<FuelLevelPoint[]> {
  const qs = new URLSearchParams();
  if (params.hours != null) qs.set('hours', String(params.hours));
  if (params.from  != null) qs.set('from',  params.from);
  if (params.to    != null) qs.set('to',    params.to);

  const res  = await apiFetch(`/api/v1/tank/tanks/${tankId}/history/${qs.toString() ? `?${qs}` : ''}`);
  const body = await res.json().catch(() => null);

  if (res.status === 404) throw { message: 'Tank not found.', status: 404 } as ApiError;
  if (!res.ok) {
    const message = body?.detail ?? body?.message ?? 'Failed to fetch tank history.';
    throw { message, status: res.status } as ApiError;
  }

  const raw   = body?.data ?? body?.results ?? body;
  const items: any[] = Array.isArray(raw) ? raw : [];

  return items.map(item => ({
    timestamp: item.timestamp ?? item.time ?? '',
    level:     Number(item.level ?? item.fuel_level ?? item.level_percentage ?? 0),
    volume:    Number(item.volume ?? item.current_volume ?? 0),
    capacity:  Number(item.capacity ?? item.tank_capacity ?? 0),
    eventType: (item.eventType ?? item.event_type) as FuelLevelEventType | undefined,
  }));
}

export interface TankQualityPoint {
  id:           string;
  timestamp:    string;
  status:       QualityReportStatus;
  waterContent?: number;
  sediment?:    string;
  temperature?: number;
  notes?:       string;
  inspector?:   string;
}

export async function fetchTankQuality(
  tankId: string,
  params: { from?: string; to?: string } = {},
): Promise<TankQualityPoint[]> {
  const qs = new URLSearchParams();
  if (params.from != null) qs.set('from', params.from);
  if (params.to   != null) qs.set('to',   params.to);

  const res  = await apiFetch(`/api/v1/tank/tanks/${tankId}/quality/${qs.toString() ? `?${qs}` : ''}`);
  const body = await res.json().catch(() => null);

  if (res.status === 404) throw { message: 'Tank not found.', status: 404 } as ApiError;
  if (!res.ok) {
    const message = body?.detail ?? body?.message ?? 'Failed to fetch tank quality data.';
    throw { message, status: res.status } as ApiError;
  }

  const raw   = body?.data ?? body?.results ?? body;
  const items: any[] = Array.isArray(raw) ? raw : [];

  return items.map((q: any) => ({
    id:           String(q.id             ?? ''),
    timestamp:    q.inspectedAt           ?? q.inspected_at  ?? q.timestamp ?? q.created_at ?? '',
    status:       (q.status               ?? 'good') as QualityReportStatus,
    waterContent: q.waterContent          != null ? Number(q.waterContent)  :
                  q.water_content         != null ? Number(q.water_content) : undefined,
    sediment:     q.sediment              ?? undefined,
    temperature:  q.temperature           != null ? Number(q.temperature)   : undefined,
    notes:        q.notes                 ?? undefined,
    inspector:    q.inspector             ?? q.inspected_by  ?? undefined,
  }));
}

/* ── Tank volume — live-chart primitive: current volume, series, and rate in one call ── */
export interface TankVolumeParams {
  interval?:        'raw' | 'second' | 'minute' | 'hour' | 'day' | 'week' | 'month';
  from?:             string;   // ISO 8601
  to?:               string;   // ISO 8601
  hours?:            number;   // window length, ignored if from/to given
  minutes?:          number;   // window length, takes precedence over hours
  // Live-polling path: pass the timestamp of your last point to get back only
  // what's new (usually nothing). Do not re-fetch the whole window on a timer.
  since?:            string;
  limit?:            number;   // capped at 5000 server-side
  rateWindowHours?:  number;   // window for the consumption-rate calculation, default 6
}

export interface TankVolumePoint {
  timestamp:    string;
  volumeLitres: number;
  percent:      number;
  /** Ties this point to an exact movement row — match on this, not timestamps. */
  readingId?:   string;
}

/** Alarm bands in litres, ready to draw as chart reference lines with no second call. */
export interface TankVolumeThresholds {
  criticalLitres?: number;
  minimumLitres?:  number;
  reorderLitres?:  number;
  overfillLitres?: number;
}

/** Always present in the response — the current reading, independent of the queried window. */
export interface TankVolumeLatest {
  timestamp:    string;
  volumeLitres: number;
  percent:      number;
  ageSeconds:   number;
  // A quiet sensor keeps returning its last reading forever — this is what
  // distinguishes "still live" from "stopped reporting". Grey the line, don't extend it.
  stale:        boolean;
}

export interface TankVolumeRate {
  litresPerHour: number;
  windowHours:   number;
}

export interface TankVolumeResult {
  points:     TankVolumePoint[];
  latest:     TankVolumeLatest | null;
  rate:       TankVolumeRate | null;
  capacity:   number | null;
  thresholds: TankVolumeThresholds | null;
}

function mapVolumePoint(item: any): TankVolumePoint {
  return {
    timestamp:    item.timestamp ?? item.time ?? '',
    volumeLitres: numOf(item.volumeLitres ?? item.volume_litres ?? item.volume ?? item.litres),
    percent:      numOf(item.percent ?? item.pct ?? item.level ?? item.level_percentage),
    readingId:    item.readingId ?? item.reading_id ?? undefined,
  };
}

export async function fetchTankVolume(tankId: string, params: TankVolumeParams = {}): Promise<TankVolumeResult> {
  const qs = new URLSearchParams();
  if (params.interval        != null) qs.set('interval',        params.interval);
  if (params.from            != null) qs.set('from',            params.from);
  if (params.to              != null) qs.set('to',              params.to);
  if (params.hours           != null) qs.set('hours',           String(params.hours));
  if (params.minutes         != null) qs.set('minutes',         String(params.minutes));
  if (params.since           != null) qs.set('since',           params.since);
  if (params.limit           != null) qs.set('limit',           String(params.limit));
  if (params.rateWindowHours != null) qs.set('rateWindowHours', String(params.rateWindowHours));

  const res  = await apiFetch(`/api/v1/tank/tanks/${tankId}/volume/${qs.toString() ? `?${qs}` : ''}`);
  const body = await res.json().catch(() => null);

  if (res.status === 404) throw { message: 'Tank not found.', status: 404 } as ApiError;
  if (res.status === 400) throw { message: body?.detail ?? body?.message ?? 'Unknown interval.', status: 400 } as ApiError;
  if (!res.ok) {
    const message = body?.detail ?? body?.message ?? 'Failed to fetch tank volume.';
    throw { message, status: res.status } as ApiError;
  }

  const items: any[] = Array.isArray(body?.data)    ? body.data
                      : Array.isArray(body?.series)  ? body.series
                      : Array.isArray(body?.results) ? body.results
                      : Array.isArray(body)          ? body
                      : [];
  const points = items.map(mapVolumePoint);

  const latestRaw = body?.latest ?? null;
  const latest: TankVolumeLatest | null = latestRaw ? {
    timestamp:    latestRaw.timestamp  ?? latestRaw.time ?? '',
    volumeLitres: numOf(latestRaw.volumeLitres ?? latestRaw.volume_litres ?? latestRaw.volume ?? latestRaw.litres),
    percent:      numOf(latestRaw.percent      ?? latestRaw.pct ?? latestRaw.level ?? latestRaw.level_percentage),
    ageSeconds:   numOf(latestRaw.ageSeconds   ?? latestRaw.age_seconds),
    stale:        Boolean(latestRaw.stale),
  } : null;

  const rateRaw = body?.rate ?? null;
  const rate: TankVolumeRate | null = rateRaw ? {
    litresPerHour: numOf(rateRaw.litresPerHour ?? rateRaw.litres_per_hour ?? rateRaw.value),
    windowHours:   numOf(rateRaw.windowHours   ?? rateRaw.window_hours ?? params.rateWindowHours ?? 6),
  } : null;

  const thresholdsRaw = body?.thresholds ?? null;
  const thresholds: TankVolumeThresholds | null = thresholdsRaw ? {
    criticalLitres: numOrUndef(thresholdsRaw.criticalLitres ?? thresholdsRaw.critical_litres),
    minimumLitres:  numOrUndef(thresholdsRaw.minimumLitres  ?? thresholdsRaw.minimum_litres),
    reorderLitres:  numOrUndef(thresholdsRaw.reorderLitres  ?? thresholdsRaw.reorder_litres),
    overfillLitres: numOrUndef(thresholdsRaw.overfillLitres ?? thresholdsRaw.overfill_litres),
  } : null;

  return {
    points,
    latest,
    rate,
    capacity: body?.capacity != null ? numOf(body.capacity) : null,
    thresholds,
  };
}

export async function deleteTank(tankId: string): Promise<void> {
  const res = await apiFetch(`/api/v1/tank/tanks/${tankId}/`, { method: 'DELETE' });

  if (res.status === 200 || res.status === 204) return;

  const body = await res.json().catch(() => null);
  if (res.status === 404) throw { message: 'Tank not found.', status: 404 } as ApiError;
  throw { message: body?.detail ?? body?.message ?? 'Failed to delete tank.', status: res.status } as ApiError;
}

export async function fetchTankById(tankId: string): Promise<import('../utils/mockData').TankData> {
  const res  = await apiFetch(`/api/v1/tank/tanks/${tankId}/`);
  const body = await res.json().catch(() => null);

  if (res.status === 404) throw { message: 'Tank not found.', status: 404 } as ApiError;
  if (!res.ok) {
    const message = body?.detail ?? body?.message ?? 'Failed to fetch tank.';
    throw { message, status: res.status } as ApiError;
  }

  const raw = body?.data ?? body;
  return mapTank(raw);
}

/* ── Tank quality reports ── */
export type QualityReportStatus = 'good' | 'degraded' | 'contaminated';

export interface QualityReport {
  id:           string;
  siteId:       string;
  siteName:     string;
  tankId:       string;
  tankName:     string;
  status:       QualityReportStatus;
  inspectedAt:  string;
  waterContent?: number;
  sediment?:    string;
  temperature?: number;
  notes?:       string;
  inspector?:   string;
}

export interface QualityReportsParams {
  siteId?: string;
  status?: QualityReportStatus;
  from?:   string;
  to?:     string;
  page?:   number;
  limit?:  number;
}

export interface QualityReportsResult {
  count:    number;
  next:     string | null;
  previous: string | null;
  results:  QualityReport[];
}

export async function fetchQualityReports(params: QualityReportsParams = {}): Promise<QualityReportsResult> {
  const qs = new URLSearchParams();
  if (params.siteId != null) qs.set('siteId',  params.siteId);
  if (params.status != null) qs.set('status',  params.status);
  if (params.from   != null) qs.set('from',    params.from);
  if (params.to     != null) qs.set('to',      params.to);
  if (params.page   != null) qs.set('page',    String(params.page));
  if (params.limit  != null) qs.set('limit',   String(params.limit));

  const res  = await apiFetch(`/api/v1/tank/quality/reports/${qs.toString() ? `?${qs}` : ''}`);
  const body = await res.json().catch(() => null);

  if (!res.ok) {
    const message = body?.detail ?? body?.message ?? 'Failed to fetch quality reports.';
    throw { message, status: res.status } as ApiError;
  }

  const raw   = body?.data ?? body;
  const items: any[] = Array.isArray(raw) ? raw : (raw?.results ?? []);

  return {
    count:    body?.total ?? raw?.count ?? items.length,
    next:     raw?.next     ?? null,
    previous: raw?.previous ?? null,
    results:  items.map((r: any) => ({
      id:           String(r.id              ?? ''),
      siteId:       String(r.siteId          ?? r.site_id          ?? r.site?.id    ?? ''),
      siteName:     r.siteName               ?? r.site_name        ?? r.site?.name  ?? '',
      tankId:       String(r.tankId          ?? r.tank_id          ?? r.tank?.id    ?? ''),
      tankName:     r.tankName               ?? r.tank_name        ?? r.tank?.name  ?? '',
      status:       (r.status                ?? 'good') as QualityReportStatus,
      inspectedAt:  r.inspectedAt            ?? r.inspected_at     ?? r.created_at  ?? '',
      waterContent: r.waterContent           != null ? Number(r.waterContent)  :
                    r.water_content          != null ? Number(r.water_content) : undefined,
      sediment:     r.sediment               ?? undefined,
      temperature:  r.temperature            != null ? Number(r.temperature)   : undefined,
      notes:        r.notes                  ?? undefined,
      inspector:    r.inspector              ?? r.inspected_by     ?? undefined,
    })),
  };
}

export interface CreateQualityReportPayload {
  tankId:       string;
  waterContent: string;
  sediment:     'normal' | 'high';
  temperature:  string;
  notes?:       string;
}

export async function createQualityReport(payload: CreateQualityReportPayload): Promise<QualityReport> {
  const body_payload: Record<string, any> = {
    tankId:       payload.tankId,
    waterContent: payload.waterContent,
    sediment:     payload.sediment,
    temperature:  payload.temperature,
  };
  if (payload.notes !== undefined) body_payload.notes = payload.notes;

  const res  = await apiFetch('/api/v1/tank/quality/reports/', {
    method:  'POST',
    headers: { 'Content-Type': 'application/json' },
    body:    JSON.stringify(body_payload),
  });
  const body = await res.json().catch(() => null);

  if (res.status === 400) {
    // Alarm-level failures come back keyed under `errors`, e.g.
    // { reorderLevelPct: ["Must be greater than minimumLevelPct (18)…"] }.
    const fieldErrors = body?.errors ?? (body && typeof body === 'object' ? body : null);
    const msgs = fieldErrors && typeof fieldErrors === 'object'
      ? Object.values(fieldErrors).flat().join('; ')
      : null;
    throw {
      message: msgs ?? body?.detail ?? body?.message ?? 'Validation error.',
      status: 400,
      errors: body?.errors,
    } as ApiError & { errors?: Record<string, string[]> };
  }
  if (!res.ok) {
    throw { message: body?.detail ?? body?.message ?? 'Failed to submit quality report.', status: res.status } as ApiError;
  }

  const r = body?.data ?? body;
  return {
    id:           String(r.id              ?? ''),
    siteId:       String(r.siteId          ?? r.site_id          ?? r.site?.id    ?? ''),
    siteName:     r.siteName               ?? r.site_name        ?? r.site?.name  ?? '',
    tankId:       String(r.tankId          ?? r.tank_id          ?? r.tank?.id    ?? payload.tankId),
    tankName:     r.tankName               ?? r.tank_name        ?? r.tank?.name  ?? '',
    status:       (r.status                ?? 'good') as QualityReportStatus,
    inspectedAt:  r.inspectedAt            ?? r.inspected_at     ?? r.created_at  ?? new Date().toISOString(),
    waterContent: r.waterContent           != null ? Number(r.waterContent)  :
                  r.water_content          != null ? Number(r.water_content) : Number(payload.waterContent),
    sediment:     r.sediment               ?? payload.sediment,
    temperature:  r.temperature            != null ? Number(r.temperature)   : Number(payload.temperature),
    notes:        r.notes                  ?? payload.notes,
    inspector:    r.inspector              ?? r.inspected_by     ?? undefined,
  };
}

/* ── Two-step MFA login: POST /api/auth/login/ then /api/auth/login/verify/ ──
   Neither response body is declared in the backend's OpenAPI schema (confirmed via
   /redoc/?format=openapi — only the request shapes and status-code descriptions are
   there), so both are parsed defensively. Confirmed empirically against the live API:
   both endpoints live under /api (proxied in dev same as everything else), error
   shape is flat {status, message} (not the {data: ...} envelope used elsewhere), and
   an expired/invalid pendingToken comes back as 401 with pendingTokenInvalid: true. */
export interface MfaLoginStart {
  pendingToken: string;
  email: string;
  /** How many more codes may be sent / tried before the account is locked out. */
  trialsRemaining?: number;
  attemptsRemaining?: number;
}

export interface MfaVerifyError extends ApiError {
  pendingTokenInvalid?: boolean;
  trialsRemaining?: number;
  attemptsRemaining?: number;
}

// Step 1 — check the password; the server emails a one-time code and returns a
// pendingToken that must be paired with that code in step 2. Never returns tokens.
export async function requestLoginCode(email: string, password: string): Promise<MfaLoginStart> {
  const res  = await guardedFetch(`${BASE_URL}/api/auth/login/`, {
    method:  'POST',
    headers: { 'Content-Type': 'application/json' },
    body:    JSON.stringify({ email, password }),
  });
  const body = await res.json().catch(() => null);

  if (!res.ok) {
    throw toApiError(res.status, body,
      res.status === 403 ? 'Account inactive, or email not verified.' : 'Invalid email or password.');
  }

  const pendingToken = body?.pendingToken ?? body?.data?.pendingToken;
  if (!pendingToken) {
    throw { message: 'Unexpected response starting sign-in. Please try again.', status: 500 } as ApiError;
  }
  return {
    pendingToken,
    email: body?.email ?? email,
    trialsRemaining:   numOrUndef(body?.trialsRemaining),
    attemptsRemaining: numOrUndef(body?.attemptsRemaining),
  };
}

// Step 2 — consume the emailed code together with the pendingToken from step 1 to
// issue the real token pair. The code alone can't complete a login.
export async function verifyLoginCode(pendingToken: string, code: string): Promise<LoginResult> {
  const res  = await guardedFetch(`${BASE_URL}/api/auth/login/verify/`, {
    method:  'POST',
    headers: { 'Content-Type': 'application/json' },
    body:    JSON.stringify({ pendingToken, code }),
  });
  const body = await res.json().catch(() => null);

  if (!res.ok) {
    // 400 = wrong code (try again); 401 = the pendingToken is missing or expired, so the
    // whole sign-in has to restart from the password step (guide §3.1).
    throw {
      ...toApiError(res.status, body, 'Invalid or expired code.'),
      pendingTokenInvalid: res.status === 401 || !!body?.pendingTokenInvalid,
      trialsRemaining:     numOrUndef(body?.trialsRemaining),
      attemptsRemaining:   numOrUndef(body?.attemptsRemaining),
    } as MfaVerifyError;
  }

  const access  = body?.access  ?? body?.token;
  const refresh = body?.refresh ?? body?.refreshToken;
  if (!access || !refresh) {
    throw { message: 'Sign-in succeeded but no session token was returned.', status: 500 } as ApiError;
  }
  const tokens: AuthTokens = { access, refresh };
  tokenStorage.save(tokens);

  return { tokens, user: body?.data ?? body?.user ?? {} };
}

/* ── Two-step signup: POST /api/auth/signup/ then /api/auth/verify-email/ ──
   Same undocumented-response-body situation as the MFA login endpoints above —
   confirmed empirically: 400 validation errors come back as
   { status, message, errors: { field: [msg, ...] } } (nested under "errors", not
   flat like most other endpoints in this API), and a bad/expired verify code as
   { status, message, trialsRemaining, attemptsRemaining }. */
export interface SignupPayload {
  username: string;
  email: string;
  password: string;
  confirm_password: string;
  company_name: string;
  industry_type: string;
  company_registration_number?: string;
  company_address?: string;
  country?: string;
  state_province?: string;
  phone_number?: string;
  website?: string;
}

function flattenFieldErrors(errors: unknown): string | null {
  if (!errors || typeof errors !== 'object') return null;
  const msgs = Object.values(errors as Record<string, unknown>).flat().filter(Boolean);
  return msgs.length > 0 ? msgs.join(' ') : null;
}

// Step 1 — creates the account and emails a 6-digit code. Returns no tokens; the
// account can't log in until verifySignupEmail() succeeds.
export async function signup(payload: SignupPayload): Promise<{ email: string }> {
  const res  = await guardedFetch(`${BASE_URL}/api/auth/signup/`, {
    method:  'POST',
    headers: { 'Content-Type': 'application/json' },
    body:    JSON.stringify(payload),
  });
  const body = await res.json().catch(() => null);

  if (!res.ok) {
    throw toApiError(res.status, body, 'Registration failed.');
  }
  return { email: body?.email ?? payload.email };
}

export interface VerifySignupEmailError extends ApiError {
  trialsRemaining?: number;
  attemptsRemaining?: number;
}

// Step 2 — consumes the emailed code and marks the address verified. The API also
// issues a token pair here, but the signup flow sends the user to log in fresh
// afterward (through the MFA login flow) rather than silently adopting them.
export async function verifySignupEmail(email: string, code: string): Promise<void> {
  const res = await guardedFetch(`${BASE_URL}/api/auth/verify-email/`, {
    method:  'POST',
    headers: { 'Content-Type': 'application/json' },
    body:    JSON.stringify({ email, code }),
  });

  if (res.ok) return;

  const body = await res.json().catch(() => null);
  throw {
    message:            body?.message ?? body?.detail ?? 'Invalid or expired code.',
    status:             res.status,
    trialsRemaining:    body?.trialsRemaining,
    attemptsRemaining:  body?.attemptsRemaining,
  } as VerifySignupEmailError;
}

/* ── Password reset: POST /api/auth/password/forgot/ then /api/auth/password/reset/ ──
   /forgot/ deliberately returns an identical 200 whether or not the address has an
   account (anti-enumeration — confirmed empirically), so a genuine 200 here says
   nothing about whether the email exists; only real errors (malformed input, network
   failure) are surfaced. /reset/ shares the nested { errors: { field: [msg] } } 400
   shape and trialsRemaining/attemptsRemaining pattern already seen on signup/login
   verify. Its description doesn't mention issuing tokens (only marking the address
   verified and revoking every outstanding session), so it's treated as fire-and-forget
   here, same as acknowledgeAlert/resolveAlert. */
export async function requestPasswordReset(email: string): Promise<void> {
  const res = await guardedFetch(`${BASE_URL}/api/auth/password/forgot/`, {
    method:  'POST',
    headers: { 'Content-Type': 'application/json' },
    body:    JSON.stringify({ email }),
  });
  if (res.ok) return;

  const body = await res.json().catch(() => null);
  throw { message: body?.message ?? body?.detail ?? 'Failed to send reset code.', status: res.status } as ApiError;
}

export interface PasswordResetError extends ApiError {
  trialsRemaining?: number;
  attemptsRemaining?: number;
}

export async function resetPassword(
  email: string, code: string, password: string, confirmPassword: string,
): Promise<void> {
  const res = await guardedFetch(`${BASE_URL}/api/auth/password/reset/`, {
    method:  'POST',
    headers: { 'Content-Type': 'application/json' },
    body:    JSON.stringify({ email, code, password, confirmPassword }),
  });

  if (res.ok) return;

  const body = await res.json().catch(() => null);
  throw {
    message:            flattenFieldErrors(body?.errors) ?? body?.message ?? body?.detail ?? 'Failed to reset password.',
    status:             res.status,
    trialsRemaining:    body?.trialsRemaining,
    attemptsRemaining:  body?.attemptsRemaining,
  } as PasswordResetError;
}

/* ── Shared resend endpoint for all three code-based flows above ──
   Confirmed empirically: same anti-enumeration 200 as /password/forgot/ (identical
   response whether or not the address has a pending code), and a 429 with a cooldown
   message if called twice within 60s — the description explicitly documents that
   window, so the frontend doesn't need to track it itself, just surface the message. */
export type ResendCodePurpose = 'signup' | 'login' | 'password_reset';

export async function resendCode(email: string, purpose: ResendCodePurpose): Promise<void> {
  const res = await guardedFetch(`${BASE_URL}/api/auth/resend-code/`, {
    method:  'POST',
    headers: { 'Content-Type': 'application/json' },
    body:    JSON.stringify({ email, purpose }),
  });
  if (res.ok) return;

  const body = await res.json().catch(() => null);
  // 429 inside the 60-second window carries retryAfter, so the button can count down.
  throw toApiError(res.status, body,
    res.status === 429 ? 'Please wait before requesting another code.' : 'Failed to resend code.');
}

/* ── Self-service password change (signed-in user) ──
   Both live under /api and require the standard Bearer auth (apiFetch) — unlike
   forgot/reset above, which are for a logged-out user proving control of an inbox,
   this is for someone already signed in changing their own password. The change
   endpoint's description says it "revokes every outstanding session" with no carve-out
   for the current one, so a successful change must be treated as an implicit logout —
   the caller is responsible for clearing local session state afterward. */
export async function requestPasswordChangeCode(): Promise<void> {
  const res = await apiFetch('/api/auth/password/change/request/', { method: 'POST' });
  if (res.ok) return;

  const body = await res.json().catch(() => null);
  throw { message: body?.message ?? body?.detail ?? 'Failed to send code.', status: res.status } as ApiError;
}

export interface PasswordChangeError extends ApiError {
  trialsRemaining?: number;
  attemptsRemaining?: number;
}

export async function changePassword(
  code: string, currentPassword: string, password: string, confirmPassword: string,
): Promise<void> {
  const res = await apiFetch('/api/auth/password/change/', {
    method: 'POST',
    body:   JSON.stringify({ code, currentPassword, password, confirmPassword }),
  });

  if (res.ok) return;

  const body = await res.json().catch(() => null);
  throw {
    message:            flattenFieldErrors(body?.errors) ?? body?.message ?? body?.detail ?? 'Failed to change password.',
    status:             res.status,
    trialsRemaining:    body?.trialsRemaining,
    attemptsRemaining:  body?.attemptsRemaining,
    errors:             body?.errors,
  } as PasswordChangeError;
}

/* ── Microsoft Azure AD SSO login ── */
export async function loginWithAzure(azureProfile: {
  username: string;   // Azure UPN, e.g. john.doe@company.onmicrosoft.com
  name:     string;   // Display name from Microsoft
  email:    string;   // Contact email collected from the user
}): Promise<LoginResult> {
  const res = await guardedFetch(`${BASE_URL}/api/v1/onboard/auth/azure/`, {
    method:  'POST',
    headers: { 'Content-Type': 'application/json' },
    body:    JSON.stringify({
      username: azureProfile.username,
      name:     azureProfile.name,
      email:    azureProfile.email,
    }),
  });

  let body: LoginApiResponse | undefined;
  try { body = await res.json(); } catch { /* non-JSON */ }

  if (!res.ok) {
    const b = body as any;
    const message =
      b?.detail ??
      b?.non_field_errors?.[0] ??
      b?.message ??
      (res.status === 403 ? 'Account inactive.' : 'Microsoft sign-in failed.');
    throw { message, status: res.status } as ApiError;
  }

  const azureTokens: AuthTokens = {
    access:  body!.token,
    refresh: body!.refreshToken,
  };
  tokenStorage.save(azureTokens);

  return { tokens: azureTokens, user: body!.data };
}

/* ── Google OAuth login ── */
export async function loginWithGoogle(googleProfile: {
  email:   string;
  name:    string;
  picture?: string;
}): Promise<LoginResult> {
  const res = await guardedFetch(`${BASE_URL}/api/v1/onboard/auth/google/`, {
    method:  'POST',
    headers: { 'Content-Type': 'application/json' },
    body:    JSON.stringify({
      email:   googleProfile.email,
      name:    googleProfile.name,
      picture: googleProfile.picture ?? '',
    }),
  });

  let body: LoginApiResponse | undefined;
  try { body = await res.json(); } catch { /* non-JSON */ }

  if (!res.ok) {
    const b = body as any;
    const message =
      b?.detail ??
      b?.non_field_errors?.[0] ??
      b?.message ??
      (res.status === 403 ? 'Account inactive.' : 'Google sign-in failed.');
    throw { message, status: res.status } as ApiError;
  }

  const tokens: AuthTokens = {
    access:  body!.token,
    refresh: body!.refreshToken,
  };
  tokenStorage.save(tokens);

  return { tokens, user: body!.data };
}

/* ── Activity Logs ───────────────────────────────────────────────────────
   GET /api/reports/logs/                          — the whole chronological stream
   GET /api/reports/logs/{target_type}/{target_id}/ — one object's full history

   Both are the same stream; the second is pre-filtered to a single site, tank,
   sensor or tanker and defaults to a 30-day window rather than 24 hours, because
   it is an investigation view rather than a live feed.

   Requires view_analytics. Company-scoped server-side: another company's record
   returns 404, not 403, so a 404 here means "not yours or not there" — never
   surface it as "deleted".

   The row shape is not published in the spec, so mapLogEntry is deliberately
   tolerant: it accepts snake_case and camelCase, and tolerates actor/target
   arriving either nested or flattened. Telemetry rows (reading, level, location)
   have no actor at all. */

export type LogCategory =
  | 'activity'   // user actions — created a site, edited a tank
  | 'reading'    // raw sensor readings
  | 'level'      // tank level changes
  | 'alert'      // alerts raised
  | 'security'   // theft / leak events
  | 'delivery'   // tanker deliveries
  | 'location'   // GPS fixes — one row per fix, excluded by default
  | 'issue'      // issues raised / escalated
  | 'quality';   // quality inspections

/** Every category the API accepts, in the order the docs list them. */
export const LOG_CATEGORIES: LogCategory[] = [
  'activity', 'reading', 'level', 'alert', 'security', 'delivery', 'location', 'issue', 'quality',
];

/* `location` is one row per GPS fix, so the API leaves it out unless asked for.
   Mirrored here so the UI's default selection matches the server's default. */
export const DEFAULT_LOG_CATEGORIES: LogCategory[] = LOG_CATEGORIES.filter(c => c !== 'location');

export type LogTargetType = 'site' | 'tank' | 'sensor' | 'tanker';

export interface LogActor {
  id?:    number | string;
  name?:  string;
  email?: string;
}

export interface LogTarget {
  type?:  string;
  id?:    string | number;
  label?: string;
}

export interface LogEntry {
  id:          string;
  timestamp:   string;
  category:    LogCategory;
  description: string;
  /** Absent on telemetry rows — readings, level changes and GPS fixes have no actor. */
  actor?:      LogActor;
  target?:     LogTarget;
  severity?:   'low' | 'medium' | 'high' | 'critical';
  siteId?:     string;
  siteName?:   string;
  /** Machine name of the action, e.g. "issue_escalated" (activity rows). */
  action?:     string;
  /** Request context recorded with a user action (October 2026): ip, userAgent, … */
  metadata?:   { ip?: string | null; userAgent?: string; [key: string]: unknown };
}

export interface LogsParams {
  from?:       string;          // ISO datetime; API defaults to 24h ago
  to?:         string;          // ISO datetime; API defaults to now
  categories?: LogCategory[];   // sent comma-separated; omit for the API default
  actorId?:    number | string; // only this user's actions; drops telemetry
  siteId?:     string;
  tankId?:     string;
  sensorId?:   number | string;
  search?:     string;          // description, target label or actor name
  page?:       number;
  limit?:      number;          // API default 50, max 200
}

export interface LogsResult {
  results: LogEntry[];
  count:   number;
  page:    number;
  pages:   number;
}

/** The API caps limit at 200; asking for more is a 400 rather than a clamp. */
export const LOG_LIMIT_MAX = 200;

function mapLogEntry(r: any, index: number): LogEntry {
  const rawCategory = String(r?.category ?? r?.type ?? 'activity').toLowerCase();
  const category = (LOG_CATEGORIES as string[]).includes(rawCategory)
    ? (rawCategory as LogCategory)
    : 'activity';

  const actorName = r?.actor?.name ?? r?.actorName ?? r?.actor_name ?? r?.actor;
  const actorId   = r?.actor?.id   ?? r?.actorId   ?? r?.actor_id;
  // A bare string actor is a name, not an object — don't produce { id: "Jane" }.
  const actor: LogActor | undefined =
    (actorName != null && actorName !== '') || actorId != null
      ? { id: actorId ?? undefined, name: typeof actorName === 'string' ? actorName : undefined, email: r?.actor?.email ?? undefined }
      : undefined;

  const targetLabel = r?.target?.label ?? r?.targetLabel ?? r?.target_label;
  const targetType  = r?.target?.type  ?? r?.targetType  ?? r?.target_type;
  const targetId    = r?.target?.id    ?? r?.targetId    ?? r?.target_id;
  const target: LogTarget | undefined =
    targetLabel != null || targetType != null || targetId != null
      ? { type: targetType ?? undefined, id: targetId ?? undefined, label: targetLabel ?? undefined }
      : undefined;

  const severity = r?.severity;

  return {
    // Telemetry rows may have no stable id; fall back to timestamp+index so React
    // keys stay unique within a page.
    id:          String(r?.id ?? `${r?.timestamp ?? r?.created_at ?? 'row'}-${index}`),
    timestamp:   r?.timestamp ?? r?.created_at ?? r?.createdAt ?? new Date().toISOString(),
    category,
    description: r?.description ?? r?.message ?? r?.title ?? '',
    actor,
    target,
    severity:    ['low', 'medium', 'high', 'critical'].includes(severity) ? severity : undefined,
    siteId:      r?.siteId   ?? r?.site_id   ?? r?.site?.id ?? undefined,
    siteName:    r?.siteName ?? r?.site_name ?? r?.site?.name ?? undefined,
    action:      r?.action ?? undefined,
    metadata:    r?.metadata && typeof r.metadata === 'object' ? r.metadata : undefined,
  };
}

function mapLogsResult(body: any, requestedPage: number, requestedLimit: number): LogsResult {
  const rows: any[] = Array.isArray(body) ? body
    : Array.isArray(body?.results) ? body.results
    : Array.isArray(body?.data)    ? body.data
    : [];

  const count = Number(body?.count ?? body?.total ?? rows.length);
  const limit = requestedLimit > 0 ? requestedLimit : 50;

  return {
    results: rows.map(mapLogEntry),
    count,
    page:    Number(body?.page ?? requestedPage),
    pages:   Number(body?.pages ?? body?.totalPages ?? Math.max(1, Math.ceil(count / limit))),
  };
}

export async function fetchLogs(params: LogsParams = {}): Promise<LogsResult> {
  const qs = new URLSearchParams();
  if (params.from   != null) qs.set('from',   params.from);
  if (params.to     != null) qs.set('to',     params.to);
  // Omit entirely when every default category is selected — let the server apply
  // its own default rather than pinning it from the client.
  if (params.categories && params.categories.length > 0) {
    qs.set('categories', params.categories.join(','));
  }
  if (params.actorId  != null && params.actorId  !== '') qs.set('actorId',  String(params.actorId));
  if (params.siteId   != null && params.siteId   !== '') qs.set('siteId',   params.siteId);
  if (params.tankId   != null && params.tankId   !== '') qs.set('tankId',   params.tankId);
  if (params.sensorId != null && params.sensorId !== '') qs.set('sensorId', String(params.sensorId));
  if (params.search)         qs.set('search',  params.search);
  if (params.page   != null) qs.set('page',    String(params.page));

  const limit = Math.min(params.limit ?? 50, LOG_LIMIT_MAX);
  qs.set('limit', String(limit));

  const res = await apiFetch(`/api/reports/logs/${qs.toString() ? `?${qs}` : ''}`);
  if (!res.ok) {
    const body = await res.json().catch(() => null);
    throw { message: body?.detail ?? body?.message ?? 'Failed to load logs.', status: res.status } as ApiError;
  }
  return mapLogsResult(await res.json().catch(() => null), params.page ?? 1, limit);
}

export interface ObjectLogsParams {
  from?:  string;   // API defaults to 30 days ago for this view, not 24 hours
  to?:    string;
  limit?: number;
}

/** Everything that ever happened to one site, tank, sensor or tanker. */
export async function fetchObjectLogs(
  targetType: LogTargetType,
  targetId:   string | number,
  params:     ObjectLogsParams = {},
): Promise<LogsResult> {
  const qs = new URLSearchParams();
  if (params.from != null) qs.set('from', params.from);
  if (params.to   != null) qs.set('to',   params.to);

  const limit = Math.min(params.limit ?? 50, LOG_LIMIT_MAX);
  qs.set('limit', String(limit));

  const res = await apiFetch(
    `/api/reports/logs/${targetType}/${targetId}/${qs.toString() ? `?${qs}` : ''}`,
  );
  if (!res.ok) {
    const body = await res.json().catch(() => null);
    // Company-scoped: another company's record is 404, not 403.
    const message = res.status === 404
      ? 'No history found for this record.'
      : body?.detail ?? body?.message ?? 'Failed to load history.';
    throw { message, status: res.status } as ApiError;
  }
  return mapLogsResult(await res.json().catch(() => null), 1, limit);
}

/* ════════════════════════════════════════════════════════════════════════
   Section C — Fuel Dispensing and Consumption Monitoring
   ════════════════════════════════════════════════════════════════════════ */

export type EquipmentType = 'generator' | 'vehicle' | 'tanker' | 'plant' | 'vessel' | 'other';

export const EQUIPMENT_TYPES: EquipmentType[] = [
  'generator', 'vehicle', 'tanker', 'plant', 'vessel', 'other',
];

export interface Equipment {
  id:                 string;
  name:               string;
  /** Asset tag written on the equipment. Unique within the company. */
  equipmentCode:      string;
  equipmentType:      EquipmentType;
  registrationNumber: string;
  fuelType:           string;
  /** Onboard tank size. The API rejects a dispense larger than this. */
  tankCapacityLitres: number;
  siteId?:            string;
  siteName?:          string;
  /* Consumption monitoring (October 2026). usageMeter defaults from the type on the
     server: generator and plant → hour meter; vehicle and tanker → odometer; else none. */
  usageMeter?:              UsageMeter;
  expectedKmPerLitre?:      number;
  expectedLitresPerHour?:   number;
  consumptionTolerancePct?: number;
  requireMeterReading?:     boolean;
}

export type UsageMeter = 'odometer' | 'hour_meter' | 'none';

/** What the server assumes when usageMeter is left out. */
export function defaultUsageMeter(type: EquipmentType): UsageMeter {
  if (type === 'generator' || type === 'plant') return 'hour_meter';
  if (type === 'vehicle' || type === 'tanker')  return 'odometer';
  return 'none';
}

/** Equipment detail adds the last 30 days of fuel and the recent fuel-issue rows. */
export interface EquipmentDetail extends Equipment {
  consumption30dLitres?: number;
  recentDispenses:       Dispense[];
}

export interface EquipmentParams {
  type?:         EquipmentType;
  siteId?:       string;
  search?:       string;
  /** Only equipment not yet mapped to a SAP measuring point. */
  unmappedOnly?: boolean;
  page?:         number;
  limit?:        number;
}

export interface EquipmentResult {
  results: Equipment[];
  total:   number;
  page:    number;
  limit:   number;
}

function mapEquipment(e: any): Equipment {
  return {
    id:                 String(e?.id ?? ''),
    name:               String(e?.name ?? ''),
    equipmentCode:      String(e?.equipmentCode      ?? e?.equipment_code      ?? ''),
    equipmentType:      (e?.equipmentType ?? e?.equipment_type ?? 'other') as EquipmentType,
    registrationNumber: String(e?.registrationNumber ?? e?.registration_number ?? ''),
    fuelType:           String(e?.fuelType ?? e?.fuel_type ?? 'diesel'),
    tankCapacityLitres: Number(e?.tankCapacityLitres ?? e?.tank_capacity_litres ?? 0),
    siteId:             e?.siteId   ?? e?.site_id   ?? e?.site?.id ?? undefined,
    siteName:           e?.siteName ?? e?.site_name ?? e?.site?.name ?? undefined,
    usageMeter:              e?.usageMeter ?? e?.usage_meter ?? undefined,
    expectedKmPerLitre:      numOrUndef(e?.expectedKmPerLitre ?? e?.expected_km_per_litre),
    expectedLitresPerHour:   numOrUndef(e?.expectedLitresPerHour ?? e?.expected_litres_per_hour),
    consumptionTolerancePct: numOrUndef(e?.consumptionTolerancePct ?? e?.consumption_tolerance_pct),
    requireMeterReading:     e?.requireMeterReading ?? e?.require_meter_reading ?? undefined,
  };
}

/** List endpoints return { results, total, page, limit }. */
function mapListEnvelope<T>(body: any, map: (row: any) => T, page: number, limit: number) {
  const rows: any[] = Array.isArray(body) ? body
    : Array.isArray(body?.results) ? body.results
    : Array.isArray(body?.data)    ? body.data
    : [];
  return {
    results: rows.map(map),
    total:   Number(body?.total ?? body?.count ?? rows.length),
    page:    Number(body?.page  ?? page),
    limit:   Number(body?.limit ?? limit),
  };
}

export async function fetchEquipment(params: EquipmentParams = {}): Promise<EquipmentResult> {
  const qs = new URLSearchParams();
  if (params.type)         qs.set('type',   params.type);
  if (params.siteId)       qs.set('siteId', params.siteId);
  if (params.search)       qs.set('search', params.search);
  if (params.unmappedOnly) qs.set('unmappedOnly', 'true');
  if (params.page  != null) qs.set('page',  String(params.page));
  const limit = Math.min(params.limit ?? 100, 100);   // list endpoints cap at 100
  qs.set('limit', String(limit));

  const res  = await apiFetch(`/api/v1/fuel/equipment/${qs.toString() ? `?${qs}` : ''}`);
  const body = await res.json().catch(() => null);
  if (!res.ok) throw { message: body?.detail ?? body?.message ?? 'Failed to load equipment.', status: res.status } as ApiError;
  return mapListEnvelope(body?.data ?? body, mapEquipment, params.page ?? 1, limit);
}

export interface EquipmentPayload {
  name:                string;
  equipmentCode:       string;
  equipmentType:       EquipmentType;
  registrationNumber?: string;
  fuelType?:           string;
  tankCapacityLitres?: number;
  siteId?:             string;
  usageMeter?:              UsageMeter;
  expectedKmPerLitre?:      number | null;
  expectedLitresPerHour?:   number | null;
  consumptionTolerancePct?: number | null;
  requireMeterReading?:     boolean;
}

function throwFieldErrors(body: any, fallback: string, status: number): never {
  const fieldErrors = body?.errors;
  const msgs = fieldErrors && typeof fieldErrors === 'object'
    ? Object.values(fieldErrors).flat().join('; ')
    : null;
  throw { message: msgs ?? body?.detail ?? body?.message ?? fallback, status, errors: fieldErrors } as ApiError & { errors?: any };
}

export async function createEquipment(payload: EquipmentPayload): Promise<Equipment> {
  const res  = await apiFetch('/api/v1/fuel/equipment/', {
    method: 'POST',
    body:   JSON.stringify(payload),
  });
  const body = await res.json().catch(() => null);
  if (!res.ok) throwFieldErrors(body, 'Failed to create equipment.', res.status);
  return mapEquipment(body?.data ?? body);
}

export async function fetchEquipmentById(equipmentId: string): Promise<EquipmentDetail> {
  const res  = await apiFetch(`/api/v1/fuel/equipment/${equipmentId}/`);
  const body = await res.json().catch(() => null);
  if (!res.ok) throw toApiError(res.status, body, 'Equipment not found.');
  const d = body?.data ?? body;
  return {
    ...mapEquipment(d),
    consumption30dLitres: numOrUndef(d?.consumption30dLitres),
    recentDispenses: (Array.isArray(d?.recentDispenses) ? d.recentDispenses : []).map(mapDispense),
  };
}

export async function updateEquipment(equipmentId: string, payload: Partial<EquipmentPayload>): Promise<Equipment> {
  const res  = await apiFetch(`/api/v1/fuel/equipment/${equipmentId}/`, {
    method: 'PUT',
    body:   JSON.stringify(payload),
  });
  const body = await res.json().catch(() => null);
  if (!res.ok) throwFieldErrors(body, 'Failed to update equipment.', res.status);
  return mapEquipment(body?.data ?? body);
}

export async function deleteEquipment(equipmentId: string): Promise<void> {
  const res = await apiFetch(`/api/v1/fuel/equipment/${equipmentId}/`, { method: 'DELETE' });
  if (res.status === 204 || res.ok) return;
  const body = await res.json().catch(() => null);
  throw { message: body?.detail ?? 'Failed to delete equipment.', status: res.status } as ApiError;
}

/* ── Dispenses ──────────────────────────────────────────────────────────
   Recording a fuel issue. Only tankId and quantityLitres are required.
   Send both meter readings or neither — one alone is a 400. Send
   equipmentId or tankerId, never both. */

export type ReconciliationStatus = 'matched' | 'variance' | 'unmeasured' | 'not_checked';
export type MeasurementSource    = 'meter' | 'tanker_sensor' | 'tank_sensor';

export interface Dispense {
  id:                     string;
  tankId:                 string;
  tankName?:              string;
  siteId?:                string;
  siteName?:              string;
  quantityLitres:         number;
  equipmentId?:           string;
  equipmentName?:         string;
  tankerId?:              string;
  issuedToName?:          string;
  issuedToUserId?:        string;
  openingMeterReading?:   number;
  closingMeterReading?:   number;
  dispensedAt:            string;
  reference?:             string;
  notes?:                 string;
  tankBalanceAfterLitres?: number;
  reconciliationStatus?:  ReconciliationStatus;
  measurementSource?:     MeasurementSource;
  /** Present on `variance` — names recorded vs measured and the percentage out. */
  warning?:               string;
  /** e.g. theft alerts on this tank were resolved because this dispense explains the drop. */
  note?:                  string;

  /* ── Blocks added in October 2026 (guide §5) — the flat fields above are kept. ── */
  status?:             string;
  driverId?:           string;
  driver?:             { id: string; name: string; phone?: string } | null;
  equipmentTypeLabel?: string;
  registrationNumber?: string;
  odometerKm?:         number;
  hourMeter?:          number;
  site?:               SiteBlock;
  tank?:               TankBlock;
  reconciliation?:     DispenseReconciliation;
  usage?:              DispenseUsage;
  /** Most serious first. Show as badges. */
  flags:               DispenseFlag[];
  quantityDisplay?:    string;
  dispensedAtDisplay?: string;
  timeAgo?:            string;
  dispensedByName?:    string;

  /* Grouped / richer blocks (guide §5, dispenses CHANGED). */
  equipment?: {
    id: string; name: string; code?: string; type?: string; typeLabel?: string;
    registrationNumber?: string; fuelType?: string; tankCapacityLitres?: number | null; sapEquipmentNumber?: string;
  } | null;
  tanker?:      { id: string; name?: string; registrationNumber?: string } | null;
  meter?:       { opening?: number | null; closing?: number | null; dispensedLitres?: number | null; recorded: boolean; suspectReading: boolean };
  tankLevel?:   { beforePct?: number | null; afterPct?: number | null; measuredDropLitres?: number | null; balanceAfterLitres?: number | null; captured: boolean };
  issuedTo?:    { name?: string; user?: UserBlock | null };
  dispensedByUser?: UserBlock;
  dispensedBy?:     string;
  sapDocumentNumber?:    string;
  meterDispensedLitres?: number;
  tankLevelBeforePct?:   number;
  tankLevelAfterPct?:    number;
  tankMeasuredLitres?:   number;
  tankerMeasuredLitres?: number;
  varianceLitres?:       number;
  variancePct?:          number;
}

/** UI tone from the API: success · danger · warning · neutral (guide §8). */
export type Tone = 'success' | 'danger' | 'warning' | 'neutral' | 'info';

export interface ReconciliationCheck {
  key: string; label: string; available: boolean;
  measuredLitres?: number; differenceLitres?: number; differencePct?: number;
  tolerancePct?: number; withinTolerance?: boolean | null; decidesStatus?: boolean; reason?: string;
}

export interface DispenseReconciliation {
  status:  ReconciliationStatus;
  label:   string;
  tone:    Tone;
  summary?: string;
  recordedLitres?: number;
  measuredLitres?: number;
  /** meter · tank · tanker — which measurement decided the status. */
  source?:         string;
  sourceLabel?:    string;
  varianceLitres?: number;
  variancePct?:    number;
  tolerancePct?:   number;
  checks:          ReconciliationCheck[];
  missingEvidence: string[];
}

export type UsageStatus = 'normal' | 'high' | 'low' | 'invalid_reading' | 'no_reading' | 'first_fill' | 'learning' | 'not_applicable';

export interface DispenseUsage {
  status:  UsageStatus;
  label:   string;
  tone:    Tone;
  summary?: string;
  distanceKm?:   number;
  hoursRun?:     number;
  actualRate?:   number;
  expectedRate?: number;
  rateUnit?:     string;
  excessLitres?: number;
}

export interface DispenseFlag { code: string; severity: 'danger' | 'warning' | 'info' | string; message: string }

function mapTone(t: any): Tone {
  return (['success', 'danger', 'warning', 'neutral', 'info'].includes(t) ? t : 'neutral') as Tone;
}

function mapDispenseReconciliation(r: any): DispenseReconciliation | undefined {
  if (!r || typeof r !== 'object') return undefined;
  return {
    status: (r.status ?? 'not_checked') as ReconciliationStatus,
    label:  String(r.label ?? ''),
    tone:   mapTone(r.tone),
    summary: r.summary ?? undefined,
    recordedLitres: numOrUndef(r.recordedLitres),
    measuredLitres: numOrUndef(r.measuredLitres),
    source:         r.source ?? undefined,
    sourceLabel:    r.sourceLabel ?? undefined,
    varianceLitres: numOrUndef(r.varianceLitres),
    variancePct:    numOrUndef(r.variancePct),
    tolerancePct:   numOrUndef(r.tolerancePct),
    checks: (Array.isArray(r.checks) ? r.checks : []).filter((c: any) => c && typeof c === 'object').map((c: any) => ({
      key: String(c.key ?? ''), label: String(c.label ?? c.key ?? ''), available: Boolean(c.available),
      measuredLitres: numOrUndef(c.measuredLitres), differenceLitres: numOrUndef(c.differenceLitres),
      differencePct: numOrUndef(c.differencePct), tolerancePct: numOrUndef(c.tolerancePct),
      withinTolerance: c.withinTolerance ?? null, decidesStatus: Boolean(c.decidesStatus), reason: c.reason ?? undefined,
    })),
    missingEvidence: (Array.isArray(r.missingEvidence) ? r.missingEvidence : []).filter((x: any) => typeof x === 'string'),
  };
}

function mapDispenseUsage(u: any): DispenseUsage | undefined {
  if (!u || typeof u !== 'object') return undefined;
  return {
    status:  (u.status ?? 'not_applicable') as UsageStatus,
    label:   String(u.label ?? ''),
    tone:    mapTone(u.tone),
    summary: u.summary ?? undefined,
    distanceKm:   numOrUndef(u.distanceKm ?? u.distance),
    hoursRun:     numOrUndef(u.hoursRun ?? u.hours),
    actualRate:   numOrUndef(u.actualRate ?? u.actualKmPerLitre ?? u.actualLitresPerHour),
    expectedRate: numOrUndef(u.expectedRate ?? u.expectedKmPerLitre ?? u.expectedLitresPerHour),
    rateUnit:     u.rateUnit ?? u.unit ?? undefined,
    excessLitres: numOrUndef(u.excessLitres),
  };
}

const FLAG_ORDER: Record<string, number> = { danger: 0, warning: 1, info: 2 };

function mapDispense(d: any): Dispense {
  const eq = d?.equipment && typeof d.equipment === 'object' ? d.equipment : null;
  const driver = d?.driver && typeof d.driver === 'object' ? d.driver : null;
  return {
    id:                     String(d?.id ?? ''),
    tankId:                 String(d?.tankId ?? d?.tank_id ?? ''),
    tankName:               d?.tankName ?? d?.tank_name ?? d?.tank?.name ?? undefined,
    siteId:                 d?.siteId   ?? d?.site_id   ?? undefined,
    siteName:               d?.siteName ?? d?.site_name ?? d?.site?.name ?? undefined,
    quantityLitres:         Number(d?.quantityLitres ?? d?.quantity_litres ?? 0),
    equipmentId:            d?.equipmentId   ?? d?.equipment_id   ?? undefined,
    equipmentName:          d?.equipmentName ?? d?.equipment_name ?? d?.equipment?.name ?? undefined,
    tankerId:               d?.tankerId ?? d?.tanker_id ?? undefined,
    issuedToName:           d?.issuedToName   ?? d?.issued_to_name   ?? undefined,
    issuedToUserId:         d?.issuedToUserId ?? d?.issued_to_user_id ?? undefined,
    openingMeterReading:    numOrUndef(d?.openingMeterReading ?? d?.opening_meter_reading),
    closingMeterReading:    numOrUndef(d?.closingMeterReading ?? d?.closing_meter_reading),
    dispensedAt:            d?.dispensedAt ?? d?.dispensed_at ?? new Date().toISOString(),
    reference:              d?.reference ?? undefined,
    notes:                  d?.notes ?? undefined,
    tankBalanceAfterLitres: numOrUndef(d?.tankBalanceAfterLitres ?? d?.tank_balance_after_litres),
    reconciliationStatus:   (d?.reconciliationStatus ?? d?.reconciliation_status) as ReconciliationStatus | undefined,
    measurementSource:      (d?.measurementSource ?? d?.measurement_source) as MeasurementSource | undefined,
    warning:                d?.warning ?? undefined,
    note:                   d?.note ?? undefined,
    status:                 d?.status ?? undefined,
    driverId:               d?.driverId ?? driver?.id ?? undefined,
    driver:                 driver ? { id: String(driver.id ?? ''), name: String(driver.name ?? ''), phone: driver.phone ?? undefined } : null,
    equipmentTypeLabel:     eq?.typeLabel ?? undefined,
    registrationNumber:     eq?.registrationNumber ?? undefined,
    odometerKm:             numOrUndef(d?.odometerKm ?? d?.odometer_km),
    hourMeter:              numOrUndef(d?.hourMeter ?? d?.hour_meter),
    site:                   mapSiteBlock(d?.site),
    tank:                   mapTankBlock(d?.tank),
    reconciliation:         mapDispenseReconciliation(d?.reconciliation),
    usage:                  mapDispenseUsage(d?.usage),
    flags: (Array.isArray(d?.flags) ? d.flags : [])
      .filter((f: any) => f && typeof f === 'object')
      .map((f: any) => ({ code: String(f.code ?? ''), severity: String(f.severity ?? 'info'), message: String(f.message ?? '') }))
      .sort((a: DispenseFlag, b: DispenseFlag) => (FLAG_ORDER[a.severity] ?? 3) - (FLAG_ORDER[b.severity] ?? 3)),
    quantityDisplay:        d?.quantityDisplay ?? undefined,
    dispensedAtDisplay:     d?.dispensedAtDisplay ?? undefined,
    timeAgo:                d?.timeAgo ?? undefined,
    dispensedByName:        d?.dispensedByUser?.name ?? (typeof d?.dispensedBy === 'string' ? d.dispensedBy : undefined),

    equipment: eq ? {
      id: String(eq.id ?? ''), name: String(eq.name ?? ''), code: eq.code ?? undefined, type: eq.type ?? undefined,
      typeLabel: eq.typeLabel ?? undefined, registrationNumber: eq.registrationNumber || undefined,
      fuelType: eq.fuelType ?? undefined, tankCapacityLitres: eq.tankCapacityLitres == null ? null : Number(eq.tankCapacityLitres),
      sapEquipmentNumber: eq.sapEquipmentNumber || undefined,
    } : null,
    tanker: d?.tanker && typeof d.tanker === 'object'
      ? { id: String(d.tanker.id ?? ''), name: d.tanker.name ?? d.tanker.fleetNumber ?? undefined, registrationNumber: d.tanker.registrationNumber ?? undefined }
      : null,
    meter: d?.meter && typeof d.meter === 'object' ? {
      opening: d.meter.opening == null ? null : Number(d.meter.opening),
      closing: d.meter.closing == null ? null : Number(d.meter.closing),
      dispensedLitres: d.meter.dispensedLitres == null ? null : Number(d.meter.dispensedLitres),
      recorded: Boolean(d.meter.recorded), suspectReading: Boolean(d.meter.suspectReading),
    } : undefined,
    tankLevel: d?.tankLevel && typeof d.tankLevel === 'object' ? {
      beforePct: d.tankLevel.beforePct == null ? null : Number(d.tankLevel.beforePct),
      afterPct:  d.tankLevel.afterPct  == null ? null : Number(d.tankLevel.afterPct),
      measuredDropLitres: d.tankLevel.measuredDropLitres == null ? null : Number(d.tankLevel.measuredDropLitres),
      balanceAfterLitres: d.tankLevel.balanceAfterLitres == null ? null : Number(d.tankLevel.balanceAfterLitres),
      captured: Boolean(d.tankLevel.captured),
    } : undefined,
    issuedTo: d?.issuedTo && typeof d.issuedTo === 'object'
      ? { name: d.issuedTo.name || undefined, user: mapUserBlock(d.issuedTo.user) ?? null }
      : undefined,
    dispensedByUser:      mapUserBlock(d?.dispensedByUser),
    dispensedBy:          typeof d?.dispensedBy === 'string' ? d.dispensedBy : undefined,
    sapDocumentNumber:    d?.sapDocumentNumber || undefined,
    meterDispensedLitres: numOrUndef(d?.meterDispensedLitres),
    tankLevelBeforePct:   numOrUndef(d?.tankLevelBeforePct),
    tankLevelAfterPct:    numOrUndef(d?.tankLevelAfterPct),
    tankMeasuredLitres:   numOrUndef(d?.tankMeasuredLitres),
    tankerMeasuredLitres: numOrUndef(d?.tankerMeasuredLitres),
    varianceLitres:       numOrUndef(d?.varianceLitres),
    variancePct:          numOrUndef(d?.variancePct),
  };
}

export interface DispensePayload {
  tankId:               string;
  quantityLitres:       number;
  equipmentId?:         string;
  tankerId?:            string;
  issuedToName?:        string;
  issuedToUserId?:      string;
  openingMeterReading?: number;
  closingMeterReading?: number;
  dispensedAt?:         string;
  deliveryId?:          string;
  reference?:           string;
  notes?:               string;
  /** UUID from /api/fleet/drivers/ (404 if unknown). */
  driverId?:            string;
  /** The reading for the equipment's usageMeter: odometer for vehicles, hour meter for generators. */
  odometerKm?:          number;
  hourMeter?:           number;
}

/** Client-side mirror of the rules the API enforces, so the user hears first. */
export function validateDispense(p: Partial<DispensePayload>): string | null {
  if (!p.tankId)                                  return 'Select the tank fuel was drawn from.';
  if (p.quantityLitres == null || Number.isNaN(p.quantityLitres)) return 'Enter the quantity dispensed.';
  if (p.quantityLitres <= 0)                      return 'Quantity must be greater than zero.';
  if (p.equipmentId && p.tankerId)                return 'Choose equipment or a tanker, not both.';
  const hasOpen  = p.openingMeterReading != null && !Number.isNaN(p.openingMeterReading);
  const hasClose = p.closingMeterReading != null && !Number.isNaN(p.closingMeterReading);
  // "Send both meter readings or neither — one alone is a 400."
  if (hasOpen !== hasClose)                       return 'Enter both meter readings, or leave both blank.';
  if (hasOpen && hasClose && p.closingMeterReading! < p.openingMeterReading!) {
    return 'Closing meter reading cannot be lower than the opening reading.';
  }
  return null;
}

export interface CreateDispenseResult {
  dispense: Dispense;
  /** Present when the fill was judged `high` — a consumption alert and notification were raised too. */
  consumptionWarning?: string;
  /** Present when recording this issue explained, and auto-resolved, an open theft alert. */
  note?: string;
}

export async function createDispense(payload: DispensePayload): Promise<CreateDispenseResult> {
  const res  = await apiFetch('/api/v1/fuel/dispenses/', {
    method: 'POST',
    body:   JSON.stringify(payload),
  });
  const body = await res.json().catch(() => null);
  if (res.status === 404 && payload.driverId) throw { message: 'That driver could not be found.', status: 404 } as ApiError;
  // 400 when the quantity is more than the tank holds, or a required meter reading is missing.
  if (!res.ok) throwFieldErrors(body, 'Failed to record dispense.', res.status);
  return {
    dispense: mapDispense(body?.data ?? body),
    consumptionWarning: body?.consumptionWarning ?? undefined,
    note: body?.note ?? body?.data?.note ?? undefined,
  };
}

export interface DispensesParams {
  tankId?: string;
  siteId?: string;
  from?:   string;
  to?:     string;
  status?: ReconciliationStatus;
  equipmentId?: string;
  driverId?:    string;
  /** high · low · normal · no_reading · first_fill · learning · invalid_reading */
  consumption?: UsageStatus;
  page?:   number;
  limit?:  number;
}

export interface DispensesSummary {
  totalLitres:   number;
  varianceCount: number;
  totalVariance: number;
  issues?:                 number;
  totalLitresDisplay?:     string;
  absoluteVarianceLitres?: number;
  byReconciliation?:       { matched: number; variance: number; unmeasured: number; notChecked: number };
  matchedPct?:             number;
  withMeterReadingsPct?:   number;
}

export interface DispensesResult {
  results: Dispense[];
  total:   number;
  page:    number;
  limit:   number;
  pages:   number;
  summary: DispensesSummary;
}

export async function fetchDispenses(params: DispensesParams = {}): Promise<DispensesResult> {
  const qs = new URLSearchParams();
  if (params.tankId)      qs.set('tankId', params.tankId);
  if (params.siteId)      qs.set('siteId', params.siteId);
  if (params.from)        qs.set('from',   params.from);
  if (params.to)          qs.set('to',     params.to);
  if (params.status) {
    // ?reconciliation= is the documented filter; status is kept for older servers.
    qs.set('reconciliation', params.status);
    qs.set('status', params.status);
  }
  if (params.equipmentId) qs.set('equipmentId', params.equipmentId);
  if (params.driverId)    qs.set('driverId', params.driverId);
  if (params.consumption) qs.set('consumption', params.consumption);
  if (params.page != null) qs.set('page', String(params.page));
  const limit = Math.min(params.limit ?? 100, 100);
  qs.set('limit', String(limit));

  const res  = await apiFetch(`/api/v1/fuel/dispenses/${qs.toString() ? `?${qs}` : ''}`);
  const body = await res.json().catch(() => null);
  if (!res.ok) throw toApiError(res.status, body, 'Failed to load fuel issues.');

  // Paged envelope: data[] with total/page/limit/pages beside it (or nested under data).
  const payload = Array.isArray(body?.data) ? body : (body?.data ?? body);
  const base    = mapListEnvelope(payload, mapDispense, params.page ?? 1, limit);
  const summary = payload?.summary ?? {};
  return {
    ...base,
    pages: numOf(payload?.pages ?? body?.pages) || Math.max(1, Math.ceil((base.total || 0) / limit)),
    summary: {
      totalLitres:   Number(summary?.totalLitres   ?? summary?.total_litres   ?? 0),
      varianceCount: Number(summary?.varianceCount ?? summary?.variance_count ?? summary?.byReconciliation?.variance ?? 0),
      totalVariance: Number(summary?.totalVariance ?? summary?.total_variance ?? 0),
      issues:                 numOrUndef(summary?.issues),
      totalLitresDisplay:     summary?.totalLitresDisplay ?? undefined,
      absoluteVarianceLitres: numOrUndef(summary?.absoluteVarianceLitres),
      byReconciliation: summary?.byReconciliation ? {
        matched:    numOf(summary.byReconciliation.matched),
        variance:   numOf(summary.byReconciliation.variance),
        unmeasured: numOf(summary.byReconciliation.unmeasured),
        notChecked: numOf(summary.byReconciliation.notChecked ?? summary.byReconciliation.not_checked),
      } : undefined,
      matchedPct:           numOrUndef(summary?.matchedPct),
      withMeterReadingsPct: numOrUndef(summary?.withMeterReadingsPct),
    },
  };
}

/** GET /api/v1/fuel/dispenses/{id}/ — one fuel issue with every block. view_deliveries. */
export async function fetchDispenseById(dispenseId: string): Promise<Dispense> {
  const res  = await apiFetch(`/api/v1/fuel/dispenses/${encodeURIComponent(dispenseId)}/`);
  const body = await res.json().catch(() => null);
  if (res.status === 404) throw { message: "This fuel issue isn't available to your account.", status: 404 } as ApiError;
  if (!res.ok) throw toApiError(res.status, body, 'Failed to load the fuel issue.');
  return mapDispense(body?.data ?? body);
}

/** PUT /api/v1/fuel/dispenses/{id}/ — correct a fuel issue (Owner, Admin, Manager — manage_deliveries).
    Send only what changed. 400s come back with field errors. The row is re-judged on save. */
export async function updateDispense(dispenseId: string, payload: Partial<DispensePayload>): Promise<Dispense> {
  const res  = await apiFetch(`/api/v1/fuel/dispenses/${encodeURIComponent(dispenseId)}/`, {
    method: 'PUT',
    body:   JSON.stringify(payload),
  });
  const body = await res.json().catch(() => null);
  if (res.status === 404) throw { message: "This fuel issue isn't available to your account.", status: 404 } as ApiError;
  if (!res.ok) throwFieldErrors(body, 'Failed to update the fuel issue.', res.status);
  return mapDispense(body?.data ?? body);
}

/** POST …/{id}/reconcile/ — re-measure a fuel issue that came back `unmeasured`; sensors report on an interval. */
export async function reconcileDispense(dispenseId: string): Promise<Dispense> {
  const res  = await apiFetch(`/api/v1/fuel/dispenses/${encodeURIComponent(dispenseId)}/reconcile/`, { method: 'POST' });
  const body = await res.json().catch(() => null);
  if (res.status === 404) throw { message: "This fuel issue isn't available to your account.", status: 404 } as ApiError;
  if (!res.ok) throw toApiError(res.status, body, 'Failed to re-check the fuel issue.');
  return mapDispense(body?.data ?? body);
}

/* ── Reconciliation report ──────────────────────────────────────────────
   One row per tank. Identity: opening + received − dispensed = closing.
   A `loss` means more fuel left the tank than anything recorded leaving it. */

export type ReconciliationDirection = 'loss' | 'gain' | 'balanced';

export interface ReconciliationRow {
  tankId:               string;
  tankName?:            string;
  siteName?:            string;
  openingLitres:        number;
  receivedLitres:       number;
  dispensedLitres:      number;
  expectedClosingLitres: number;
  actualClosingLitres:  number;
  unaccountedLitres:    number;
  unaccountedPct:       number;
  direction:            ReconciliationDirection;
  fuelType?:            string;
  openingAt?:           string;
  closingAt?:           string;
  /** The sensor first reported inside the period, so the opening balance is its first reading. */
  openingInsidePeriod?: boolean;
  /** false = the tank has no readings at all: show "no data", not zeros. */
  hasReadings?:         boolean;
  /** The site has several tanks of this fuel; each is shown the whole delivery. */
  receivedIsSiteLevel?: boolean;
}

function mapReconciliationRow(r: any): ReconciliationRow {
  return {
    tankId:                String(r?.tankId ?? r?.tank_id ?? ''),
    tankName:              r?.tankName ?? r?.tank_name ?? undefined,
    siteName:              r?.siteName ?? r?.site_name ?? undefined,
    openingLitres:         Number(r?.openingLitres         ?? r?.opening_litres         ?? 0),
    receivedLitres:        Number(r?.receivedLitres        ?? r?.received_litres        ?? 0),
    dispensedLitres:       Number(r?.dispensedLitres       ?? r?.dispensed_litres       ?? 0),
    expectedClosingLitres: Number(r?.expectedClosingLitres ?? r?.expected_closing_litres ?? 0),
    actualClosingLitres:   Number(r?.actualClosingLitres   ?? r?.actual_closing_litres   ?? 0),
    unaccountedLitres:     Number(r?.unaccountedLitres     ?? r?.unaccounted_litres     ?? 0),
    unaccountedPct:        Number(r?.unaccountedPct        ?? r?.unaccounted_pct        ?? 0),
    direction:             (r?.direction ?? 'balanced') as ReconciliationDirection,
    fuelType:              r?.fuelType ?? r?.fuel_type ?? undefined,
    openingAt:             r?.openingAt ?? undefined,
    closingAt:             r?.closingAt ?? undefined,
    openingInsidePeriod:   r?.openingInsidePeriod ?? undefined,
    hasReadings:           r?.hasReadings ?? undefined,
    receivedIsSiteLevel:   r?.receivedIsSiteLevel ?? undefined,
  };
}

export async function fetchReconciliation(
  params: { from: string; to: string; tankId?: string },
): Promise<ReconciliationRow[]> {
  const qs = new URLSearchParams({ from: params.from, to: params.to });
  if (params.tankId) qs.set('tankId', params.tankId);

  const res  = await apiFetch(`/api/v1/fuel/reconciliation/?${qs}`);
  const body = await res.json().catch(() => null);
  if (!res.ok) throw { message: body?.detail ?? body?.message ?? 'Failed to load reconciliation.', status: res.status } as ApiError;

  const payload = body?.data ?? body;
  const rows: any[] = Array.isArray(payload) ? payload
    : Array.isArray(payload?.results) ? payload.results : [];
  // Worst tank first, as the guide asks.
  return rows.map(mapReconciliationRow).sort((a, b) => b.unaccountedPct - a.unaccountedPct);
}

/* ── Fuel consumption by vehicle and driver (guide §4.3, NEW) ───────────
   GET /api/v1/fuel/consumption/ — view_analytics, site-scoped. Each fill is judged
   against the previous fill of the same equipment (full-tank-to-full-tank): expected
   litres = distance ÷ expected km/L (or hours × expected L/h). The expected rate is the
   equipment's benchmark or, after 3 fills, the median of its own last 10 rates. Above
   the tolerance (default 20%) the fill is `high` and a consumption alert is raised. */
export interface MeterBreakdown {
  fills: number; measuredFills: number;
  distanceKm?: number; hoursRun?: number; litres: number;
  kmPerLitre?: number | null; litresPerHour?: number | null; excessLitres: number;
}

export interface ConsumptionCounts {
  fills: number; litresIssued: number; highFills: number; lowFills: number;
  invalidReadings: number; missingReadings: number;
  suspectedExcessLitres: number; suspectedExcessDisplay?: string;
  byMeter: { odometer?: MeterBreakdown; hour_meter?: MeterBreakdown };
  lastFill?: string;
}

export interface EquipmentConsumption extends ConsumptionCounts {
  equipment: { id: string; name: string; code?: string; type?: string; registrationNumber?: string; site?: { id: string; name: string } };
  meter?: UsageMeter;
  benchmark?: number | null;
  baselineUsed?: number | null;
  baselineSource?: string | null;
  tolerancePct?: number;
  lastReading?: number | null;
}

export interface DriverConsumption extends ConsumptionCounts {
  driver: { id: string; name: string; phone?: string; linked?: boolean };
  equipment: string[];
}

export interface FuelConsumptionReport {
  summary:     ConsumptionCounts;
  byEquipment: EquipmentConsumption[];
  byDriver:    DriverConsumption[];
  flagged:     Dispense[];
}

function mapMeter(m: any): MeterBreakdown | undefined {
  if (!m || typeof m !== 'object') return undefined;
  return {
    fills: numOf(m.fills), measuredFills: numOf(m.measuredFills),
    distanceKm: numOrUndef(m.distanceKm), hoursRun: numOrUndef(m.hoursRun), litres: numOf(m.litres),
    kmPerLitre: m.kmPerLitre == null ? null : Number(m.kmPerLitre),
    litresPerHour: m.litresPerHour == null ? null : Number(m.litresPerHour),
    excessLitres: numOf(m.excessLitres),
  };
}

function mapCounts(c: any): ConsumptionCounts {
  return {
    fills: numOf(c?.fills), litresIssued: numOf(c?.litresIssued), highFills: numOf(c?.highFills),
    lowFills: numOf(c?.lowFills), invalidReadings: numOf(c?.invalidReadings), missingReadings: numOf(c?.missingReadings),
    suspectedExcessLitres: numOf(c?.suspectedExcessLitres), suspectedExcessDisplay: c?.suspectedExcessDisplay ?? undefined,
    byMeter: { odometer: mapMeter(c?.byMeter?.odometer), hour_meter: mapMeter(c?.byMeter?.hour_meter) },
    lastFill: c?.lastFill ?? undefined,
  };
}

export async function fetchFuelConsumption(
  params: { from?: string; to?: string; siteId?: string; equipmentId?: string; driverId?: string } = {},
): Promise<FuelConsumptionReport> {
  const qs = new URLSearchParams();
  (Object.entries(params) as [string, unknown][]).forEach(([k, v]) => { if (v) qs.set(k, String(v)); });
  const res  = await apiFetch(`/api/v1/fuel/consumption/${qs.toString() ? `?${qs}` : ''}`);
  const body = await res.json().catch(() => null);
  if (!res.ok) throw toApiError(res.status, body, 'Failed to load the consumption report.');
  const d = body?.data ?? body ?? {};
  return {
    summary: mapCounts(d.summary),
    byEquipment: (Array.isArray(d.byEquipment) ? d.byEquipment : []).map((r: any) => ({
      ...mapCounts(r),
      equipment: {
        id: String(r?.equipment?.id ?? ''), name: String(r?.equipment?.name ?? ''), code: r?.equipment?.code,
        type: r?.equipment?.type, registrationNumber: r?.equipment?.registrationNumber,
        site: r?.equipment?.site ? { id: String(r.equipment.site.id ?? ''), name: String(r.equipment.site.name ?? '') } : undefined,
      },
      meter: r?.meter ?? undefined,
      benchmark: r?.benchmark ?? null,
      baselineUsed: r?.baselineUsed ?? null,
      baselineSource: r?.baselineSource ?? null,
      tolerancePct: numOrUndef(r?.tolerancePct),
      lastReading: r?.lastReading ?? null,
    })),
    // Worst driver first.
    byDriver: (Array.isArray(d.byDriver) ? d.byDriver : []).map((r: any) => ({
      ...mapCounts(r),
      driver: { id: String(r?.driver?.id ?? ''), name: String(r?.driver?.name ?? ''), phone: r?.driver?.phone, linked: r?.driver?.linked },
      equipment: Array.isArray(r?.equipment) ? r.equipment.map(String) : [],
    })).sort((a: DriverConsumption, b: DriverConsumption) => b.suspectedExcessLitres - a.suspectedExcessLitres),
    flagged: (Array.isArray(d.flagged) ? d.flagged : []).map(mapDispense),
  };
}

/* ════════════════════════════════════════════════════════════════════════
   Section 2.0 — SAP Integration

   Posting happens server-side on a schedule; the frontend builds the setup
   wizard and the health screen and never posts to SAP itself.

   OWNER-ONLY. The connection holds credentials to the customer's ERP, so the
   whole area is hidden from everyone else including Admin — see
   isOwner()/useOwnerPower in utils/permissions.
   ════════════════════════════════════════════════════════════════════════ */

export type SapAuthMethod = 'oauth_cc' | 'basic';

export interface SapConnection {
  baseUrl:            string;
  authMethod:         SapAuthMethod;
  tokenUrl?:          string;
  clientId?:          string;
  username?:          string;
  postIntervalMinutes: number;
  isEnabled:          boolean;
  /* Secrets are write-only — GET returns booleans, never the values, so the UI
     renders "•••• saved, replace?" rather than a populated field. */
  hasClientSecret:    boolean;
  hasPassword:        boolean;
  /** Drive a status badge without an extra call. */
  lastSuccessAt?:     string;
  lastError?:         string;
  lastErrorAt?:       string;
  fieldMapping?:      Record<string, string>;
}

function mapSapConnection(c: any): SapConnection {
  return {
    baseUrl:             String(c?.baseUrl ?? c?.base_url ?? ''),
    authMethod:          (c?.authMethod ?? c?.auth_method ?? 'oauth_cc') as SapAuthMethod,
    tokenUrl:            c?.tokenUrl ?? c?.token_url ?? undefined,
    clientId:            c?.clientId ?? c?.client_id ?? undefined,
    username:            c?.username ?? undefined,
    postIntervalMinutes: Number(c?.postIntervalMinutes ?? c?.post_interval_minutes ?? 60),
    isEnabled:           Boolean(c?.isEnabled ?? c?.is_enabled ?? false),
    hasClientSecret:     Boolean(c?.hasClientSecret ?? c?.has_client_secret ?? false),
    hasPassword:         Boolean(c?.hasPassword ?? c?.has_password ?? false),
    lastSuccessAt:       c?.lastSuccessAt ?? c?.last_success_at ?? undefined,
    lastError:           c?.lastError ?? c?.last_error ?? undefined,
    lastErrorAt:         c?.lastErrorAt ?? c?.last_error_at ?? undefined,
    fieldMapping:        c?.fieldMapping ?? c?.field_mapping ?? undefined,
  };
}

export async function fetchSapConnection(): Promise<SapConnection | null> {
  const res = await apiFetch('/api/v1/sap/connection/');
  if (res.status === 404) return null;   // not configured yet
  const body = await res.json().catch(() => null);
  if (!res.ok) throw { message: body?.detail ?? body?.message ?? 'Failed to load SAP connection.', status: res.status } as ApiError;
  return mapSapConnection(body?.data ?? body);
}

export interface SapConnectionPayload {
  baseUrl?:             string;
  authMethod?:          SapAuthMethod;
  tokenUrl?:            string;
  clientId?:            string;
  /** Omit to leave the stored secret alone; send a value only to replace it. */
  clientSecret?:        string;
  username?:            string;
  password?:            string;
  postIntervalMinutes?: number;
  isEnabled?:           boolean;
  fieldMapping?:        Record<string, string>;
}

/** Creates on first call, updates after. */
export async function saveSapConnection(payload: SapConnectionPayload): Promise<SapConnection> {
  const res  = await apiFetch('/api/v1/sap/connection/', {
    method: 'PUT',
    body:   JSON.stringify(payload),
  });
  const body = await res.json().catch(() => null);
  if (!res.ok) throwFieldErrors(body, 'Failed to save SAP connection.', res.status);
  return mapSapConnection(body?.data ?? body);
}

export interface SapTestResult {
  ok:          boolean;
  stage?:      string;
  status?:     number | string;
  /** Already translated from SAP's own codes — show verbatim when ok is false. */
  error?:      string;
  elapsedMs?:  number;
  csrfOffered?: boolean;
}

/** Read-only and safe against production. */
export async function testSapConnection(): Promise<SapTestResult> {
  const res  = await apiFetch('/api/v1/sap/connection/test/', { method: 'POST' });
  const body = await res.json().catch(() => null);
  const data = body?.data ?? body ?? {};
  // A failed *test* is a valid 200 result, not a transport error.
  if (!res.ok && data?.ok == null) {
    throw { message: body?.detail ?? body?.message ?? 'Connection test failed.', status: res.status } as ApiError;
  }
  return {
    ok:          Boolean(data?.ok),
    stage:       data?.stage,
    status:      data?.status,
    error:       data?.error,
    elapsedMs:   numOrUndef(data?.elapsedMs ?? data?.elapsed_ms),
    csrfOffered: data?.csrfOffered ?? data?.csrf_offered,
  };
}

/** Valid keys for fieldMapping, per the guide. */
export const SAP_MAPPING_KEYS = [
  'measuring_point', 'reading_value', 'reading_unit', 'reading_date',
  'reading_time', 'document_text', 'counter_reading', 'read_by',
] as const;
export type SapMappingKey = typeof SAP_MAPPING_KEYS[number];

export interface SapMetadata {
  odataVersion?:   string;
  documentEntity?: string;
  /** Entity/field names read from the customer's own $metadata. */
  entities:        string[];
  mappingPresent:  string[];
  mappingMissing:  string[];
  /** True once nothing is missing — gate "turn it on" behind this. */
  verified:        boolean;
}

export async function fetchSapMetadata(): Promise<SapMetadata> {
  const res  = await apiFetch('/api/v1/sap/connection/metadata/');
  const body = await res.json().catch(() => null);
  if (!res.ok) throw { message: body?.detail ?? body?.message ?? 'Failed to read SAP metadata.', status: res.status } as ApiError;
  const d = body?.data ?? body ?? {};
  return {
    odataVersion:   d?.odataVersion ?? d?.odata_version ?? undefined,
    documentEntity: d?.documentEntity ?? d?.document_entity ?? undefined,
    entities:       Array.isArray(d?.entities) ? d.entities.map(String) : [],
    mappingPresent: Array.isArray(d?.mappingPresent ?? d?.mapping_present) ? (d.mappingPresent ?? d.mapping_present).map(String) : [],
    mappingMissing: Array.isArray(d?.mappingMissing ?? d?.mapping_missing) ? (d.mappingMissing ?? d.mapping_missing).map(String) : [],
    verified:       Boolean(d?.verified),
  };
}

/* ── Measuring points ── */

export type MeasuringPointKind = 'tank_level' | 'tank_volume' | 'fuel_issued' | 'equipment_consumption';

export const MEASURING_POINT_KINDS: MeasuringPointKind[] = [
  'tank_level', 'tank_volume', 'fuel_issued', 'equipment_consumption',
];

export interface MeasuringPoint {
  id:             string;
  kind:           MeasuringPointKind;
  tankId?:        string;
  tankName?:      string;
  equipmentId?:   string;
  equipmentName?: string;
  /** Zero-padded to 12 characters server-side, so a short number is fine. */
  measuringPoint: string;
  unit:           string;
}

function mapMeasuringPoint(m: any): MeasuringPoint {
  return {
    id:             String(m?.id ?? ''),
    kind:           (m?.kind ?? 'tank_level') as MeasuringPointKind,
    tankId:         m?.tankId ?? m?.tank_id ?? undefined,
    tankName:       m?.tankName ?? m?.tank_name ?? m?.tank?.name ?? undefined,
    equipmentId:    m?.equipmentId ?? m?.equipment_id ?? undefined,
    equipmentName:  m?.equipmentName ?? m?.equipment_name ?? m?.equipment?.name ?? undefined,
    measuringPoint: String(m?.measuringPoint ?? m?.measuring_point ?? ''),
    unit:           String(m?.unit ?? ''),
  };
}

export async function fetchMeasuringPoints(): Promise<MeasuringPoint[]> {
  const res  = await apiFetch('/api/v1/sap/measuring-points/');
  const body = await res.json().catch(() => null);
  if (!res.ok) throw { message: body?.detail ?? body?.message ?? 'Failed to load measuring points.', status: res.status } as ApiError;
  const payload = body?.data ?? body;
  const rows: any[] = Array.isArray(payload) ? payload
    : Array.isArray(payload?.results) ? payload.results : [];
  return rows.map(mapMeasuringPoint);
}

export interface MeasuringPointPayload {
  kind:           MeasuringPointKind;
  /** tankId for tank_* / fuel_issued; equipmentId for equipment_consumption. */
  tankId?:        string;
  equipmentId?:   string;
  measuringPoint: string;
  unit:           string;
}

export async function createMeasuringPoint(payload: MeasuringPointPayload): Promise<MeasuringPoint> {
  const res  = await apiFetch('/api/v1/sap/measuring-points/', {
    method: 'POST',
    body:   JSON.stringify(payload),
  });
  const body = await res.json().catch(() => null);
  if (!res.ok) throwFieldErrors(body, 'Failed to map measuring point.', res.status);
  return mapMeasuringPoint(body?.data ?? body);
}

export async function updateMeasuringPoint(pointId: string, payload: Partial<MeasuringPointPayload>): Promise<MeasuringPoint> {
  const res  = await apiFetch(`/api/v1/sap/measuring-points/${pointId}/`, {
    method: 'PATCH',
    body:   JSON.stringify(payload),
  });
  const body = await res.json().catch(() => null);
  if (!res.ok) throwFieldErrors(body, 'Failed to update measuring point.', res.status);
  return mapMeasuringPoint(body?.data ?? body);
}

export async function deleteMeasuringPoint(pointId: string): Promise<void> {
  const res = await apiFetch(`/api/v1/sap/measuring-points/${pointId}/`, { method: 'DELETE' });
  if (res.status === 204 || res.ok) return;
  const body = await res.json().catch(() => null);
  throw { message: body?.detail ?? 'Failed to delete measuring point.', status: res.status } as ApiError;
}

/* ── Health screen ── */

export type SapLogStatus     = 'pending' | 'sent' | 'failed' | 'skipped';
export type SapLogSourceType = 'tank_reading' | 'dispense';

export interface SapLog {
  id:          string;
  status:      SapLogStatus;
  sourceType?: SapLogSourceType;
  targetLabel?: string;
  message?:    string;
  createdAt:   string;
  /** Only on the single-log endpoint — put these behind a "details" expander. */
  requestPayload?: any;
  responseBody?:   any;
}

function mapSapLog(l: any): SapLog {
  return {
    id:          String(l?.id ?? ''),
    status:      (l?.status ?? 'pending') as SapLogStatus,
    sourceType:  (l?.sourceType ?? l?.source_type) as SapLogSourceType | undefined,
    targetLabel: l?.targetLabel ?? l?.target_label ?? l?.target ?? undefined,
    message:     l?.message ?? l?.error ?? undefined,
    createdAt:   l?.createdAt ?? l?.created_at ?? l?.timestamp ?? new Date().toISOString(),
    requestPayload: l?.requestPayload ?? l?.request_payload ?? undefined,
    responseBody:   l?.responseBody   ?? l?.response_body   ?? undefined,
  };
}

export interface SapLogsParams {
  status?:     SapLogStatus;
  sourceType?: SapLogSourceType;
  from?:       string;
  to?:         string;
  page?:       number;
  limit?:      number;
}

export interface SapLogsResult {
  results: SapLog[];
  total:   number;
  page:    number;
  limit:   number;
  /** Per-status counts — the summary strip. */
  counts:  Record<SapLogStatus, number>;
}

export async function fetchSapLogs(params: SapLogsParams = {}): Promise<SapLogsResult> {
  const qs = new URLSearchParams();
  if (params.status)     qs.set('status',     params.status);
  if (params.sourceType) qs.set('sourceType', params.sourceType);
  if (params.from)       qs.set('from',       params.from);
  if (params.to)         qs.set('to',         params.to);
  if (params.page != null) qs.set('page', String(params.page));
  const limit = Math.min(params.limit ?? 50, 100);
  qs.set('limit', String(limit));

  const res  = await apiFetch(`/api/v1/sap/logs/${qs.toString() ? `?${qs}` : ''}`);
  const body = await res.json().catch(() => null);
  if (!res.ok) throw { message: body?.detail ?? body?.message ?? 'Failed to load SAP logs.', status: res.status } as ApiError;

  const payload = body?.data ?? body;
  const base    = mapListEnvelope(payload, mapSapLog, params.page ?? 1, limit);
  const raw     = payload?.counts ?? {};
  return {
    ...base,
    counts: {
      pending: Number(raw?.pending ?? 0),
      sent:    Number(raw?.sent    ?? 0),
      failed:  Number(raw?.failed  ?? 0),
      skipped: Number(raw?.skipped ?? 0),
    },
  };
}

/** Adds requestPayload and responseBody — engineer-only detail. */
export async function fetchSapLogById(logId: string): Promise<SapLog> {
  const res  = await apiFetch(`/api/v1/sap/logs/${logId}/`);
  const body = await res.json().catch(() => null);
  if (!res.ok) throw { message: body?.detail ?? 'Log entry not found.', status: res.status } as ApiError;
  return mapSapLog(body?.data ?? body);
}

/** Re-sends failed posts. */
export async function retrySapLogs(limit = 50): Promise<{ retried: number; recovered: number }> {
  const res  = await apiFetch(`/api/v1/sap/logs/retry/?limit=${Math.min(limit, 100)}`, { method: 'POST' });
  const body = await res.json().catch(() => null);
  if (!res.ok) throw { message: body?.detail ?? body?.message ?? 'Retry failed.', status: res.status } as ApiError;
  const d = body?.data ?? body ?? {};
  return { retried: Number(d?.retried ?? 0), recovered: Number(d?.recovered ?? 0) };
}

/** Manual catch-up. `force` ignores the posting interval. Routine posting is a
    scheduled server command — this is not the normal path. */
export async function pushSapNow(force = false): Promise<{ message?: string }> {
  const res  = await apiFetch(`/api/v1/sap/push/${force ? '?force=true' : ''}`, { method: 'POST' });
  const body = await res.json().catch(() => null);
  if (!res.ok) throw { message: body?.detail ?? body?.message ?? 'Push failed.', status: res.status } as ApiError;
  return { message: body?.message ?? body?.data?.message };
}

/* ════════════════════════════════════════════════════════════════════════
   Issues as work orders — SOW 7.0 (Maintenance and Support)

   The server has no maintenance model: an Issue links to a SITE, not to a
   sensor, tank or piece of equipment, and it has no type field. Preventive and
   corrective work are therefore indistinguishable server-side.

   The integration guide's workaround, implemented here:
     · prefix the title with a category the UI supplies from a fixed list —
       [Preventive], [Corrective], [Calibration], [Replacement]
     · put the asset in the title too, so ?search= finds it either way
   Both are conventions, not schema. They survive only while titles are built
   by the pickers below rather than typed by hand, which is why buildIssueTitle
   is the only way this module composes one.
   ════════════════════════════════════════════════════════════════════════ */

export type IssueCategory = 'Preventive' | 'Corrective' | 'Calibration' | 'Replacement';

export const ISSUE_CATEGORIES: IssueCategory[] = [
  'Preventive', 'Corrective', 'Calibration', 'Replacement',
];

export type IssueSeverity = 'low' | 'medium' | 'high' | 'critical';
export type IssueStatus   = 'open' | 'in-progress' | 'resolved';

/** `[Corrective] SN-0142 — no reporting since 14 Sep` */
export function buildIssueTitle(
  category: IssueCategory,
  assetLabel: string,
  summary: string,
): string {
  const asset = assetLabel.trim();
  const rest  = summary.trim();
  return `[${category}]${asset ? ` ${asset}` : ''}${rest ? ` — ${rest}` : ''}`;
}

/** Reads a category back off a title written by buildIssueTitle. */
export function parseIssueCategory(title: string): IssueCategory | undefined {
  const match = /^\[([A-Za-z]+)\]/.exec(title ?? '');
  const found = ISSUE_CATEGORIES.find(c => c.toLowerCase() === match?.[1]?.toLowerCase());
  return found;
}

export interface CreateIssuePayload {
  title:       string;
  description: string;
  severity:    IssueSeverity;
  siteId:      string;
  assignedTo?: string | number;
}

export interface IssueRecord {
  id:          string;
  title:       string;
  description: string;
  severity:    IssueSeverity;
  status:      IssueStatus;
  siteId?:     string;
  assignedTo?: string | number;
  createdAt?:  string;
  category?:   IssueCategory;
}

function mapIssue(i: any): IssueRecord {
  const title = String(i?.title ?? '');
  return {
    id:          String(i?.id ?? ''),
    title,
    description: String(i?.description ?? ''),
    severity:    (i?.severity ?? 'medium') as IssueSeverity,
    status:      (i?.status ?? 'open') as IssueStatus,
    siteId:      i?.siteId ?? i?.site_id ?? undefined,
    assignedTo:  i?.assignedTo ?? i?.assigned_to ?? undefined,
    createdAt:   i?.createdAt ?? i?.created_at ?? undefined,
    category:    parseIssueCategory(title),
  };
}

/** POST /api/reports/issues/ — requires manage_issues. See notes above. */
export async function createIssue(payload: CreateIssuePayload): Promise<IssueRecord> {
  const res  = await apiFetch('/api/reports/issues/', {
    method: 'POST',
    body:   JSON.stringify(payload),
  });
  const body = await res.json().catch(() => null);
  if (!res.ok) throwFieldErrors(body, 'Failed to raise issue.', res.status);
  return mapIssue(body?.data ?? body);
}

/** PUT /api/reports/issues/{id}/ — status changes and reassignment. */
export async function updateIssue(
  issueId: string,
  payload: Partial<Pick<IssueRecord, 'title' | 'description' | 'severity' | 'status'>> & { assignedTo?: string | number },
): Promise<IssueRecord> {
  const res  = await apiFetch(`/api/reports/issues/${issueId}/`, {
    method: 'PUT',
    body:   JSON.stringify(payload),
  });
  const body = await res.json().catch(() => null);
  if (!res.ok) throwFieldErrors(body, 'Failed to update issue.', res.status);
  return mapIssue(body?.data ?? body);
}

/* ── Calibration and replacement history (SOW 7.0) ───────────────────────
   The server records neither. Until it does, the UI writes an Issue at the
   moment of the action with the before/after values in the description, which
   gives a queryable service history via ?search=[Calibration].

   Stopgap, not a fix: a real calibration_history resource would replace this. */

export async function recordCalibrationIssue(params: {
  siteId:      string;
  assetLabel:  string;      // e.g. "SN-0142"
  oldRange:    string;      // e.g. "0-1000"
  newRange:    string;      // e.g. "0-1024"
  reason:      string;
}): Promise<IssueRecord> {
  return createIssue({
    siteId:   params.siteId,
    title:    buildIssueTitle('Calibration', params.assetLabel, `range ${params.oldRange} → ${params.newRange}`),
    description:
      `Sensor range changed from ${params.oldRange} to ${params.newRange}.\n\n` +
      `Reason: ${params.reason}\n\n` +
      `Recorded automatically when the range was changed. Changing a range ` +
      `rewrites the percentage on every subsequent reading.`,
    severity: 'low',
  });
}

export async function recordReplacementIssue(params: {
  siteId:     string;
  oldAsset:   string;
  newAsset:   string;
  reason:     string;
}): Promise<IssueRecord> {
  return createIssue({
    siteId:   params.siteId,
    title:    buildIssueTitle('Replacement', params.oldAsset, `→ ${params.newAsset}`),
    description: `Unit ${params.oldAsset} replaced by ${params.newAsset}.\n\nReason: ${params.reason}`,
    severity: 'low',
  });
}
